<template>
  <div
    ref="workspace"
    :class="['restaurante-checkout', { 'is-fullscreen': isFullscreen }]"
  >
    <div class="checkout-page">
      <div class="checkout-page-content">
        <div v-if="!checkout" class="empty-state">
          Carregando dados do checkout.
        </div>

        <template v-else>
          <b-row class="checkout-layout">
            <b-col lg="8" class="checkout-left-column">
              <div class="checkout-card checkout-main-card">
                <div class="checkout-title">
                  <div>
                    <h3>Faturamento · {{ mesaLabel }}</h3>
                    <span class="checkout-header-context">{{ ambienteLabel }}</span>
                  </div>
                  <div class="checkout-actions">
                    <button
                      type="button"
                      class="checkout-fullscreen-button"
                      :title="isFullscreen ? 'Sair da tela cheia' : 'Usar tela cheia'"
                      :aria-label="isFullscreen ? 'Sair da tela cheia' : 'Usar tela cheia'"
                      @click="toggleFullscreen"
                    >
                      <i
                        :class="['fas', isFullscreen ? 'fa-compress' : 'fa-expand']"
                        aria-hidden="true"
                      />
                    </button>
                    <button
                      type="button"
                      class="btn btn-light-dark font-weight-bolder font-size-sm checkout-back-button"
                      @click="voltarMesas"
                    >
                      Voltar para Mesas
                    </button>
                  </div>
                </div>
              </div>

              <div class="checkout-card checkout-selection-card mt-5">
                <div v-if="itensPendentes.length || !fechamentoNfcePendente" class="section-title">
                  <h4>Itens para faturar</h4>
                  <button
                    v-if="canVincularAvulsaMesa"
                    type="button"
                    class="btn btn-sm btn-light-primary font-weight-bold"
                    @click="openVincularAvulsaModal"
                  >
                    Vincular comanda
                  </button>
                </div>

                <div
                  v-if="avisosCheckout.length"
                  class="mb-5"
                >
                  <div
                    v-for="alerta in avisosCheckout"
                    :key="alerta"
                    class="alert alert-warning mb-2"
                  >
                    {{ alerta }}
                  </div>
                </div>

                <div
                  v-if="cobrancaParcialConcluida"
                  class="alert alert-success checkout-next-charge"
                >
                  <span
                    >Cobrança parcial paga. O saldo restante permanece no
                    atendimento.</span
                  >
                  <div class="checkout-dividir-controles">
                    <button
                      type="button"
                      class="btn btn-sm btn-success font-weight-bold"
                      @click="fazerNovaCobranca"
                    >
                      Fazer nova cobrança
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-success font-weight-bold"
                      @click="voltarMesas"
                    >
                      Voltar para Mesas
                    </button>
                  </div>
                </div>

                <div v-if="mostrarSeletorComandas" class="checkout-comandas-grid">
                  <button
                    v-if="!modoApenasComandasCheckout"
                    type="button"
                    :class="['checkout-comanda', { active: form.escopo === 'mesa' }]"
                    :aria-pressed="form.escopo === 'mesa'"
                    :disabled="dividindoCobranca && !podeTrocarModoDivisao"
                    @click="selectMesaInteira"
                  >
                    <span class="checkout-comanda-heading">
                      <strong>Todas as comandas</strong>
                    </span>
                  </button>

                  <button
                    v-for="comanda in checkout.comandas"
                    :key="comanda.id"
                    type="button"
                    :class="[
                      'checkout-comanda',
                      {
                        active:
                          form.escopo === 'comanda' &&
                          form.comanda_id === comanda.id,
                      },
                    ]"
                    :aria-pressed="form.escopo === 'comanda' && form.comanda_id === comanda.id"
                    :disabled="dividindoCobranca && !podeTrocarModoDivisao"
                    @click="selectComanda(comanda.id)"
                  >
                    <span class="checkout-comanda-heading">
                      <strong>{{ comandaCardLabel(comanda) }}</strong>
                    </span>
                  </button>
                </div>
                <div v-if="itensPendentes.length || !fechamentoNfcePendente" class="checkout-products-table-wrap" role="region" aria-label="Produtos para faturar" tabindex="0">
                  <table class="checkout-products-table">
                    <thead><tr><th>Produto</th><th>Qtd.</th><th>Unitário</th><th>Total</th></tr></thead>
                    <tbody>
                      <tr v-for="item in itensPendentes" :key="item.id">
                        <td><strong>{{ produtoDescricao(item) }}</strong></td>
                        <td>{{ formatQuantity(item.quantidade_pendente) }}</td>
                        <td>{{ formatMoney(valorUnitarioBaseCobranca(item)) }}</td>
                        <td><strong>{{ formatMoney(item.valor_pendente) }}</strong></td>
                      </tr>
                      <tr v-if="!itensPendentes.length"><td colspan="4" class="checkout-products-empty">Nenhum item disponível para faturamento.</td></tr>
                    </tbody>
                  </table>
                </div>
                <div
                  v-if="checkout.itens_cancelados.length"
                  class="checkout-section mt-6"
                >
                  <div class="section-title">
                    <h4>Itens cancelados</h4>
                    <span>Não entram na cobrança</span>
                  </div>
                  <div class="cancelled-list">
                    <div
                      v-for="item in checkout.itens_cancelados"
                      :key="item.id"
                      class="cancelled-item"
                    >
                      <strong>{{ produtoDescricao(item) }}</strong>
                      <span>{{ formatQuantity(item.quantidade) }}</span>
                      <span>{{ formatMoney(item.valor_total) }}</span>
                    </div>
                  </div>
                </div>
              <div v-if="fechamentoNfcePendente" class="checkout-section mt-6">
                <div class="section-title"><h4>Itens da NFC-e existente</h4></div>
                <p class="text-muted mb-4">Confira os itens do documento existente e retome a formalização financeira pelo F5.</p>
                <div class="checkout-products-table-wrap" role="region" aria-label="Itens da NFC-e existente" tabindex="0">
                  <table class="checkout-products-table">
                    <thead><tr><th>Produto</th><th>Qtd.</th><th>Unitário</th><th>Total</th></tr></thead>
                    <tbody>
                      <tr v-for="(item, index) in itensRecuperacaoNfce" :key="index">
                        <td><strong>{{ item.descricao }}</strong></td>
                        <td>{{ formatQuantity(item.quantidade) }}</td>
                        <td>{{ formatMoney(item.valor_unitario) }}</td>
                        <td><strong>{{ formatMoney(item.valor_total) }}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              </div>

              <div v-if="cobrancasDivisao.length" class="checkout-card checkout-history-card mt-5">
                <div class="section-title">
                  <h4>Histórico de cobranças</h4>
                </div>
                <div
                  class="cobrancas-list"
                  role="region"
                  aria-label="Histórico de cobranças"
                  tabindex="0"
                >
                  <div
                    v-for="(fechamento, index) in cobrancasDivisao"
                    :key="fechamento.id"
                    class="cobranca-record"
                  >
                    <div
                      :class="
                        fechamentoPorValor(fechamento)
                          ? 'cobranca-division-heading'
                          : 'cobranca-line'
                      "
                    >
                      <strong
                        v-if="fechamentoPorValor(fechamento)"
                        class="cobranca-division-label"
                      >
                        Divisão por valor &middot; {{ fechamentoLabel(fechamento) }}
                      </strong>
                      <div v-else class="cobranca-line-content">
                        <div class="cobranca-line-heading">
                          <strong>
                            Cobrança {{ numeroCobrancaHistorico(index) }}
                          </strong>
                          <span
                            class="cobranca-status-badge"
                            :class="statusFechamentoClasse(fechamento)"
                          >
                            {{ statusFechamentoLabel(fechamento) }}
                          </span>
                        </div>
                        <div class="cobranca-line-meta">
                          <span>{{ fechamentoLabel(fechamento) }}</span>
                          <span>
                            &middot; {{ (fechamento.itens || []).length }}
                            {{ (fechamento.itens || []).length === 1 ? "item" : "itens" }}
                          </span>
                        </div>
                      </div>
                      <div class="cobranca-line-side">
                        <strong
                          v-if="!fechamentoPorValor(fechamento)"
                          class="cobranca-line-value"
                        >
                          {{ formatMoney(Number(fechamento.valor_total || 0)) }}
                        </strong>
                        <div
                          v-if="
                            canRetomarFechamento(fechamento) ||
                            canCancelarRascunho(fechamento)
                          "
                          class="cobranca-line-actions"
                        >
                          <button
                            v-if="canRetomarFechamento(fechamento)"
                            type="button"
                            class="btn btn-sm btn-light-primary font-weight-bold"
                            @click="retomarFechamento(fechamento)"
                          >
                            Continuar
                          </button>

                          <button
                            v-if="canCancelarRascunho(fechamento)"
                            type="button"
                            class="btn btn-sm btn-light-danger font-weight-bold"
                            :disabled="cancelandoRascunho"
                            @click="cancelarRascunho(fechamento)"
                          >
                            {{ cancelandoRascunho ? "Cancelando..." : "Cancelar cobrança" }}
                          </button>
                        </div>
                      </div>
                    </div>
                    <template v-if="fechamentoPorValor(fechamento)">
                      <div class="cobranca-financial-cards">
                        <div
                          v-for="(valor, linhaIndex) in fechamento.distribuicao_financeira"
                          :key="linhaIndex"
                          class="cobranca-line cobranca-financial-card"
                        >
                          <div class="cobranca-line-content">
                            <div class="cobranca-line-heading">
                              <strong>
                                Cobrança {{ numeroCobrancaHistorico(index) + linhaIndex }}
                              </strong>
                              <span
                                class="cobranca-status-badge"
                                :class="statusFechamentoClasse(fechamento)"
                                :aria-label="'Status da divisão: ' + statusFechamentoLabel(fechamento)"
                                :title="'Status da divisão: ' + statusFechamentoLabel(fechamento)"
                              >
                                {{ statusFechamentoLabel(fechamento) }}
                              </span>
                            </div>
                            <div class="cobranca-line-meta">
                              <span>{{ fechamentoLabel(fechamento) }}</span>
                            </div>
                          </div>
                          <div class="cobranca-line-side">
                            <strong class="cobranca-line-value">
                              {{ formatMoney(valor) }}
                            </strong>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="fechamento.rascunho_financeiro_incompleto"
                        class="cobranca-financial-summary"
                      >
                        <span>
                          Restante {{ formatMoney(restanteFechamentoHistorico(fechamento)) }}
                        </span>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </b-col>

            <b-col lg="4" class="checkout-right-column">
              <div class="checkout-panel checkout-billing-panel">
                <div class="section-title checkout-panel-title">
                  <h4><Cog aria-hidden="true" /> Dados do faturamento</h4>
                </div>

                <div class="checkout-billing-fields">
                <div class="checkout-attendant-field">
                  <label for="checkout-atendente">Atendente <span aria-hidden="true">*</span></label>
                  <VueSelect
                    input-id="checkout-atendente"
                    :value="atendenteSelecionado"
                    :options="atendentes"
                    label="label"
                    :clearable="false"
                    :disabled="!podeEditarAjustesCobranca"
                    class="checkout-attendant-select"
                    @input="selecionarAtendente"
                  >
                    <template #no-options>Nenhum atendente encontrado</template>
                  </VueSelect>
                </div>

                <b-form-group label="CPF/CNPJ na NFC-e (opcional)" label-for="documento_cobranca">
                  <b-form-input
                    id="documento_cobranca"
                    v-model="documentoCobranca"
                    placeholder="Não informado"
                    autocomplete="off"
                    :disabled="
                      Boolean(rascunhoCobrancaAtual && rascunhoCobrancaAtual.id) ||
                      Boolean(fechamentoNfcePendente)
                    "
                    :state="
                      documentoCobranca && !documentoCobrancaValido
                        ? false
                        : null
                    "
                    @input="onDocumentoCobrancaInput"
                    @blur="buscarPessoaDocumentoCobranca"
                    @keyup.enter="buscarPessoaDocumentoCobranca"
                  />
                  <small v-if="clienteDocumentoLocalizado && clienteDocumentoNome" class="text-success"
                    >Cliente encontrado: {{ clienteDocumentoNome }}</small
                  >
                  <small v-else-if="clienteDocumentoLocalizado" class="text-success">Cliente cadastrado</small>
                  <small v-if="clienteDocumentoInativo">Cliente cadastrado, mas inativo</small>
                  <div v-if="clienteDocumentoNaoCadastrado" class="mt-1">
                    <small>Cliente não cadastrado</small>
                    <b-button size="sm" variant="link" class="p-0 ml-2" @click="$bvModal.show('novo-cliente-checkout')">Cadastrar cliente</b-button>
                  </div>
                  <b-form-invalid-feedback
                    >Informe um CPF ou CNPJ válido.</b-form-invalid-feedback
                  >
                </b-form-group>
                </div>

                <div class="checkout-adjustments">
                  <div>
                    <label for="taxa_servico">Taxa serviço (%)</label>
                    <div class="checkout-adjustment-input" :class="{ invalid: entradaAjusteInvalida('taxaServico') }">
                      <div class="checkout-adjustment-unit">
                        <span class="checkout-adjustment-fixed" aria-hidden="true">%</span>
                      </div>
                      <div class="checkout-adjustment-value">
                        <b-form-input id="taxa_servico" :value="taxaServicoEntradaExibicao"
                          type="text" inputmode="decimal" autocomplete="off"
                          :aria-invalid="entradaAjusteInvalida('taxaServico')"
                          :disabled="!podeEditarAjustesCobranca"
                          @focus="focarValorAjuste('taxaServico', $event)"
                          @input="digitarValorAjuste('taxaServico', $event)"
                          @blur="finalizarEntradaAjuste('taxaServico')" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label for="couvert_valor">Couvert (R$)</label>
                    <div class="checkout-adjustment-input" :class="{ invalid: entradaAjusteInvalida('couvert') }">
                      <div class="checkout-adjustment-unit">
                        <span class="checkout-adjustment-fixed" aria-hidden="true">R$</span>
                      </div>
                      <div class="checkout-adjustment-value">
                        <b-form-input id="couvert_valor" :value="couvertEntradaExibicao"
                          type="text" inputmode="decimal" autocomplete="off"
                          :aria-invalid="entradaAjusteInvalida('couvert')"
                          :disabled="!podeEditarAjustesCobranca"
                          @focus="focarValorAjuste('couvert', $event)"
                          @input="digitarValorAjuste('couvert', $event)"
                          @blur="finalizarEntradaAjuste('couvert')" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label for="acrescimo_cobranca">Acréscimo</label>
                    <div class="checkout-adjustment-input" :class="{ invalid: entradaAjusteInvalida('acrescimo') }">
                      <div class="checkout-adjustment-unit" role="group" aria-label="Unidade do acréscimo">
                        <button type="button" :class="{ active: acrescimoTipo === 1 }"
                          :aria-pressed="acrescimoTipo === 1" :disabled="!podeEditarAjustesCobranca"
                          @click="alterarTipoAjusteGeral('acrescimo', 1)">R$</button>
                        <button type="button" :class="{ active: acrescimoTipo === 2 }"
                          :aria-pressed="acrescimoTipo === 2" :disabled="!podeEditarAjustesCobranca"
                          @click="alterarTipoAjusteGeral('acrescimo', 2)">%</button>
                      </div>
                      <div class="checkout-adjustment-value">
                        <b-form-input id="acrescimo_cobranca" :value="acrescimoEntradaExibicao"
                          type="text" inputmode="decimal" autocomplete="off"
                          :aria-invalid="entradaAjusteInvalida('acrescimo')"
                          :disabled="!podeEditarAjustesCobranca"
                          @focus="focarValorAjuste('acrescimo', $event)"
                          @input="digitarValorAjuste('acrescimo', $event)"
                          @blur="finalizarEntradaAjuste('acrescimo')" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label for="desconto_cobranca">Desconto</label>
                    <div class="checkout-adjustment-input" :class="{ invalid: entradaAjusteInvalida('desconto') }">
                      <div class="checkout-adjustment-unit" role="group" aria-label="Unidade do desconto">
                        <button type="button" :class="{ active: descontoTipo === 1 }"
                          :aria-pressed="descontoTipo === 1" :disabled="!podeEditarAjustesCobranca"
                          @click="alterarTipoAjusteGeral('desconto', 1)">R$</button>
                        <button type="button" :class="{ active: descontoTipo === 2 }"
                          :aria-pressed="descontoTipo === 2" :disabled="!podeEditarAjustesCobranca"
                          @click="alterarTipoAjusteGeral('desconto', 2)">%</button>
                      </div>
                      <div class="checkout-adjustment-value">
                        <b-form-input id="desconto_cobranca" :value="descontoEntradaExibicao"
                          type="text" inputmode="decimal" autocomplete="off"
                          :aria-invalid="entradaAjusteInvalida('desconto')"
                          :disabled="!podeEditarAjustesCobranca"
                          @focus="focarValorAjuste('desconto', $event)"
                          @input="digitarValorAjuste('desconto', $event)"
                          @blur="finalizarEntradaAjuste('desconto')" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="checkout-panel checkout-summary-panel mt-6">
                <div class="section-title">
                  <h4><ChartPie aria-hidden="true" /> Resumo da cobrança</h4>
                </div>
                <div class="totals-box">
                  <div class="totals-section totals-status">
                    <div>
                      <span>{{ fechamentoNfcePendente ? "NFC-e existente" : currentTotalsLabel }}</span>
                      <strong>{{ formatarValorFinanceiro(resumoFinanceiro ? resumoFinanceiro.valor_total : null) }}</strong>
                    </div>
                    <div>
                      <span>Já pago</span>
                      <strong>{{ formatarValorFinanceiro(resumoFinanceiro ? resumoFinanceiro.valor_ja_pago : null) }}</strong>
                    </div>
                    <div class="total-pending">
                      <span>Pendente de pagamento</span>
                      <strong>{{ formatarValorFinanceiro(resumoFinanceiro ? resumoFinanceiro.valor_pendente_pagamento : null) }}</strong>
                    </div>
                    <p v-if="resumoComandaIndisponivel" class="checkout-financial-note">
                      Itens desta comanda foram incluídos em um fechamento da mesa.
                      Pagamentos ou ajustes globais sem vínculo individual não são atribuídos à comanda.
                    </p>
                  </div>
                  <div class="totals-section totals-composition">
                    <div>
                      <span>Taxa serviço (%)</span>
                      <strong>{{ formatMoney(fechamentoNfcePendente ? Number(totaisRecuperacaoNfce.valor_taxa_servico || 0) : valorTaxaServico) }}</strong>
                    </div>
                    <div>
                      <span>Couvert</span>
                      <strong>{{ formatMoney(fechamentoNfcePendente ? Number(totaisRecuperacaoNfce.valor_couvert || 0) : valorCouvertTotal) }}</strong>
                    </div>
                    <div>
                      <span>Acréscimo</span>
                      <strong>{{ formatMoney(fechamentoNfcePendente ? Number(fechamentoNfcePendente.valor_acrescimo || 0) : valorAcrescimoCobranca) }}</strong>
                    </div>
                    <div>
                      <span>Desconto</span>
                      <strong>-{{ formatMoney(fechamentoNfcePendente ? Number(fechamentoNfcePendente.valor_desconto || 0) : valorDescontoCobranca) }}</strong>
                    </div>
                  </div>
                  <div class="total-main">
                    <span>Total da cobrança</span>
                    <strong>
                      {{ formatMoney(fechamentoNfcePendente ? Number(fechamentoNfcePendente.valor_total) : totalCobrancaExibicao) }}
                    </strong>
                  </div>
                </div>

                <div class="checkout-action-grid mt-3">
                <button
                  type="button"
                  class="btn btn-light-primary font-weight-bold checkout-action-secondary"
                  :disabled="!podeImprimirConferencia"
                  @click="imprimirConferencia"
                >
                  <Receipt aria-hidden="true" />
                  <span>{{
                    imprimindoConferencia
                      ? "Gerando conferência..."
                      : "Conferência"
                  }}</span>
                </button>
                <button
                  type="button"
                  class="btn btn-light-primary font-weight-bold checkout-action-secondary"
                  :disabled="!podeDividirCobranca"
                  @click="abrirDivisaoCobranca"
                >
                  <AccountMultipleOutline aria-hidden="true" />
                  <span>Dividir</span>
                </button>
                <button
                  type="button"
                  class="btn btn-primary font-weight-bold checkout-action-presale"
                  :disabled="!canFinalizarPreVenda"
                  @click="finalizarCobranca('pre_venda')"
                >
                  <TagOutline aria-hidden="true" />
                  <span>{{ gerarPreVendaLabel }}</span>
                </button>
                <button
                  v-if="featureFlags.nfceCheckout"
                  type="button"
                  class="btn btn-light-success font-weight-bold checkout-action-nfce"
                  :disabled="!canFinalizarNfce"
                  @click="finalizarCobranca('nfce')"
                >
                  <FileDocumentOutline aria-hidden="true" />
                  <span>{{ gerarNfceLabel }}</span>
                </button>
                </div>
              </div>

            </b-col>
          </b-row>
        </template>
      </div>
    </div>

    <NovoClienteModal
      modal-id="novo-cliente-checkout"
      :documento-inicial="clienteCadastroPagamentoPendente
        ? (validaCpfCnpj(buscaClientePagamento) ? buscaClientePagamento : '')
        : documentoCobranca"
      @selected="selecionarClienteCriado"
    />

    <b-modal id="modal-conferencia-pdf" title="Conferência" size="xl" hide-footer>
      <template v-if="conferenciaBlobUrl">
        <iframe
          :src="conferenciaBlobUrl"
          title="PDF da Conferência"
          class="w-100 border-0"
          style="height: 70vh"
        />
      </template>
    </b-modal>

    <b-modal
      id="modal-dividir-cobranca"
      :modal-class="[
        'restaurante-checkout-division-modal',
        {
          'restaurante-checkout-division-modal--choice':
            divisaoModalSelecionandoModo,
          'restaurante-checkout-division-modal--value':
            !divisaoModalSelecionandoModo && modoDivisao === 'valor',
        },
      ]"
      :body-class="
        !divisaoModalSelecionandoModo && modoDivisao === 'valor'
          ? 'checkout-division-value-body'
          : ''
      "
      :title="tituloModalDivisao"
      centered
      scrollable
      no-close-on-backdrop
      @hidden="onDivisaoModalHidden"
    >
      <template #modal-title>
        <span class="checkout-division-modal-heading">
          <span>{{ tituloModalDivisao }}</span>
          <small
            v-if="!divisaoModalSelecionandoModo && modoDivisao === 'valor'"
          >
            Distribua o valor total da conta entre as cobranças.
          </small>
        </span>
      </template>
      <template v-if="divisaoModalSelecionandoModo">
        <p class="text-muted mb-4">
          Escolha a forma de divisão da cobrança.
        </p>
        <div class="checkout-division-options">
          <button
            type="button"
            class="checkout-division-option checkout-division-option--items"
            :disabled="!podeTrocarModoDivisao && modoDivisao !== 'itens'"
            @click="escolherModoDivisao('itens')"
          >
            <span class="checkout-division-option-icon" aria-hidden="true">
              <FormatListBulleted :size="24" />
            </span>
            <span class="checkout-division-option-copy">
              <strong>Dividir por Itens</strong>
              <span>Selecione quais itens farão parte desta cobrança.</span>
            </span>
            <ChevronRight class="checkout-division-option-chevron" :size="22" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="checkout-division-option checkout-division-option--value"
            :disabled="!podeTrocarModoDivisao && modoDivisao !== 'valor'"
            @click="escolherModoDivisao('valor')"
          >
            <span class="checkout-division-option-icon" aria-hidden="true">
              <CurrencyUsd :size="24" />
            </span>
            <span class="checkout-division-option-copy">
              <strong>Dividir por Valor</strong>
              <span>Distribua o valor total da conta entre as cobranças.</span>
            </span>
            <ChevronRight class="checkout-division-option-chevron" :size="22" aria-hidden="true" />
          </button>
        </div>

      </template>

      <div
        v-if="!divisaoModalSelecionandoModo && modoDivisao === 'valor'"
        class="checkout-dividir-valor"
      >
        <div class="checkout-financial-summary">
          <div class="checkout-financial-summary__total">
            <span class="checkout-financial-summary-icon" aria-hidden="true"><CashMultiple :size="26" /></span>
            <span class="checkout-financial-summary-copy">
              <span>Total a distribuir</span>
              <strong>{{ formatMoney(totalDistribuirCentavos / 100) }}</strong>
            </span>
          </div>
          <div
            class="checkout-financial-summary__distributed"
            :class="{
              'is-complete':
                totalFinanceiroBackend !== null &&
                restanteDistribuicaoCentavos === 0,
            }"
          >
            <span class="checkout-financial-summary-icon" aria-hidden="true"><CheckCircleOutline :size="26" /></span>
            <span class="checkout-financial-summary-copy">
              <span>Distribuído</span>
              <strong>{{ formatMoney(distribuidoCentavos / 100) }}</strong>
            </span>
          </div>
          <div
            class="checkout-financial-summary__remaining"
            :class="{
              'is-complete':
                totalFinanceiroBackend !== null &&
                restanteDistribuicaoCentavos === 0,
              'is-pending': restanteDistribuicaoCentavos > 0,
              'is-over': restanteDistribuicaoCentavos < 0,
            }"
          >
            <span class="checkout-financial-summary-icon" aria-hidden="true"><ChartPie :size="26" /></span>
            <span class="checkout-financial-summary-copy">
              <span>Restante</span>
              <strong>{{ formatMoney(restanteDistribuicaoCentavos / 100) }}</strong>
            </span>
          </div>
        </div>

        <div class="checkout-financial-section-header">
          <h4>Cobranças</h4>
          <div class="checkout-dividir-controles checkout-financial-actions">
          <button
            type="button"
            class="btn btn-primary btn-sm font-weight-bold"
            :disabled="
              !podeEditarDistribuicaoFinanceira ||
              distribuicaoFinanceira.length >= 20
            "
            @click="adicionarCobrancaFinanceira"
          >
            <Plus :size="17" aria-hidden="true" /> Adicionar cobrança
          </button>
          <button
            type="button"
            class="btn btn-light-primary btn-sm font-weight-bold checkout-equal-action"
            :disabled="
              !podeEditarDistribuicaoFinanceira ||
              calculandoTotalFinanceiro ||
              totalFinanceiroBackend === null
            "
            @click="dividirFinanceiroIgualmente"
          >
            <CallSplit :size="17" aria-hidden="true" /> Dividir igualmente
          </button>
          <button
            type="button"
            class="btn btn-link btn-sm checkout-clear-values"
            :disabled="!podeEditarDistribuicaoFinanceira"
            @click="limparDistribuicaoFinanceira"
          >
            <TrashCanIcon :size="17" aria-hidden="true" /> Limpar valores
          </button>
          </div>
        </div>
        <div class="checkout-financial-list" role="region" aria-label="Cobranças">
          <div class="checkout-financial-list-head" aria-hidden="true">
            <span>#</span><span>Nome da cobrança</span><span>Valor</span><span>Ação</span>
          </div>
          <div
            v-for="(cobranca, index) in distribuicaoFinanceira"
            :key="cobranca.id"
            class="checkout-financial-line"
          >
            <span class="checkout-financial-line-index" aria-hidden="true">{{ index + 1 }}</span>
            <div class="checkout-financial-line-name">
              <label :for="`cobranca_financeira_${cobranca.id}`">
                Cobrança {{ index + 1 }}
              </label>
            </div>
            <div class="checkout-financial-field">
              <span aria-hidden="true">R$</span>
              <b-form-input
                :id="`cobranca_financeira_${cobranca.id}`"
                v-model="cobranca.valor"
                type="number"
                min="0.01"
                step="0.01"
                :disabled="!podeEditarDistribuicaoFinanceira"
                @input="resetEstadoCobranca"
              />
            </div>
            <button
              type="button"
              class="btn btn-light-danger btn-sm btn-icon checkout-financial-remove"
              title="Excluir cobrança"
              :aria-label="`Excluir cobrança ${index + 1}`"
              :disabled="!podeEditarDistribuicaoFinanceira || distribuicaoFinanceira.length <= 2"
              @click="excluirCobrancaFinanceira(cobranca.id)"
            >
              <TrashCanIcon :size="16" />
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="!divisaoModalSelecionandoModo && modoDivisao === 'itens'"
        class="checkout-tables-grid checkout-division-items"
      >
        <div class="checkout-section checkout-division-panel checkout-division-panel--pending">
          <div class="checkout-division-panel-header">
            <h4><span class="checkout-division-panel-icon" aria-hidden="true"><FormatListBulleted :size="20" /></span> {{ checkoutItensTitle }}</h4>
            <span>{{ itensPendentes.length }} com saldo</span>
          </div>

          <div v-if="!itensDoEscopo.length" class="blocked-state">
            Atendimento sem itens válidos para cobrança.
          </div>

          <div v-else class="table-responsive itens-pendentes-scroll checkout-division-table-wrap">
            <table
              class="table table-head-custom table-vertical-center table-compact"
            >
              <thead>
                <tr>
                  <th>Produto</th>
                  <th class="text-right">Total pendente</th>
                  <th class="text-right">Qtd.</th>
                  <th class="text-right">Ação</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in itensDoEscopo"
                  :key="item.id"
                  :class="{
                    muted: Number(item.quantidade_pendente) <= 0,
                  }"
                >
                  <td>
                    <span class="checkout-division-product">
                      <img v-if="item.produto && item.produto.imagem" :src="item.produto.imagem" alt="" class="checkout-division-product-image" />
                      <span v-else class="checkout-division-product-image checkout-division-product-placeholder" aria-hidden="true"><FormatListBulleted :size="18" /></span>
                      <span class="checkout-division-product-copy">
                        <strong>{{ produtoDescricao(item) }}</strong>
                        <small>{{ itemComandaOrigem(item) }}</small>
                      </span>
                    </span>
                  </td>
                  <td class="text-right">
                    <strong>{{ formatMoney(item.valor_pendente) }}</strong>
                  </td>
                  <td class="text-right">{{ formatQuantity(item.quantidade_pendente) }}</td>
                  <td class="text-right">
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold checkout-division-add"
                      :class="{
                        'btn-light-success':
                          itemActionLabel(item) === 'Pago',
                        'btn-light-warning':
                          itemActionLabel(item) === 'Reservado',
                      }"
                      :disabled="!canAddItem(item)"
                      @click="addItemCobranca(item)"
                    >
                      {{ itemActionLabel(item) }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="checkout-section checkout-division-panel checkout-division-panel--selected">
          <div class="checkout-division-panel-header">
            <h4><span class="checkout-division-panel-icon" aria-hidden="true"><CartOutline :size="20" /></span> Itens da cobrança</h4>
            <span>{{ cobrancaItensDetalhados.length }} item(ns)</span>
          </div>

          <div v-if="!cobrancaItens.length" class="checkout-division-empty">
            <CartOutline :size="32" aria-hidden="true" />
            <strong>Nenhum item adicionado</strong>
            <span>Adicione itens pendentes para montar esta cobrança.</span>
          </div>

          <div v-else class="table-responsive cobranca-itens-scroll checkout-division-table-wrap">
            <table
              class="table table-head-custom table-vertical-center table-compact"
            >
              <thead>
                <tr>
                  <th>Produto</th>
                  <th class="text-right">Qtd</th>
                  <th class="text-right">Valor</th>
                  <th class="text-right">Ação</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="linha in cobrancaItensDetalhados"
                  :key="linha.atendimento_item_id"
                >
                  <td>
                    <span class="checkout-division-product">
                      <img v-if="linha.item.produto && linha.item.produto.imagem" :src="linha.item.produto.imagem" alt="" class="checkout-division-product-image" />
                      <span v-else class="checkout-division-product-image checkout-division-product-placeholder" aria-hidden="true"><FormatListBulleted :size="18" /></span>
                      <span class="checkout-division-product-copy">
                        <strong>{{ produtoDescricao(linha.item) }}</strong>
                        <small>{{ itemComandaOrigem(linha.item) }}</small>
                      </span>
                    </span>
                    <template v-for="operacao in ['acrescimo', 'desconto']">
                      <span v-if="valorAjusteLinha(linha, operacao) > 0" :key="operacao"
                        :class="['ajuste-chip', `ajuste-chip-${operacao}`]">
                        {{ ajusteItemLabel(linha, operacao) }}
                      </span>
                    </template>
                  </td>
                  <td class="text-right cobranca-quantidade-cell">
                    {{ formatQuantity(linha.quantidade) }}
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ formatMoney(valorLinhaCobranca(linha)) }}
                  </td>
                  <td class="text-right cobranca-acoes-cell">
                    <div class="item-action-buttons">
                      <button
                        type="button"
                        class="btn btn-sm btn-light-danger btn-hover-danger btn-icon btn-ajuste-item mr-2"
                        v-b-tooltip.hover="'Ajuste do preço do item'"
                        :disabled="!podeEditarDivisao"
                        @click="openAjusteItemModal(linha)"
                      >
                        <Cash :size="17" />
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-danger btn-hover-danger btn-icon"
                        :disabled="!podeEditarDivisao"
                        @click="
                          removeItemCobranca(
                            linha.atendimento_item_id
                          )
                        "
                      >
                        <TrashCanIcon :size="16" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <template #modal-footer>
        <div class="checkout-division-footer">
          <button
            type="button"
            class="btn btn-light checkout-division-footer-back"
            @click="voltarDivisaoModal"
          >
            Voltar
          </button>
          <div v-if="!divisaoModalSelecionandoModo" class="checkout-division-footer-actions">
            <button
              type="button"
              class="btn btn-light-primary font-weight-bold checkout-division-footer-save"
              :disabled="!canCriarRascunho"
              @click="salvarCobrancaNoModal"
            >
              Salvar alterações
            </button>
            <button
              type="button"
              class="btn btn-primary font-weight-bold checkout-division-footer-presale"
              :disabled="!canFinalizarPreVenda"
              @click="faturarDivisaoNoModal('pre_venda')"
            >
              {{ gerarPreVendaLabel }}
            </button>
            <button
              v-if="featureFlags.nfceCheckout"
              type="button"
              class="btn btn-light-success font-weight-bold checkout-division-footer-nfce"
              :disabled="!canFinalizarNfce"
              @click="faturarDivisaoNoModal('nfce')"
            >
              {{ gerarNfceLabel }}
            </button>
          </div>
        </div>
      </template>
    </b-modal>

    <b-modal
      id="modal-pagamento-restaurante"
      modal-class="restaurante-workspace-modal restaurante-checkout-payment-modal"
      :title="pagamentoModalTitle"
      centered
      scrollable
      size="lg"
      body-class="restaurante-payment-modal-body"
      :hide-header-close="Boolean(confirmandoPagamento || (faturamentoPendente && faturamentoPendente.documentoGerado))"
      :no-close-on-backdrop="Boolean(confirmandoPagamento || (faturamentoPendente && faturamentoPendente.documentoGerado))"
      :no-close-on-esc="Boolean(confirmandoPagamento || (faturamentoPendente && faturamentoPendente.documentoGerado))"
      :busy="confirmandoPagamento"
      @hidden="onPagamentoModalHidden"
    >
      <template v-if="!pagamentoDistribuido">
        <b-row class="restaurante-payment-form-row align-items-end">
          <b-col md="5">
            <div class="restaurante-payment-field-label">
              Forma de Pagamento
            </div>
            <b-form-select
              v-model="pagamentoForm.forma_pagamento_id"
              :options="formaPagamentoOptions"
            />
          </b-col>
          <b-col md="4">
            <div class="restaurante-payment-field-label">Valor</div>
            <b-form-input
              ref="valorPagamentoRestaurante"
              v-model="pagamentoForm.valor"
              inputmode="decimal"
              type="text"
              aria-label="Valor do pagamento em reais"
              @focus="$event.target.select()"
              @blur="formatarValorPagamento"
              @keyup.enter="adicionarPagamento"
            />
          </b-col>
          <b-col md="3">
            <b-button
              variant="primary"
              class="restaurante-payment-add-button"
              :disabled="!canAdicionarPagamento"
              @click="adicionarPagamento"
            >
              (Enter) Adicionar
            </b-button>
          </b-col>
        </b-row>

        <div class="restaurante-payment-shortcuts mt-3">
          <button
            v-for="atalho in atalhosPagamento"
            :key="atalho.tecla"
            type="button"
            class="restaurante-payment-shortcuts__item"
            @click="adicionarPagamentoPorAtalho(atalho)"
          >
            <strong>{{ atalho.tecla }}</strong>
            <span>{{ atalho.descricao }}</span>
          </button>
        </div>

        <div v-if="pagamentos.length" class="restaurante-payment-list mt-3">
          <h5 class="restaurante-payment-section-title">Pagamentos adicionados</h5>
          <div
            v-for="(pagamento, index) in pagamentos"
            :key="`${pagamento.forma_pagamento_id}-${index}`"
            :class="['restaurante-payment-item', { 'has-installments': pagamentoPrazo(pagamento) }]"
          >
            <div class="restaurante-payment-item__description">
              <strong>{{ pagamento.descricao }}</strong>
              <span v-if="pagamentoPrazo(pagamento) && pagamento.parcelas">
                {{ pagamento.parcelas.length }} parcelas
              </span>
            </div>
            <strong class="restaurante-payment-item__value">{{ formatMoney(pagamento.valor) }}</strong>
            <button v-if="pagamentoPrazo(pagamento)" type="button" class="btn btn-link btn-sm restaurante-payment-item__edit"
              @click="abrirParcelamento(pagamento, index)">Editar parcelas</button>
            <button
              type="button"
              class="btn btn-icon btn-light-danger btn-sm restaurante-payment-item__remove"
              :aria-label="`Remover pagamento ${index + 1}`"
              @click="removerPagamento(index)"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </template>
      <div v-else class="restaurante-payment-list mt-3">
        <div
          v-for="(pagamento, index) in pagamentos"
          :key="index"
          class="restaurante-payment-item checkout-distributed-payment"
        >
          <div>
            <strong>Cobrança {{ index + 1 }}</strong>
            <span>{{ formatMoney(pagamento.valor) }}</span>
          </div>
          <b-form-select
            v-model="pagamento.forma_pagamento_id"
            :options="formaPagamentoOptions"
            :aria-label="`Forma de pagamento da cobrança ${index + 1}`"
            @focus="lembrarPagamentoDistribuido(index)"
            @change="onFormaPagamentoDistribuidoChange(index)"
          />
        </div>
      </div>

      <div :class="['restaurante-payment-summary', { 'has-change': trocoExibidoPagamento > 0 }]">
        <div class="restaurante-payment-total">
          <span>Total da cobrança</span>
          <strong>{{ formatMoney(pagamentoFechamentoValor) }}</strong>
        </div>
        <div class="restaurante-payment-remaining">
          <span>Restante</span>
          <strong>{{ formatMoney(restantePagamento) }}</strong>
        </div>
        <div v-if="trocoExibidoPagamento > 0" class="restaurante-payment-change">
          <span>{{ trocoPagamento > 0 ? 'Troco' : 'Troco' }}</span>
          <strong>{{ formatMoney(trocoExibidoPagamento) }}</strong>
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button v-if="!confirmandoPagamento && !(faturamentoPendente && faturamentoPendente.documentoGerado) && !(nfeGerada && nfeGerada.id)" variant="link" @click="cancel()">
          Cancelar
        </b-button>
        <span v-else class="text-muted mr-auto">
          {{ confirmandoPagamento ? 'Finalizando faturamento...' : 'Documento existente. Retome a confirmação financeira.' }}
        </span>
        <b-button
          ref="finishModalRestaurante"
          variant="primary"
          :disabled="!canConfirmarPagamento"
          @click="confirmarPagamento"
        >
          {{ confirmarPagamentoLabel }}
        </b-button>
      </template>
    </b-modal>

    <b-modal id="modal-parcelamento-restaurante" modal-class="restaurante-parcelamento-modal"
      title="Detalhes do parcelamento" centered scrollable size="lg"
      :no-close-on-backdrop="carregandoParcelamento || confirmandoParcelamento"
      :no-close-on-esc="carregandoParcelamento || confirmandoParcelamento"
      :hide-header-close="carregandoParcelamento || confirmandoParcelamento"
      @hidden="onParcelamentoHidden">
      <template v-if="parcelamentoForm">
        <div class="restaurante-parcelamento-info">
          <div class="restaurante-parcelamento-valor"><small>Valor total</small><strong>{{ formatMoney(parcelamentoForm.valor) }}</strong></div>
          <div><small>Condição</small><strong>{{ parcelamentoForma && parcelamentoForma.descricao }}</strong></div>
          <div><small>Quantidade <span v-if="parcelamentoForm.limite < 48">(máx. {{ parcelamentoForm.limite }})</span></small>
            <input :value="parcelamentoForm.quantidade" type="text" inputmode="numeric"
              autocomplete="off" pattern="[0-9]*" aria-label="Quantidade de parcelas"
              class="form-control restaurante-parcelamento-quantidade"
              @input="solicitarAlteracaoQuantidade($event.target.value)"
              @blur="validarQuantidadeAoSair" @keydown.enter.prevent="validarQuantidadeAoSair" />
            <small v-if="erroQuantidadeParcelamento" class="restaurante-parcelamento-erro">
              {{ erroQuantidadeParcelamento }}</small>
          </div>
          <div><small>Intervalo</small><strong>{{ parcelamentoIntervaloLabel }}</strong></div>
        </div>
        <div v-if="clienteFinanceiroId" class="restaurante-parcelamento-cliente">
          <span>Cliente: {{ clienteFinanceiroNome || 'Cliente cadastrado' }} <small>{{ clienteFinanceiroDocumento }}</small></span>
          <button type="button" title="Remover cliente" aria-label="Remover cliente"
            @click="removerClienteParcelamento">×</button>
        </div>
        <div v-else class="restaurante-receber-cliente">
          <label for="cliente-financeiro-parcelamento" class="restaurante-payment-field-label">
            Cliente <span class="text-danger">*</span>
          </label>
          <div class="restaurante-receber-busca">
            <b-form-input id="cliente-financeiro-parcelamento" :value="buscaClientePagamento"
              placeholder="CPF/CNPJ ou nome do cliente"
              aria-label="Buscar cliente para parcelamento" @input="onBuscaClientePagamentoInput"
              @blur="buscarClientePagamentoAoSair" @keyup.enter="buscarClientePagamentoAoSair" />
            <b-button variant="outline-primary" @click="abrirCadastroClientePagamento">Cadastrar cliente</b-button>
          </div>
          <small v-if="buscandoClientePagamento" class="text-muted">Consultando cliente...</small>
          <small v-else-if="clienteDocumentoNaoCadastrado" class="text-muted">Cliente não cadastrado</small>
          <small v-if="erroBuscaClientePagamento" class="text-danger">{{ erroBuscaClientePagamento }}</small>
          <small v-else-if="clienteParcelamentoPendente" class="text-danger">
            Selecione um cliente para confirmar o parcelamento.</small>
          <div v-if="resultadosClientePagamento.length" class="restaurante-receber-resultados">
            <button v-for="cliente in resultadosClientePagamento" :key="cliente.id_pessoa" type="button"
              @click="selecionarClientePagamento(cliente)">
              {{ cliente.nome || cliente.nome_fantasia }} · {{ cliente.cnpj_cpf }}
            </button>
          </div>
        </div>
        <div v-if="carregandoParcelamento" class="text-muted mt-3">Calculando parcelas...</div>
        <div v-else-if="parcelamentoForm.parcelas.length" class="restaurante-receber-tabela-wrap restaurante-parcelamento-tabela-wrap mt-3">
          <table class="restaurante-receber-tabela">
            <thead><tr><th>Parcela</th><th>Vencimento</th><th>Valor (R$)</th><th><span class="sr-only">Excluir</span></th></tr></thead>
            <tbody><tr v-for="(parcela, indice) in parcelamentoForm.parcelas" :key="indice">
              <td>{{ indice + 1 }}/{{ parcelamentoForm.parcelas.length }}</td>
              <td><b-form-input v-model="parcela.date" type="date" lang="pt-BR"
                :state="erroDataParcela(parcelamentoForm.parcelas, indice) ? false : null"
                :aria-label="`Vencimento da parcela ${indice + 1}`" />
                <small v-if="erroDataParcela(parcelamentoForm.parcelas, indice)"
                  class="restaurante-parcelamento-erro">{{ erroDataParcela(parcelamentoForm.parcelas, indice) }}</small></td>
              <td><b-form-input v-model="parcela.value" inputmode="decimal" type="text"
                :state="erroValorParcela(parcela.value) ? false : null"
                :aria-label="`Valor da parcela ${indice + 1}`" @focus="$event.target.select()"
                @blur="formatarValorParcela(parcela)" /></td>
              <td><button type="button" class="restaurante-parcelamento-excluir"
                :disabled="parcelamentoForm.parcelas.length <= 1 || carregandoParcelamento"
                :aria-label="`Excluir parcela ${indice + 1}`" :title="`Excluir parcela ${indice + 1}`"
                @click="excluirParcela(indice)"><TrashCanIcon :size="16" /></button></td>
            </tr></tbody>
          </table>
        </div>
        <small v-if="mensagemValoresParcelamento" class="restaurante-parcelamento-erro d-block mt-2">
          {{ mensagemValoresParcelamento }}</small>
        <small v-if="erroParcelamento" class="text-danger d-block mt-2">{{ erroParcelamento }}</small>
      </template>
      <template #modal-footer>
        <b-button variant="link" :disabled="confirmandoParcelamento" @click="$bvModal.hide('modal-parcelamento-restaurante')">Voltar</b-button>
        <b-button variant="light-primary" :disabled="carregandoParcelamento || confirmandoParcelamento"
          @click="restaurarParcelamentoPadrao">Restaurar padrão</b-button>
        <b-button variant="primary" :disabled="!podeConfirmarParcelamento"
          @click="confirmarParcelamento">Confirmar parcelamento</b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-abertura-caixa-restaurante"
      modal-class="restaurante-workspace-modal restaurante-checkout-compact-modal"
      title="Abertura de caixa"
      centered
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      scrollable
      size="md"
      @shown="focusValorAberturaCaixa"
    >
      <b-row>
        <b-col md="7">
          <b-form-group label="Valor Caixa R$" label-for="caixa_valor_abertura">
            <b-form-input
              id="caixa_valor_abertura"
              ref="caixaValorAberturaRestaurante"
              v-model.number="caixaValorAbertura"
              min="0"
              step="0.01"
              type="number"
              @keyup.enter="abrirCaixaCheckout"
            />
          </b-form-group>
        </b-col>
        <b-col md="5">
          <b-form-group label="Imprimir comprovante">
            <span class="switch switch-icon">
              <label>
                <input v-model="caixaImpressaoComprovante" type="checkbox" />
                <span></span>
              </label>
            </span>
          </b-form-group>
        </b-col>
      </b-row>

      <template #modal-footer="{ cancel }">
        <b-button
          variant="link"
          :disabled="abrindoCaixa"
          @click="cancelarAberturaCaixa(cancel)"
        >
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="abrindoCaixa"
          @click="abrirCaixaCheckout"
        >
          {{ abrindoCaixa ? "Abrindo..." : "Abrir caixa" }}
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-ajuste-item-restaurante"
      modal-class="restaurante-workspace-modal restaurante-ajuste-item-modal"
      title="Ajuste no item"
      centered
      scrollable
      @shown="focusAjusteItemInput"
    >
      <div class="restaurante-ajuste-layout">
        <div class="restaurante-ajuste-fields">
          <div class="restaurante-ajuste-product">
            <img v-if="ajusteItemModal.imagem" :src="ajusteItemModal.imagem" alt="" />
            <span v-else class="restaurante-ajuste-product-placeholder" aria-hidden="true"><FormatListBulleted :size="22" /></span>
            <div><strong>{{ ajusteItemModal.produto }}</strong><small v-if="ajusteItemModal.unidade">{{ ajusteItemModal.unidade }}</small><span>Valor unitário: {{ formatMoney(ajusteItemModal.precoAtual) }}</span></div>
          </div>
          <div class="restaurante-ajuste-inputs">
            <div v-for="operacao in ['acrescimo', 'desconto']" :key="operacao">
              <label :for="`ajuste_item_${operacao}`">{{ operacao === 'acrescimo' ? 'Acréscimo' : 'Desconto' }}</label>
              <div class="checkout-adjustment-input">
                <div class="checkout-adjustment-unit" role="group" :aria-label="`Unidade do ${operacao}`">
                  <button v-for="tipo in [1, 2]" :key="tipo" type="button"
                    :class="{ active: ajusteItemModal[operacao].tipo === tipo }"
                    :aria-pressed="ajusteItemModal[operacao].tipo === tipo"
                    @click="ajusteItemModal[operacao].tipo = tipo">{{ tipo === 1 ? 'R$' : '%' }}</button>
                </div>
                <div class="checkout-adjustment-value">
                  <b-form-input :id="`ajuste_item_${operacao}`" :ref="`ajusteItemInput_${operacao}`"
                    v-model="ajusteItemModal[operacao].valor" type="text" inputmode="decimal"
                    autocomplete="off" class="text-right" @blur="formatarEntradaAjusteItem(operacao)"
                    @focus="$event.target.select()" @click="$event.target.select()"
                    @keyup.enter="aplicarAjusteItem" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="restaurante-ajuste-summary">
          <div class="restaurante-ajuste-summary-title"><Cash :size="20" aria-hidden="true" /><strong>Resumo</strong></div>
          <div class="restaurante-ajuste-summary-rows">
            <div><span>Valor atual</span><strong>{{ formatMoney(ajusteItemModal.precoAtual) }}</strong></div>
            <div><span>Acréscimo</span><strong class="restaurante-ajuste-positive">{{ valorAcrescimoItem > 0 ? formatMoney(valorAcrescimoItem) : '—' }}</strong></div>
            <div><span>Desconto</span><strong class="restaurante-ajuste-negative">{{ valorDescontoItem > 0 ? formatMoney(valorDescontoItem) : '—' }}</strong></div>
          </div>
          <div class="restaurante-ajuste-summary-total"><span>Novo valor unitário</span><strong>{{ formatMoney(precoUnitarioComAjusteItem) }}</strong></div>
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button @click="cancel()">Cancelar</b-button>
        <b-button variant="primary" @click="aplicarAjusteItem">
          Aplicar
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="modal-vincular-avulsa-checkout"
      modal-class="restaurante-workspace-modal restaurante-checkout-compact-modal restaurante-checkout-avulsa-modal"
      title="Adicionar comanda avulsa"
      centered
      scrollable
      size="sm"
      :no-close-on-esc="vinculandoAvulsa"
      :no-close-on-backdrop="vinculandoAvulsa"
      :hide-header-close="vinculandoAvulsa"
      @shown="focusCodigoAvulsaMesa"
    >
      <b-form-group class="checkout-avulsa-field" label="Código da comanda" label-for="codigo_avulsa_mesa">
        <b-form-input
          id="codigo_avulsa_mesa"
          ref="codigoAvulsaMesa"
          v-model.trim="avulsaMesaForm.codigo"
          :state="avulsaMesaErro ? false : null"
          :disabled="vinculandoAvulsa"
          autocomplete="off"
          placeholder="Digite ou leia o código"
          @input="avulsaMesaErro = ''"
          @keyup.enter="vincularAvulsaMesa"
        />
        <b-form-invalid-feedback :state="!avulsaMesaErro">
          {{ avulsaMesaErro }}
        </b-form-invalid-feedback>
      </b-form-group>

      <template #modal-footer="{ cancel }">
        <b-button variant="light" class="checkout-avulsa-cancel" :disabled="vinculandoAvulsa" @click="cancel()">
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          class="checkout-avulsa-submit"
          :disabled="vinculandoAvulsa"
          @click="vincularAvulsaMesa"
        >
          {{ vinculandoAvulsa ? "Adicionando..." : "Adicionar comanda" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Cash from "vue-material-design-icons/Cash.vue";
import Cog from "vue-material-design-icons/Cog.vue";
import ChartPie from "vue-material-design-icons/ChartPie.vue";
import AccountMultipleOutline from "vue-material-design-icons/AccountMultipleOutline.vue";
import FileDocumentOutline from "vue-material-design-icons/FileDocumentOutline.vue";
import TagOutline from "vue-material-design-icons/TagOutline.vue";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import CurrencyUsd from "vue-material-design-icons/CurrencyUsd.vue";
import FormatListBulleted from "vue-material-design-icons/FormatListBulleted.vue";
import ChevronRight from "vue-material-design-icons/ChevronRight.vue";
import CartOutline from "vue-material-design-icons/CartOutline.vue";
import CashMultiple from "vue-material-design-icons/CashMultiple.vue";
import CheckCircleOutline from "vue-material-design-icons/CheckCircleOutline.vue";
import Plus from "vue-material-design-icons/Plus.vue";
import CallSplit from "vue-material-design-icons/CallSplit.vue";
import Receipt from "vue-material-design-icons/Receipt.vue";
import TrashCanIcon from "vue-material-design-icons/TrashCan.vue";
import { restauranteFeatureFlags } from "../../config";
import { consultarEstadoImpressoraQz } from "../../services/qzTrayPrinter";
import { imprimirDocumentosPdv } from "@/modules/Vendas/pdvAutoPrint";
import ValidacaoMixin from "../../../Application/mixins/ValidacaoMixin";
const NovoClienteModal = () => import("@/modules/Pessoa/components/NovoClienteModal.vue");

export default {
  name: "app-restaurante-checkout",
  mixins: [ValidacaoMixin],
  components: {
    Cash,
    Cog,
    ChartPie,
    AccountMultipleOutline,
    FileDocumentOutline,
    TagOutline,
    VueSelect,
    CurrencyUsd,
    FormatListBulleted,
    ChevronRight,
    CartOutline,
    CashMultiple,
    CheckCircleOutline,
    Plus,
    CallSplit,
    Receipt,
    TrashCanIcon,
    NovoClienteModal,
  },
  data() {
    return {
      checkout: null,
      checkoutAutoRefreshTimer: null,
      checkoutAutoRefreshEmAndamento: false,
      checkoutUltimaBusca: 0,
      checkoutVisibilityHandler: null,
      checkoutFocusHandler: null,
      atendentes: [],
      atendenteId: null,
      atendenteNome: "",
      rascunho: null,
      criandoRascunho: false,
      gerandoPreVenda: false,
      gerandoNfce: false,
      abrindoFaturamento: false,
      confirmandoPagamento: false,
      cancelandoRascunho: false,
      imprimindoConferencia: false,
      conferenciaUrl: null,
      conferenciaBlobUrl: null,
      carregandoConferenciaPdf: false,
      preVendaGerada: null,
      nfeGerada: null,
      pagamentoFechamento: null,
      tipoPagamentoCobranca: "pre_venda",
      vinculandoAvulsa: false,
      avulsaMesaErro: "",
      aberturaCaixaObrigatoria: null,
      abrindoCaixa: false,
      isFullscreen: false,
      fullscreenHandler: null,
      gerarPreVendaPendenteAbertura: false,
      tipoCobrancaPendenteAbertura: null,
      caixaValorAbertura: 0,
      caixaImpressaoComprovante: false,
      restauranteImpressaoAtiva: false,
      restauranteImpressoraCheckout: null,
      impressoraPdv: null,
      impressoesPdvTentadas: new Set(),
      restauranteImpressoraComprovante: null,
      serieNfce: 99,
      informacoesComplementaresNfce: "",
      avulsaMesaForm: {
        codigo: "",
      },
      featureFlags: restauranteFeatureFlags,
      formasPagamento: [],
      pagamentos: [],
      previsoesParcelas: {},
      parcelamentoForm: null,
      pagamentoDistribuidoAnterior: {},
      carregandoParcelamento: false,
      confirmandoParcelamento: false,
      erroParcelamento: "",
      sequenciaParcelamento: 0,
      timerQuantidadeParcelas: null,
      clienteFinanceiroId: null,
      clienteFinanceiroNome: "",
      clienteFinanceiroDocumento: "",
      carregandoPreviaParcelas: false,
      erroPreviaParcelas: "",
      sequenciaPreviaParcelas: 0,
      pagamentoForm: {
        forma_pagamento_id: null,
        valor: 0,
      },
      buscaClientePagamento: "",
      buscandoClientePagamento: false,
      resultadosClientePagamento: [],
      erroBuscaClientePagamento: "",
      sequenciaBuscaClientePagamento: 0,
      timerBuscaClientePagamento: null,
      ultimoTermoBuscaClientePagamento: "",
      ultimoPagamentoAdicionado: null,
      clienteAntesPagamento: null,
      clienteSelecionadoNoPagamento: false,
      clienteCadastroPagamentoPendente: false,
      ajusteItemModal: {
        atendimento_item_id: null,
        produto: "",
        imagem: "",
        unidade: "",
        acrescimo: { tipo: 1, valor: "" },
        desconto: { tipo: 1, valor: "" },
        precoAtual: 0,
      },
      cobrancaItens: [],
      documentoCobranca: "",
      clienteDocumentoLocalizado: false,
      clienteDocumentoNome: "",
      clienteDocumentoNaoCadastrado: false,
      clienteDocumentoInativo: false,
      clientePessoaId: null,
      documentoConsultado: null,
      cobrancaIdempotencyToken: null,
      edicaoPersistidaOriginal: null,
      faturamentoPendente: null,
      dividindoCobranca: false,
      divisaoModalSelecionandoModo: true,
      faturamentoModalPendente: null,
      modoDivisao: null,
      distribuicaoFinanceira: [],
      proximaCobrancaFinanceiraId: 1,

      cancelandoDivisao: false,
      cobrancaParcialConcluida: false,
      totalFinanceiroBackend: null,
      calculandoTotalFinanceiro: false,
      sequenciaTotalFinanceiro: 0,
      form: {
        escopo: "mesa",
        comanda_id: null,
        pagador: "mesa",
      },
      taxaServicoPercentual: 0,
      couvertValor: 0,
      acrescimoTipo: 1,
      acrescimoValor: 0,
      descontoTipo: 1,
      descontoValor: 0,
      ajusteTexto: { taxaServico: null, couvert: null, acrescimo: null, desconto: null },
      atalhosPagamento: [
        { tecla: "F1", descricao: "C. Débito", termos: ["débito", "debito"] },
        {
          tecla: "F2",
          descricao: "C. Crédito",
          termos: ["crédito", "credito"],
        },
        { tecla: "F3", descricao: "Pix", termos: ["pix"] },
        { tecla: "F4", descricao: "À vista", termos: ["à vista", "a vista"] },
      ],
    };
  },
  computed: {
    tituloModalDivisao() {
      if (this.divisaoModalSelecionandoModo || !this.modoDivisao) {
        return "Como deseja dividir?";
      }
      return this.modoDivisao === "itens"
        ? "Dividir por Itens"
        : "Dividir por Valor";
    },
    ...mapGetters(["itsblocked"]),
    usuarioAutenticado() {
      return this.$store?.getters?.currentUser || null;
    },
    atendenteSelecionado() {
      return this.atendentes.find((item) => Number(item.id_usuario) === Number(this.atendenteId)) ||
        (this.atendenteId ? { id_usuario: this.atendenteId, label: this.atendenteId + " - " + this.atendenteNome } : null);
    },
    atendimentoId() {
      return this.checkout?.atendimento?.id || this.$route.params.atendimentoId;
    },
    documentoCobrancaValido() {
      return (
        !this.documentoCobranca || this.validaCpfCnpj(this.documentoCobranca)
      );
    },
    codigoComandaRoute() {
      return (
        this.$route.params.codigoComanda ||
        this.$route.query.codigo_comanda ||
        null
      );
    },
    checkoutComandaIdRoute() {
      return this.$route.query.comanda_id || null;
    },
    modoApenasComandasCheckout() {
      return this.$route.query.modo_comandas === "1";
    },
    checkoutItensTitle() {
      return this.form.escopo === "comanda" || this.modoApenasComandasCheckout
        ? "Itens pendentes da comanda"
        : "Itens pendentes da mesa";
    },
    mesaLabel() {
      if (!this.checkout || !this.checkout.mesa) {
        if (
          this.checkout?.checkout_origem === "comanda_avulsa" ||
          this.modoApenasComandasCheckout
        ) {
          const comanda = (this.checkout?.comandas || []).find(
            (item) => Number(item.id) === Number(this.form.comanda_id)
          );

          return comanda ? this.comandaLabel(comanda) : "Comanda";
        }

        return "-";
      }

      const vinculadas = this.checkout.mesa.mesas_vinculadas || [];

      if (!vinculadas.length) {
        return `Mesa ${this.checkout.mesa.numero}`;
      }

      return `Mesa ${this.checkout.mesa.numero} + ${vinculadas
        .map((mesa) => mesa.numero)
        .join(", ")}`;
    },
    ambienteLabel() {
      return (
        this.checkout?.ambiente?.nome ||
        this.checkout?.mesa?.ambiente_nome ||
        "-"
      );
    },
    canVincularAvulsaMesa() {
      return (
        !this.modoApenasComandasCheckout && Boolean(this.checkout?.mesa?.id)
      );
    },
    mostrarSeletorComandas() {
      const comandas = this.checkout?.comandas || [];
      if (comandas.length !== 1) return comandas.length > 1;
      if (this.modoApenasComandasCheckout) return false;
      const comanda = comandas[0];
      const mesmosItens = (this.checkout?.itens || []).every(
        (item) => Number(item.comanda_id) === Number(comanda.id)
      );
      const mesmosTotais = [
        "valor_total", "valor_ja_cobrado", "valor_reservado", "valor_pendente",
      ].every((campo) =>
        Math.round(Number(this.baseTotals[campo] || 0) * 100) ===
        Math.round(Number(comanda[campo] || 0) * 100)
      );
      return !mesmosItens || !mesmosTotais;
    },
    baseTotals() {
      return (
        this.checkout?.totais || {
          valor_total: 0,
          valor_ja_cobrado: 0,
          valor_reservado: 0,
          valor_pendente: 0,
        }
      );
    },
    selectedComanda() {
      if (this.form.escopo !== "comanda" || !this.form.comanda_id) {
        return null;
      }

      return (this.checkout?.comandas || []).find(
        (comanda) => Number(comanda.id) === Number(this.form.comanda_id)
      );
    },
    currentTotals() {
      if (!this.selectedComanda) {
        return this.baseTotals;
      }

      return {
        valor_total: Number(this.selectedComanda.valor_total || 0),
        valor_ja_cobrado: Number(this.selectedComanda.valor_ja_cobrado || 0),
        valor_reservado: Number(this.selectedComanda.valor_reservado || 0),
        valor_pendente: Number(this.selectedComanda.valor_pendente || 0),
      };
    },
    currentTotalsLabel() {
      return this.form.escopo === "comanda" ? "Total da comanda" : "Total da mesa";
    },
    resumoFinanceiro() {
      if (this.fechamentoNfcePendente) {
        return {
          valor_total: Number(this.fechamentoNfcePendente.valor_total || 0),
          valor_ja_pago: 0,
          valor_pendente_pagamento: Number(this.fechamentoNfcePendente.valor_total || 0),
        };
      }
      return this.form.escopo === "comanda"
        ? this.selectedComanda?.totais_financeiros || null
        : this.checkout?.totais_financeiros || null;
    },
    totaisRecuperacaoNfce() {
      return this.fechamentoNfcePendente?.totais_cobranca || {};
    },
    itensRecuperacaoNfce() {
      const fechamento = this.fechamentoNfcePendente;
      if (!fechamento) return [];
      if (fechamento.itens_documento?.length) return fechamento.itens_documento;
      return (fechamento.itens || []).map((item) => ({
        descricao: item.produto?.descricao || `Produto #${item.produto_id}`,
        quantidade: Number(item.quantidade || 0),
        valor_unitario: Number(item.valor_unitario || 0),
        valor_total: Number(item.valor_total || 0),
      }));
    },
    avisosCheckout() {
      return (this.checkout?.alertas || []).filter((alerta) =>
        ![
          "Atendimento aberto sem itens. Fechamento bloqueado.",
          "Mesa ocupada com total zerado.",
        ].includes(alerta)
      );
    },
    resumoComandaIndisponivel() {
      return Boolean(this.selectedComanda &&
        this.resumoFinanceiro?.valores_financeiros_indisponiveis);
    },
    cobrancasDivisao() {
      return (this.checkout?.fechamentos || []).filter((fechamento) =>
        ["itens", "valor"].includes(fechamento.modo_divisao) ||
        Array.isArray(fechamento.distribuicao_financeira)
      );
    },
    rascunhoAtivo() {
      const mesmoEscopo = (fechamento) => fechamento?.escopo === this.form.escopo &&
        Number(fechamento.comanda_id || 0) === Number(this.form.comanda_id || 0);
      if (mesmoEscopo(this.rascunho)) return this.rascunho;

      const recuperaveis = (this.checkout?.fechamentos_diretos_recuperaveis || [])
        .filter(mesmoEscopo);
      return recuperaveis.length === 1 ? recuperaveis[0] : null;
    },
    rascunhoCobrancaAtual() {
      return this.dividindoCobranca ? this.rascunho : this.rascunhoAtivo;
    },

    itensDoEscopo() {
      const itens = this.checkout?.itens || [];
      const fechamentoDireto = !this.dividindoCobranca && this.rascunhoAtivo;
      if (fechamentoDireto?.id) {
        const reservados = new Map((fechamentoDireto.itens || []).map((linha) =>
          [Number(linha.atendimento_item_id), Number(linha.quantidade)]));
        return itens.filter((item) => reservados.has(Number(item.id)) &&
          (this.form.escopo !== "comanda" ||
            Number(item.comanda_id) === Number(this.form.comanda_id)))
          .map((item) => {
            const quantidade = reservados.get(Number(item.id));
            const unitario = Number(item.quantidade || 0) > 0
              ? Number(item.valor_total || 0) / Number(item.quantidade) : 0;
            return {
              ...item,
              quantidade_pendente: quantidade,
              valor_pendente: this.roundCurrency(unitario * quantidade),
            };
          });
      }
      const disponiveis = this.dividindoCobranca && this.modoDivisao === "itens"
        ? itens.map((item) => {
            const reservado = (this.rascunho?.itens || []).find(
              (linha) => Number(linha.atendimento_item_id) === Number(item.id)
            );
            if (!reservado) return item;
            const quantidade = Number(reservado.quantidade || 0);
            const valorUnitario = Number(item.quantidade || 0) > 0
              ? Number(item.valor_total || 0) / Number(item.quantidade)
              : 0;
            return {
              ...item,
              quantidade_pendente: Number(item.quantidade_pendente || 0) + quantidade,
              valor_pendente: this.roundCurrency(
                Number(item.valor_pendente || 0) + valorUnitario * quantidade
              ),
            };
          })
        : itens;
      if (this.form.escopo !== "comanda") {
        return disponiveis;
      }

      return disponiveis.filter(
        (item) => Number(item.comanda_id || 0) === Number(this.form.comanda_id)
      );
    },
    itensPendentes() {
      return this.itensDoEscopo.filter(
        (item) =>
          Number(item.quantidade_pendente || 0) > 0 &&
          Number(item.valor_pendente || 0) > 0 &&
          item.cobravel !== false
      );
    },
    podeDividirCobranca() {
      return (
        !this.dividindoCobranca &&
        Number(this.currentTotals.valor_pendente || 0) > 0 &&
        !this.rascunhoCobrancaAtual &&
        !this.fechamentosNfcePendentes.length &&
        !this.preVendaGerada &&
        !this.nfeGerada &&
        !this.pagamentoFechamento
      );
    },
    edicaoPersistidaAlterada() {
      return Boolean(
        this.dividindoCobranca &&
        this.rascunho?.id &&
        this.edicaoPersistidaOriginal !== JSON.stringify(this.requestPayload())
      );
    },
    podeTrocarModoDivisao() {
      return this.podeEditarDivisao && !this.rascunho?.id;
    },
    podeEditarDivisao() {
      return (
        this.dividindoCobranca &&
        (!this.rascunho?.id ||
          ["rascunho", "preview", "aguardando_conversao"].includes(
            this.rascunho.status
          )) &&
        !this.rascunho?.pre_venda_id &&
        !this.rascunho?.venda_id &&
        !this.rascunho?.nfe_id &&
        !this.rascunho?.pagamentos?.length &&
        !this.preVendaGerada &&
        !this.nfeGerada &&
        !this.pagamentoFechamento
      );
    },
    totalDistribuirCentavos() {
      const total =
        this.modoDivisao === "valor" && this.totalFinanceiroBackend !== null
          ? this.totalFinanceiroBackend
          : this.totalCobrancaExibicao;
      return Math.round(total * 100);
    },
    distribuidoCentavos() {
      return this.distribuicaoFinanceira.reduce(
        (total, cobranca) =>
          total + (this.centavosFinanceiros(cobranca.valor) || 0),
        0
      );
    },
    restanteDistribuicaoCentavos() {
      return this.totalDistribuirCentavos - this.distribuidoCentavos;
    },
    rascunhoFinanceiroIncompleto() {
      return Boolean(
        this.rascunho?.rascunho_financeiro_incompleto ||
        this.rascunho?.payload_preview?.rascunho_financeiro_incompleto
      );
    },
    podeEditarDistribuicaoFinanceira() {
      return (
        this.dividindoCobranca &&
        this.podeEditarDivisao
      );
    },
    distribuicaoFinanceiraSalvavel() {
      return (
        this.modoDivisao === "valor" &&
        this.totalDistribuirCentavos > 0 &&
        this.totalFinanceiroBackend !== null &&
        !this.calculandoTotalFinanceiro &&
        this.distribuicaoFinanceira.length > 0 &&
        this.distribuicaoFinanceira.length <= 20 &&
        this.distribuicaoFinanceira.every(
          (cobranca) => this.centavosFinanceiros(cobranca.valor) >= 0
        ) &&
        this.restanteDistribuicaoCentavos >= 0
      );
    },
    distribuicaoFinanceiraValida() {
      return (
        this.modoDivisao === "valor" &&
        this.totalDistribuirCentavos > 0 &&
        this.totalFinanceiroBackend !== null &&
        !this.calculandoTotalFinanceiro &&
        this.distribuicaoFinanceira.length >= 2 &&
        this.distribuicaoFinanceira.length <= 20 &&
        this.distribuicaoFinanceira.every(
          (cobranca) => this.centavosFinanceiros(cobranca.valor) > 0
        ) &&
        this.restanteDistribuicaoCentavos === 0
      );
    },
    mensagemDistribuicaoFinanceira() {
      if (this.calculandoTotalFinanceiro) {
        return "Atualizando o total da conta...";
      }
      if (this.totalFinanceiroBackend === null) {
        return "Não foi possível calcular o total. Atualize o Checkout.";
      }
      if (this.totalDistribuirCentavos < 2) {
        return "A divisão exige pelo menos R$ 0,02 para duas cobranças.";
      }
      if (this.distribuicaoFinanceira.length < 2) {
        return "Adicione pelo menos duas cobranças.";
      }
      if (
        this.distribuicaoFinanceira.some(
          (cobranca) => !(this.centavosFinanceiros(cobranca.valor) > 0)
        )
      ) {
        return "Informe um valor positivo em cada cobrança.";
      }
      return "Distribua todo o valor da conta para continuar.";
    },
    distribuicaoFinanceiraFechamento() {
      const fechamento = this.pagamentoFechamento;
      return (
        fechamento?.distribuicao_financeira ||
        fechamento?.payload_preview?.distribuicao_financeira ||
        null
      );
    },
    pagamentoDistribuido() {
      return (
        Array.isArray(this.distribuicaoFinanceiraFechamento) &&
        this.distribuicaoFinanceiraFechamento.length > 0
      );
    },
    podeEditarAjustesCobranca() {
      return (
        (this.dividindoCobranca
          ? this.podeEditarDivisao
          : !this.rascunhoCobrancaAtual?.id &&
            !this.fechamentosNfcePendentes.length &&
            !this.preVendaGerada &&
            !this.nfeGerada &&
            !this.pagamentoFechamento)
      );
    },
    formaPagamentoOptions() {
      const repetidas = this.formasPagamento.reduce((contagem, forma) => {
        const nome = String(forma.descricao || "").trim().toLocaleLowerCase("pt-BR");
        contagem[nome] = (contagem[nome] || 0) + 1;
        return contagem;
      }, {});
      return [
        { value: null, text: "Selecione a forma" },
        ...this.formasPagamento.map((forma) => ({
          value: forma.id,
          text: repetidas[String(forma.descricao || "").trim().toLocaleLowerCase("pt-BR")] > 1
            ? `${forma.descricao} — ${this.destinoFinanceiroForma(forma) === "caixa" ? "Caixa" : "Receber"}`
            : forma.descricao,
        })),
      ];
    },
    destinoFormaSelecionada() {
      const forma = this.formasPagamento.find((item) =>
        Number(item.id) === Number(this.pagamentoForm.forma_pagamento_id));
      return this.destinoFinanceiroForma(forma);
    },
    parcelamentoForma() {
      return this.formasPagamento.find((forma) =>
        Number(forma.id) === Number(this.parcelamentoForm?.forma_pagamento_id)) || null;
    },
    parcelamentoIntervaloLabel() {
      const labels = ["Nenhum", "Diário", "Semanal", "Quinzenal", "Mensal",
        "Bimestral", "Trimestral", "Semestral", "Anual", "Intervalos definidos"];
      return labels[Number(this.parcelamentoForma?.tipo_repeticao)] || "Conforme cadastro";
    },
    erroQuantidadeParcelamento() {
      const form = this.parcelamentoForm;
      if (!form) return "";
      if (form.quantidade === "") return "";
      const limite = Math.min(Number(form.limite), Number(this.parcelamentoForma?.vezes));
      if (!/^\d+$/.test(String(form.quantidade)) || Number(form.quantidade) < 1)
        return "Informe uma quantidade válida.";
      if (Number(form.quantidade) > limite)
        return `Máximo de ${limite} parcelas para esta condição.`;
      return "";
    },
    mensagemValoresParcelamento() {
      const form = this.parcelamentoForm;
      if (!form?.parcelas?.length) return "";
      if (form.parcelas.some((parcela) => this.erroValorParcela(parcela.value)))
        return "Informe valores positivos com até duas casas decimais.";
      const soma = form.parcelas.reduce((total, parcela) =>
        total + Math.round(this.parseValorPagamento(parcela.value) * 100), 0);
      const diferenca = Math.round(form.valor * 100) - soma;
      if (diferenca > 0) return `Falta distribuir ${this.formatMoney(diferenca / 100)}.`;
      if (diferenca < 0) return `Valor excedido em ${this.formatMoney(-diferenca / 100)}.`;
      return "";
    },
    podeConfirmarParcelamento() {
      const form = this.parcelamentoForm;
      return Boolean(form && this.clienteFinanceiroId && !this.carregandoParcelamento &&
        !this.confirmandoParcelamento && !this.timerQuantidadeParcelas &&
        /^\d+$/.test(String(form.quantidade)) &&
        !this.erroQuantidadeParcelamento &&
        Number(form.quantidade) === form.parcelas.length &&
        this.parcelasPagamentoValidas(form));
    },
    clienteParcelamentoPendente() {
      const form = this.parcelamentoForm;
      return Boolean(form && !this.clienteFinanceiroId && !this.buscandoClientePagamento &&
        !this.carregandoParcelamento && /^\d+$/.test(String(form.quantidade)) &&
        Number(form.quantidade) === form.parcelas.length &&
        this.parcelasPagamentoValidas(form));
    },
    cobrancaItensDetalhados() {
      return this.cobrancaItens
        .map((linha) => {
          const item = this.itensDoEscopo.find(
            (item) => item.id === linha.atendimento_item_id
          ) || (this.rascunho?.itens || []).find(
            (salvo) =>
              Number(salvo.atendimento_item_id) ===
              Number(linha.atendimento_item_id)
          );

          if (!item) {
            return null;
          }

          return {
            ...linha,
            item,
          };
        })
        .filter(Boolean);
    },
    subtotalCobrancaLocal() {
      return this.cobrancaItensDetalhados.reduce(
        (total, linha) => total + this.valorLinhaCobranca(linha),
        0
      );
    },
    totaisPreviewCobranca() {
      return this.rascunhoCobrancaAtual?.totais_cobranca || {};
    },
    subtotalProdutosCobranca() {
      if (this.dividindoCobranca && this.cobrancaItensDetalhados.length &&
        (this.modoDivisao === "itens" || this.rascunhoFinanceiroIncompleto)) {
        return this.roundCurrency(this.subtotalCobrancaLocal);
      }
      const subtotalBackend = this.pickNumber(this.totaisPreviewCobranca, [
        "valor_produtos",
        "subtotal_produtos",
        "subtotal",
        "valor_subtotal",
      ]);

      if (
        (this.dividindoCobranca || this.rascunhoAtivo?.id) &&
        this.totaisPreviewCobranca.valor_bruto !== undefined
      ) {
        return this.roundCurrency(
          Number(this.totaisPreviewCobranca.valor_bruto || 0) -
            Number(this.totaisPreviewCobranca.valor_desconto_itens ??
              this.totaisPreviewCobranca.valor_desconto ?? 0) +
            Number(this.totaisPreviewCobranca.valor_acrescimo_itens ??
              this.totaisPreviewCobranca.valor_acrescimo ?? 0)
        );
      }
      if (this.subtotalCobrancaLocal > 0) {
        return this.roundCurrency(this.subtotalCobrancaLocal);
      }

      return this.roundCurrency(
        subtotalBackend ??
          this.pickNumber(this.rascunhoCobrancaAtual, [
            "valor_produtos",
            "subtotal_produtos",
            "valor_subtotal",
            "valor_total",
          ]) ??
          0
      );
    },
    taxaServicoPercentualAtual() {
      if (this.dividindoCobranca && this.rascunhoFinanceiroIncompleto) {
        return Number(this.taxaServicoPercentual || 0);
      }
      return (
        this.pickNumber(this.totaisPreviewCobranca, [
          "taxa_servico_percentual",
          "percentual_taxa_servico",
        ]) ?? Number(this.taxaServicoPercentual || 0)
      );
    },
    couvertValorAtual() {
      if (this.dividindoCobranca && this.rascunhoFinanceiroIncompleto) {
        return Number(this.couvertValor || 0);
      }
      return (
        this.pickNumber(this.totaisPreviewCobranca, [
          "couvert_valor",
          "valor_couvert_unitario",
        ]) ?? Number(this.couvertValor || 0)
      );
    },
    valorTaxaServicoBackend() {
      return this.pickNumber(this.totaisPreviewCobranca, [
        "valor_taxa_servico",
        "taxa_servico",
        "taxa_servico_valor",
      ]);
    },
    valorCouvertBackend() {
      return this.pickNumber(this.totaisPreviewCobranca, [
        "valor_couvert",
        "couvert",
        "couvert_total",
      ]);
    },
    possuiAjusteLocal() {
      return (
        Number(this.taxaServicoPercentual || 0) > 0 ||
        Number(this.couvertValor || 0) > 0 ||
        Number(this.acrescimoValor || 0) > 0 ||
        Number(this.descontoValor || 0) > 0
      );
    },
    baseAjustesGerais() {
      return this.subtotalProdutosCobranca;
    },
    taxaServicoEntradaExibicao() {
      return this.ajusteTexto.taxaServico !== null
        ? this.ajusteTexto.taxaServico
        : this.formatarEntradaAjuste("taxaServico");
    },
    couvertEntradaExibicao() {
      return this.ajusteTexto.couvert !== null
        ? this.ajusteTexto.couvert
        : this.formatarEntradaAjuste("couvert");
    },
    acrescimoEntradaExibicao() {
      return this.ajusteTexto.acrescimo !== null
        ? this.ajusteTexto.acrescimo
        : this.formatarEntradaAjuste("acrescimo");
    },
    descontoEntradaExibicao() {
      return this.ajusteTexto.desconto !== null
        ? this.ajusteTexto.desconto
        : this.formatarEntradaAjuste("desconto");
    },
    ajustesGeraisValidos() {
      if (["taxaServico", "couvert", "acrescimo", "desconto"].some(
        (campo) => this.entradaAjusteInvalida(campo)
      )) return false;
      const valorAcrescimo = Number(this.acrescimoValor || 0);
      const valorDesconto = Number(this.descontoValor || 0);
      return [valorAcrescimo, valorDesconto].every(
        (valor) => Number.isFinite(valor) && valor >= 0 && valor <= 9999999999999.99
      ) &&
        (this.acrescimoTipo !== 2 || valorAcrescimo <= 100) &&
        (this.descontoTipo !== 2 || valorDesconto <= 100) &&
        this.valorDescontoLocal <= this.baseAjustesGerais &&
        this.totalCobrancaLocal > 0;
    },
    valorAcrescimoLocal() {
      return this.roundCurrency(this.acrescimoTipo === 2
        ? this.baseAjustesGerais * Number(this.acrescimoValor || 0) / 100
        : Number(this.acrescimoValor || 0));
    },
    valorDescontoLocal() {
      return this.roundCurrency(this.descontoTipo === 2
        ? this.baseAjustesGerais * Number(this.descontoValor || 0) / 100
        : Number(this.descontoValor || 0));
    },
    valorTaxaServicoLocal() {
      return this.roundCurrency(
        this.subtotalProdutosCobranca *
          (Number(this.taxaServicoPercentual || 0) / 100)
      );
    },
    valorTaxaServico() {
      if (this.dividindoCobranca) {
        return this.valorTaxaServicoLocal;
      }
      if (this.rascunhoAtivo?.id && !this.dividindoCobranca) {
        return this.valorTaxaServicoBackend ?? 0;
      }
      if (Number(this.taxaServicoPercentual || 0) > 0) {
        return this.valorTaxaServicoLocal;
      }

      return this.valorTaxaServicoBackend ?? 0;
    },
    valorCouvertLocal() {
      return this.roundCurrency(Number(this.couvertValor || 0));
    },
    valorCouvertTotal() {
      if (this.dividindoCobranca) {
        return this.valorCouvertLocal;
      }
      if (this.rascunhoAtivo?.id && !this.dividindoCobranca) {
        return this.valorCouvertBackend ?? 0;
      }
      if (Number(this.couvertValor || 0) > 0) {
        return this.valorCouvertLocal;
      }

      return this.valorCouvertBackend ?? 0;
    },
    valorAcrescimoCobranca() {
      if (this.rascunhoAtivo?.id && !this.dividindoCobranca) {
        return Number(this.totaisPreviewCobranca.valor_acrescimo_geral || 0);
      }
      return this.valorAcrescimoLocal;
    },
    valorDescontoCobranca() {
      if (this.rascunhoAtivo?.id && !this.dividindoCobranca) {
        return Number(this.totaisPreviewCobranca.valor_desconto_geral || 0);
      }
      return this.valorDescontoLocal;
    },
    totalCobrancaLocal() {
      return this.roundCurrency(
        this.subtotalProdutosCobranca + this.valorTaxaServico +
        this.valorCouvertTotal + this.valorAcrescimoCobranca -
        this.valorDescontoCobranca
      );
    },
    totalCobrancaExibicao() {
      if (this.rascunhoAtivo?.id && !this.dividindoCobranca) {
        return this.roundCurrency(Number(this.rascunhoAtivo.valor_total || 0));
      }
      const totalBackend = this.pickNumber(this.totaisPreviewCobranca, [
        "valor_total",
        "total",
        "total_cobranca",
      ]);

      if (this.modoDivisao === "valor" && this.totalFinanceiroBackend !== null) {
        return this.totalFinanceiroBackend;
      }
      if (this.dividindoCobranca && this.modoDivisao === "itens") {
        return this.totalCobrancaLocal;
      }

      if (
        !this.dividindoCobranca &&
        (this.possuiAjusteLocal || this.valorAcrescimoCobranca > 0)
      ) {
        return this.totalCobrancaLocal;
      }

      return this.roundCurrency(totalBackend ?? this.totalCobrancaLocal);
    },
    totaisCobrancaCalculados() {
      return {
        valor_produtos: this.subtotalProdutosCobranca,
        subtotal_produtos: this.subtotalProdutosCobranca,
        valor_subtotal: this.subtotalProdutosCobranca,
        taxa_servico_percentual: this.taxaServicoPercentualAtual,
        valor_taxa_servico: this.valorTaxaServico,
        taxa_servico_valor: this.valorTaxaServico,
        couvert_valor: this.couvertValorAtual,
        valor_couvert: this.valorCouvertTotal,
        couvert_total: this.valorCouvertTotal,
        valor_acrescimo_geral: this.valorAcrescimoCobranca,
        valor_desconto_geral: this.valorDescontoCobranca,
        acrescimo: this.valorAcrescimoCobranca,
        desconto: this.valorDescontoCobranca,
        extra_value: this.roundCurrency(this.valorTaxaServico + this.valorCouvertTotal + this.valorAcrescimoCobranca),
        extraValue: this.roundCurrency(this.valorTaxaServico + this.valorCouvertTotal + this.valorAcrescimoCobranca),
        discount: this.valorDescontoCobranca,
        tipoAcrescimo: this.tipoAcrescimoCobranca,
        tipoAcrescimoValue:
          this.valorTaxaServico + this.valorCouvertTotal + this.valorAcrescimoCobranca > 0
            ? 1 : null,
        valor_total: this.totalCobrancaExibicao,
        total: this.totalCobrancaExibicao,
        total_cobranca: this.totalCobrancaExibicao,
      };
    },
    tipoAcrescimoCobranca() {
      return this.valorTaxaServico + this.valorCouvertTotal +
        this.valorAcrescimoCobranca > 0
        ? { value: 1, text: "Valor R$" }
        : null;
    },
    canPreview() {
      return (
        !this.isBlocked &&
        !this.calculandoTotalFinanceiro &&
        this.ajustesGeraisValidos &&
        (!this.dividindoCobranca ||
          this.modoDivisao === "itens" ||
          (this.modoDivisao === "valor" &&
            this.distribuicaoFinanceiraValida)) &&
        this.cobrancaItens.length > 0 &&
        (this.form.escopo === "mesa" || Boolean(this.form.comanda_id))
      );
    },
    podeImprimirConferencia() {
      return (
        Boolean(this.checkout?.atendimento?.id) &&
        this.ajustesGeraisValidos &&
        this.cobrancaItens.length > 0 &&
        (this.form.escopo === "mesa" || Boolean(this.form.comanda_id)) &&
        !this.imprimindoConferencia
      );
    },
    canCriarRascunho() {
      const podeSalvar = this.modoDivisao === "valor"
        ? !this.isBlocked &&
          this.distribuicaoFinanceiraSalvavel &&
          this.cobrancaItens.length > 0 &&
          (this.form.escopo === "mesa" || Boolean(this.form.comanda_id))
        : this.canPreview;
      return (
        podeSalvar &&
        this.ajustesGeraisValidos &&
        !this.criandoRascunho &&
        (!this.rascunhoCobrancaAtual || this.podeEditarDivisao) &&
        !this.preVendaGerada
      );
    },
    canFinalizarPreVenda() {
      return (
        (this.fechamentoPreVendaPendente ||
          (this.dividindoCobranca
            ? this.canPreview
            : this.canPreview ||
              (Boolean(this.rascunhoCobrancaAtual?.id) &&
                !this.rascunhoFinanceiroIncompleto))) &&
        !this.criandoRascunho &&
        !this.gerandoPreVenda &&
        !this.confirmandoPagamento &&
        !this.fechamentosNfcePendentes.length &&
        !this.preVendaGerada
      );
    },
    fechamentoPreVendaPendente() {
      if (this.dividindoCobranca) return null;
      const candidatos = (this.checkout?.fechamentos || []).filter((fechamento) =>
        Boolean(fechamento.pre_venda_id) &&
        !fechamento.pagamentos?.length &&
        fechamento.status === "convertido" &&
        fechamento.escopo === this.form.escopo &&
        Number(fechamento.comanda_id || 0) === Number(this.form.comanda_id || 0)
      );
      return candidatos.length === 1 ? candidatos[0] : null;
    },
    fechamentosNfcePendentes() {
      if (this.dividindoCobranca) return [];
      return (this.checkout?.fechamentos || []).filter((fechamento) =>
        Boolean(fechamento.nfe_id) &&
        !fechamento.pre_venda_id &&
        !fechamento.pagamentos?.length &&
        ["preview", "erro", "aguardando_conversao"].includes(fechamento.status) &&
        fechamento.escopo === this.form.escopo &&
        Number(fechamento.comanda_id || 0) === Number(this.form.comanda_id || 0)
      );
    },
    fechamentoNfcePendente() {
      const candidatos = this.fechamentosNfcePendentes || [];
      return candidatos.length === 1 ? candidatos[0] : null;
    },
    canFinalizarNfce() {
      return (
        this.featureFlags.nfceCheckout &&
        this.fechamentosNfcePendentes.length <= 1 &&
        (this.fechamentoNfcePendente ||
          (this.dividindoCobranca
            ? this.canPreview
            : this.canPreview ||
              (Boolean(this.rascunhoCobrancaAtual?.id) &&
                !this.rascunhoFinanceiroIncompleto))) &&
        this.rascunhoCobrancaAtual?.status !== "erro" &&
        !this.criandoRascunho &&
        !this.gerandoNfce &&
        !this.confirmandoPagamento &&
        !this.preVendaGerada
      );
    },
    totalPago() {
      return this.pagamentos.reduce(
        (total, pagamento) => total + Number(pagamento.valor || 0),
        0
      );
    },
    restantePagamento() {
      return Math.max(
        Number(this.pagamentoFechamento?.valor_total || 0) - this.totalPago,
        0
      );
    },
    trocoPagamento() {
      return Math.max(
        this.totalPago - Number(this.pagamentoFechamento?.valor_total || 0),
        0
      );
    },
    trocoPrevistoPagamento() {
      if (this.pagamentoDistribuido || this.trocoPagamento > 0) return 0;
      const forma = this.formasPagamento.find((item) =>
        Number(item.id) === Number(this.pagamentoForm.forma_pagamento_id));
      const valor = this.parseValorPagamento(this.pagamentoForm.valor);
      if (Number(forma?.id_meio_pagamento) !== 1 ||
          this.destinoFinanceiroForma(forma) !== "caixa" ||
          !Number.isFinite(valor) || valor <= 0 ||
          Math.abs(Math.round(valor * 100) - valor * 100) > 0.000001 ||
          this.pagamentos.some((pagamento) => {
            const formaAdicionada = this.formasPagamento.find((item) =>
              Number(item.id) === Number(pagamento.forma_pagamento_id));
            return Number(formaAdicionada?.id_meio_pagamento) === 1;
          })) return 0;
      return Math.max(this.totalPago + valor - this.pagamentoFechamentoValor, 0);
    },
    trocoExibidoPagamento() {
      return this.trocoPagamento > 0 ? this.trocoPagamento : this.trocoPrevistoPagamento;
    },
    pagamentoFechamentoValor() {
      return Number(this.pagamentoFechamento?.valor_total || 0);
    },
    canAdicionarPagamento() {
      const valor = this.parseValorPagamento(this.pagamentoForm.valor);
      return (
        !this.pagamentoDistribuido &&
        (Boolean(this.pagamentoFechamento?.id) ||
          Boolean(this.faturamentoPendente)) &&
        this.destinoFormaSelecionada !== "invalido" &&
        Number.isFinite(valor) && valor > 0 && valor <= 9999999999999.99
      );
    },
    canConfirmarPagamento() {
      return (
        (Boolean(this.pagamentoFechamento?.id) ||
          Boolean(this.faturamentoPendente)) &&
        this.pagamentos.length > 0 &&
        this.pagamentos.every((pagamento) => {
          const forma = this.formasPagamento.find((item) =>
            Number(item.id) === Number(pagamento.forma_pagamento_id));
          const destino = this.destinoFinanceiroForma(forma);
          return destino !== "invalido";
        }) &&
        (this.pagamentoDistribuido
          ? Math.round(this.totalPago * 100) ===
              Math.round(this.pagamentoFechamentoValor * 100)
          : this.restantePagamento <= 0) &&
        !this.confirmandoPagamento &&
        this.trocoPagamentoValido &&
        !this.buscandoClientePagamento && !this.timerBuscaClientePagamento &&
        (!this.pagamentosPrazo.length || Boolean(this.clienteFinanceiroId)) &&
        this.pagamentosPrazo.every((linha) =>
          this.parcelasPagamentoValidas(linha.pagamento)
        ) &&
        !this.gerandoNfce
      );
    },
    pagamentoModalTitle() {
      return this.tipoPagamentoCobranca === "nfce"
        ? "Defina a forma de pagamento da NFC-e"
        : "Defina a forma de pagamento da pré-venda";
    },
    pagamentosPrazo() {
      return this.pagamentos.map((pagamento, index) => ({
        pagamento,
        index,
        forma: this.formasPagamento.find((forma) =>
          Number(forma.id) === Number(pagamento.forma_pagamento_id)
        ),
      })).filter((linha) => this.destinoFinanceiroForma(linha.forma) === "receber");
    },
    totalReceberPagamento() {
      return this.pagamentosPrazo.reduce((total, linha) => total + Number(linha.pagamento.valor || 0), 0);
    },
    trocoPagamentoValido() {
      if (this.trocoPagamento <= 0) return true;
      const dinheiro = this.pagamentos.filter((pagamento) => {
        const forma = this.formasPagamento.find((item) => Number(item.id) === Number(pagamento.forma_pagamento_id));
        return Number(forma?.id_meio_pagamento) === 1;
      });
      return dinheiro.length === 1 && Number(dinheiro[0].valor) >= this.trocoPagamento;
    },
    confirmarPagamentoLabel() {
      if (this.confirmandoPagamento || this.gerandoNfce) {
        return this.tipoPagamentoCobranca === "nfce"
          ? "Emitindo NFC-e..."
          : "Finalizando...";
      }

      return this.tipoPagamentoCobranca === "nfce"
        ? "(F5) Emitir NFC-e"
        : "(F8) Finalizar";
    },
    atalhoFinalizacaoPagamento() {
      return this.tipoPagamentoCobranca === "nfce" ? "f5" : "f8";
    },
    valorAcrescimoItem() {
      return this.calcularValorAjusteItem("acrescimo");
    },
    valorDescontoItem() {
      return this.calcularValorAjusteItem("desconto");
    },
    precoUnitarioComAjusteItem() {
      const precoBase = Number(this.ajusteItemModal.precoAtual || 0);
      if (!Number.isFinite(precoBase) || precoBase <= 0) {
        return 0;
      }
      return Math.max(this.roundCurrency(
        precoBase + this.valorAcrescimoItem - this.valorDescontoItem
      ), 0);
    },
    isBlocked() {
      if (!this.checkout) {
        return true;
      }

      return (
        this.checkout.flags?.atendimento_sem_itens ||
        !this.checkout.flags?.atendimento_aberto ||
        (this.cobrancaItens.length === 0 &&
          Number(this.currentTotals.valor_pendente || 0) <= 0)
      );
    },
    gerarPreVendaLabel() {
      if (this.criandoRascunho) {
        return "Preparando cobrança...";
      }

      if (this.gerandoPreVenda) {
        return "Gerando pré-venda...";
      }

      if (this.preVendaGerada) {
        return "Pré-venda gerada";
      }

      if (!this.rascunhoCobrancaAtual?.id) {
        return "(F8) Gerar pré-venda";
      }

      return "(F8) Gerar pré-venda";
    },
    gerarNfceLabel() {
      if (this.criandoRascunho) {
        return "Preparando cobrança...";
      }

      if (this.gerandoNfce) {
        return "Emitindo NFC-e...";
      }

      if (this.fechamentoNfcePendente) {
        return "(F5) Retomar NFC-e";
      }

      return "(F5) Emitir NFC-e";
    },
  },
  mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }

    this.definirAtendentePadrao();
    this.getAtendentes();
    this.getCheckout().then(async (carregado) => {
      if (carregado) await this.recuperarFechamentoDiretoAoAbrir();
    });
    this.checkoutVisibilityHandler = () => {
      if (!document.hidden) this.atualizarCheckoutSeOcioso();
    };
    this.checkoutFocusHandler = () => this.atualizarCheckoutSeOcioso();
    document.addEventListener("visibilitychange", this.checkoutVisibilityHandler);
    window.addEventListener("focus", this.checkoutFocusHandler);
    this.checkoutAutoRefreshTimer = window.setInterval(
      () => this.atualizarCheckoutSeOcioso(), 30000
    );
    this.getFormasPagamento();
    this.getConfiguracaoCaixa();
    this.getConfiguracaoFiscalNfce();
    window.addEventListener("keydown", this.handleCheckoutKeydown, {
      capture: true,
    });
    this.fullscreenHandler = () => {
      this.isFullscreen =
        document.fullscreenElement === document.documentElement;
    };
    document.addEventListener("fullscreenchange", this.fullscreenHandler);
    this.fullscreenHandler();
  },
  beforeDestroy() {
    if (this.timerBuscaClientePagamento) clearTimeout(this.timerBuscaClientePagamento);
    if (this.conferenciaBlobUrl) URL.revokeObjectURL(this.conferenciaBlobUrl);
    if (this.checkoutAutoRefreshTimer) {
      window.clearInterval(this.checkoutAutoRefreshTimer);
    }
    if (this.checkoutVisibilityHandler) {
      document.removeEventListener("visibilitychange", this.checkoutVisibilityHandler);
    }
    if (this.checkoutFocusHandler) {
      window.removeEventListener("focus", this.checkoutFocusHandler);
    }
    window.removeEventListener("keydown", this.handleCheckoutKeydown, {
      capture: true,
    });
    if (this.fullscreenHandler) {
      document.removeEventListener("fullscreenchange", this.fullscreenHandler);
    }
  },
  beforeRouteLeave(to, from, next) {
    if (
      this.abrindoFaturamento ||
      this.criandoRascunho ||
      this.gerandoPreVenda ||
      this.gerandoNfce ||
      this.confirmandoPagamento ||
      Boolean(this.faturamentoPendente?.documentoGerado)
    ) {
      this.$toast.warning(
        this.faturamentoPendente?.documentoGerado
          ? "Conclua o pagamento do documento gerado antes de sair do checkout."
          : "Cancele ou finalize a cobrança atual antes de sair do checkout."
      );
      return next(false);
    }

    next();
  },
  methods: {
    ...mapActions(["setLoading"]),
    async toggleFullscreen() {
      try {
        if (document.fullscreenElement === document.documentElement) {
          await document.exitFullscreen();
          return;
        }

        if (!document.documentElement?.requestFullscreen) {
          this.$toast.warning(
            "A tela cheia não está disponível neste navegador."
          );
          return;
        }

        await document.documentElement.requestFullscreen();
      } catch (error) {
        this.$toast.warning("Não foi possível abrir o checkout em tela cheia.");
      }
    },
    definirAtendentePadrao() {
      if (this.atendenteId) return;
      this.atendenteId = this.usuarioAutenticado?.id_usuario || null;
      this.atendenteNome = this.usuarioAutenticado?.nome || "";
    },
    async getAtendentes() {
      try {
        const { data } = await this.$http.get("/restaurante/checkout/atendentes");
        const usuarios = Array.isArray(data) ? data : data?.data || [];
        this.atendentes = usuarios.map((usuario) => ({
          id_usuario: Number(usuario.id_usuario),
          nome: usuario.nome,
          label: usuario.id_usuario + " - " + usuario.nome,
        }));
      } catch (error) {
        this.atendentes = [];
        this.$toast.warning("Não foi possível carregar os atendentes.");
      }
    },
    selecionarAtendente(atendente) {
      if (!this.podeEditarAjustesCobranca || !atendente) return;
      this.atendenteId = Number(atendente.id_usuario);
      this.atendenteNome = atendente.nome;
    },
    aplicarAtendenteDoFechamento(fechamento) {
      if (!fechamento?.atendente_id) return;
      this.atendenteId = Number(fechamento.atendente_id);
      this.atendenteNome = fechamento.atendente_nome || this.atendenteNome;
    },
    aplicarAjustesGeraisDoFechamento(fechamento) {
      const totais = fechamento?.totais_cobranca;
      this.taxaServicoPercentual = this.pickNumber(totais, ["taxa_servico_percentual"]) ?? 0;
      this.couvertValor = this.pickNumber(totais, ["couvert_valor", "couvert_total"]) ?? 0;
      this.acrescimoTipo = Number(totais?.acrescimo_tipo || 1);
      this.acrescimoValor = Number(totais?.acrescimo_valor || 0);
      this.descontoTipo = Number(totais?.desconto_tipo || 1);
      this.descontoValor = Number(totais?.desconto_valor || 0);
    },
    async atualizarCheckoutSeOcioso() {
      if (
        document.hidden || !this.checkout || this.checkoutAutoRefreshEmAndamento ||
        Date.now() - this.checkoutUltimaBusca < 30000 ||
        this.dividindoCobranca || this.modoDivisao || this.rascunho ||
        this.faturamentoPendente || this.pagamentoFechamento ||
        this.preVendaGerada || this.nfeGerada || this.cobrancaParcialConcluida ||
        this.criandoRascunho || this.gerandoPreVenda || this.gerandoNfce ||
        this.confirmandoPagamento || this.cancelandoRascunho ||
        this.vinculandoAvulsa || this.possuiAjusteLocal ||
        Object.values(this.ajusteTexto).some((valor) => valor !== null) ||
        document.querySelector(".modal.show")
      ) return false;

      const itensAntes = JSON.stringify(this.cobrancaItens);
      const itensPadrao = JSON.stringify(
        this.itensPendentes.map((item) => this.buildLinhaCobranca(item))
      );
      if (itensAntes !== itensPadrao) return false;

      this.checkoutAutoRefreshEmAndamento = true;
      try {
        const atualizado = await this.getCheckout({ silencioso: true });
        if (atualizado && itensAntes === JSON.stringify(this.cobrancaItens) &&
          !this.dividindoCobranca && !this.possuiAjusteLocal) {
          this.carregarItensDoPagador();
        }
        return atualizado;
      } finally {
        this.checkoutAutoRefreshEmAndamento = false;
      }
    },
    async getCheckout({ silencioso = false, notificarErro = true } = {}) {
      this.checkoutUltimaBusca = Date.now();
      try {
        if (!silencioso) this.setLoading(true);
        const primeiraBusca = !this.checkout;
        const pagadorAtual = {
          escopo: this.form.escopo,
          comanda_id: this.form.comanda_id,
        };
        const payload = this.codigoComandaRoute
          ? { codigo_comanda: this.codigoComandaRoute }
          : { atendimento_id: this.atendimentoId };
        const { data } = await this.$http.post(
          "/restaurante/checkout/find",
          payload
        );
        this.checkout = data;
        this.cobrancaParcialConcluida = false;
        if (
          this.rascunho?.id &&
          (!this.dividindoCobranca || this.rascunhoFinanceiroIncompleto) &&
          !this.preVendaGerada
        ) {
          const atualizado = (data.fechamentos || []).find(
            (fechamento) => Number(fechamento.id) === Number(this.rascunho.id)
          );
          this.rascunho = atualizado &&
            (this.dividindoCobranca
              ? atualizado.rascunho_financeiro_incompleto
              : Array.isArray(data.fechamentos_diretos_recuperaveis)
                ? data.fechamentos_diretos_recuperaveis.some(
                    (fechamento) => Number(fechamento.id) === Number(atualizado.id))
                : this.canRetomarFechamento(atualizado))
            ? atualizado
            : null;
        }

        const comandaIdRoute = this.checkoutComandaIdRoute;

        if (comandaIdRoute) {
          this.form.escopo = "comanda";
          this.form.comanda_id = Number(comandaIdRoute);
        } else if (data.checkout_comanda_id) {
          this.form.escopo = "comanda";
          this.form.comanda_id = data.checkout_comanda_id;
        } else if (this.pagadorComandaAindaExiste(pagadorAtual, data)) {
          this.form.escopo = "comanda";
          this.form.comanda_id = pagadorAtual.comanda_id;
        } else {
          this.form.escopo = "mesa";
          this.form.comanda_id = null;
        }

        if (primeiraBusca && !comandaIdRoute) {
          const notasPendentes = (data.fechamentos || []).filter((fechamento) =>
            fechamento.nfe_id && !fechamento.pre_venda_id &&
            !fechamento.pagamentos?.length &&
            ["preview", "erro", "aguardando_conversao"].includes(fechamento.status)
          );
          if (notasPendentes.length === 1) {
            this.form.escopo = notasPendentes[0].escopo === "comanda" ? "comanda" : "mesa";
            this.form.comanda_id = notasPendentes[0].comanda_id || null;
          }
        }

        this.syncPagadorFromForm();
        if (this.fechamentoNfcePendente) {
          this.aplicarAtendenteDoFechamento(this.fechamentoNfcePendente);
        }

        if (
          this.modoDivisao === "valor" &&
          (!this.rascunho?.id || this.rascunhoFinanceiroIncompleto)
        ) {
          this.carregarItensDoPagador();
          await this.carregarTotalFinanceiro();
        } else if (
          !this.cobrancaItens.length &&
          !this.dividindoCobranca &&
          !this.rascunho?.id
        ) {
          this.carregarItensDoPagador();
        }

        this.restaurarDocumentoCobranca();
        return true;
      } catch (error) {
        if (notificarErro) this.handleError(error, "Não foi possível carregar o checkout.");
        return false;
      } finally {
        if (!silencioso) this.setLoading(false);
      }
    },
    pagadorComandaAindaExiste(pagador, checkout) {
      if (pagador.escopo !== "comanda" || !pagador.comanda_id) {
        return false;
      }

      return (checkout?.comandas || []).some(
        (comanda) => Number(comanda.id) === Number(pagador.comanda_id)
      );
    },
    async getFormasPagamento() {
      try {
        const { data } = await this.$http.post("/financeiro/formas-pagamento");
        this.formasPagamento = data || [];

        if (!this.pagamentoForm.forma_pagamento_id) this.ensureFormaPagamentoPadrao();
      } catch (error) {
        this.handleError(
          error,
          "Não foi possível carregar formas de pagamento."
        );
      }
    },
    async getConfiguracaoCaixa() {
      try {
        const { data } = await this.$http.post("/configuracoesgerais/find");
        const configuracao = Array.isArray(data) ? data[0] : data;
        this.aberturaCaixaObrigatoria = Boolean(
          Number(configuracao?.abertura_caixa || 0)
        );
        this.restauranteImpressaoAtiva = Boolean(
          Number(configuracao?.restaurante_impressao_qz_tray || 0)
        );
        this.restauranteImpressoraCheckout =
          configuracao?.restaurante_impressora_checkout || null;
        this.impressoraPdv = configuracao?.impressora_pdv || null;
        this.restauranteImpressoraComprovante =
          configuracao?.restaurante_impressora_comprovante_cliente ||
          configuracao?.restaurante_impressora_checkout ||
          null;
      } catch (error) {
        this.aberturaCaixaObrigatoria = false;
        this.restauranteImpressaoAtiva = false;
        this.restauranteImpressoraCheckout = null;
        this.impressoraPdv = null;
        this.restauranteImpressoraComprovante = null;
      }
    },
    async getConfiguracaoFiscalNfce() {
      try {
        const { data } = await this.$http.post(
          "/configuracoes/current-config",
          {
            modelo: 65,
          }
        );
        const configuracao = Array.isArray(data) ? data[0] : data;

        this.serieNfce = configuracao?.serie || this.serieNfce;
        this.informacoesComplementaresNfce =
          configuracao?.informacoesComplementares || "";
      } catch (error) {
        this.serieNfce = this.serieNfce || 99;
        this.informacoesComplementaresNfce = "";
      }
    },
    handleCheckoutKeydown(event) {
      const key = String(event.key || "").toLowerCase();

      if (event.repeat) {
        return;
      }

      if (this.isModalVisible("novo-cliente-checkout")) {
        return;
      }
      if (this.isModalVisible("modal-parcelamento-restaurante")) {
        return;
      }

      if (this.isModalVisible("modal-pagamento-restaurante")) {
        const atalhoPagamento = this.atalhosPagamento.find(
          (atalho) => atalho.tecla.toLowerCase() === key
        );

        if (atalhoPagamento) {
          event.preventDefault();
          event.stopPropagation();
          this.adicionarPagamentoPorAtalho(atalhoPagamento);
          return;
        }

        if (key === this.atalhoFinalizacaoPagamento) {
          event.preventDefault();
          event.stopPropagation();
          if (!this.buscandoClientePagamento && !this.confirmandoPagamento &&
              !this.carregandoPreviaParcelas && this.canConfirmarPagamento) {
            this.confirmarPagamento();
          }
        }

        return;
      }

      if (this.isModalVisible("modal-dividir-cobranca")) {
        if (key === "f8" || key === "f5") {
          event.preventDefault();
          event.stopPropagation();
          if (!this.divisaoModalSelecionandoModo) {
            this.faturarDivisaoNoModal(key === "f8" ? "pre_venda" : "nfce");
          }
        }
        return;
      }

      if (
        this.isModalVisible("modal-abertura-caixa-restaurante") ||
        this.isModalVisible("modal-ajuste-item-restaurante") ||
        this.isModalVisible("modal-vincular-avulsa-checkout")
      ) {
        return;
      }

      if (key === "f8") {
        event.preventDefault();
        event.stopPropagation();
        this.finalizarCobranca("pre_venda");
        return;
      }

      if (key === "f5" && this.featureFlags.nfceCheckout) {
        event.preventDefault();
        event.stopPropagation();
        this.finalizarCobranca("nfce");
      }
    },
    isModalVisible(id) {
      const modal = document.getElementById(id);
      return (
        !!modal &&
        modal.classList.contains("show") &&
        modal.getAttribute("aria-hidden") !== "true"
      );
    },
    async ensureCaixaAbertoParaPdv(tipo = "pre_venda") {
      if (this.aberturaCaixaObrigatoria === null) {
        await this.getConfiguracaoCaixa();
      }

      if (!this.aberturaCaixaObrigatoria) {
        return true;
      }

      try {
        const { data } = await this.$http.post("/caixa/get-caixa");

        if (data?.caixa_aberto) {
          return true;
        }

        this.gerarPreVendaPendenteAbertura = tipo === "pre_venda";
        this.tipoCobrancaPendenteAbertura = tipo;
        this.$bvModal.show("modal-abertura-caixa-restaurante");
        return false;
      } catch (error) {
        this.handleError(error, "Não foi possível verificar o caixa.");
        return false;
      }
    },
    focusValorAberturaCaixa() {
      this.$nextTick(() => {
        const inputEl =
          this.$refs.caixaValorAberturaRestaurante?.$el ||
          this.$refs.caixaValorAberturaRestaurante;

        if (inputEl) {
          inputEl.focus();
          inputEl.select();
        }
      });
    },
    cancelarAberturaCaixa(cancel) {
      this.gerarPreVendaPendenteAbertura = false;
      this.tipoCobrancaPendenteAbertura = null;
      cancel();
    },
    async abrirCaixaCheckout() {
      try {
        this.abrindoCaixa = true;
        this.setLoading(true);
        const { data } = await this.$http.post("/caixa/create", {
          id_tipo: 1,
          impressao_comprovante: this.caixaImpressaoComprovante,
          valor_abertura: Number(this.caixaValorAbertura || 0),
        });

        if (this.caixaImpressaoComprovante && data?.["comprovante-url"]) {
          await this.imprimirDocumentoPdv(data["comprovante-url"]);
        }

        this.$toast.success("Caixa aberto com sucesso.");
        const tipoPendente =
          this.tipoCobrancaPendenteAbertura ||
          (this.gerarPreVendaPendenteAbertura ? "pre_venda" : null);
        this.gerarPreVendaPendenteAbertura = false;
        this.tipoCobrancaPendenteAbertura = null;
        this.$bvModal.hide("modal-abertura-caixa-restaurante");

        if (tipoPendente) {
          await this.finalizarCobranca(tipoPendente);
        }
      } catch (error) {
        this.handleError(error, "Não foi possível abrir o caixa.");
      } finally {
        this.abrindoCaixa = false;
        this.setLoading(false);
      }
    },
    syncPagadorFromForm() {
      this.form.pagador =
        this.form.escopo === "mesa"
          ? "mesa"
          : `comanda:${this.form.comanda_id}`;
    },
    openVincularAvulsaModal() {
      this.avulsaMesaForm.codigo = "";
      this.avulsaMesaErro = "";
      this.$bvModal.show("modal-vincular-avulsa-checkout");
    },
    focusCodigoAvulsaMesa() {
      this.$nextTick(() => {
        if (this.$refs.codigoAvulsaMesa) {
          this.$refs.codigoAvulsaMesa.focus();
        }
      });
    },
    async vincularAvulsaMesa() {
      if (this.vinculandoAvulsa) return;
      const codigo = String(this.avulsaMesaForm.codigo || "").trim();

      if (!codigo) {
        this.avulsaMesaErro = "Informe o código da comanda avulsa.";
        this.$toast.warning("Informe o código da comanda avulsa.");
        return;
      }

      if (!this.checkout?.mesa?.id) {
        this.$toast.warning("Checkout sem mesa para vincular comanda.");
        return;
      }

      try {
        this.avulsaMesaErro = "";
        this.vinculandoAvulsa = true;
        const { data } = await this.$http.post(
          "/restaurante/comandas/avulsas/find-or-create",
          { codigo }
        );
        await this.$http.post("/restaurante/comandas/avulsas/vincular-mesa", {
          comanda_id: data?.comanda?.id,
          mesa_id: this.checkout.mesa.id,
        });
        this.$toast.success("Comanda avulsa adicionada à mesa.");
        this.$bvModal.hide("modal-vincular-avulsa-checkout");
        await this.getCheckout();
        this.selectMesaInteira();
      } catch (error) {
        this.avulsaMesaErro = "Não foi possível adicionar a comanda. Confira o código e tente novamente.";
        this.handleError(error, "Não foi possível adicionar a comanda avulsa.");
      } finally {
        this.vinculandoAvulsa = false;
      }
    },
    async selectMesaInteira() {
      if (this.modoApenasComandasCheckout) {
        return;
      }
      if (this.dividindoCobranca && !(await this.cancelarDivisaoCobranca())) {
        return;
      }

      const mudouPagador = this.form.escopo !== "mesa";
      if (mudouPagador) this.limparAjustesGerais();
      this.form.escopo = "mesa";
      this.form.comanda_id = null;
      this.form.pagador = "mesa";
      this.conferenciaUrl = null;
      this.carregarSaldoMesa();
      if (mudouPagador) this.restaurarDocumentoCobranca(true);
    },
    async selectComanda(comandaId) {
      if (this.dividindoCobranca && !(await this.cancelarDivisaoCobranca())) {
        return;
      }
      const mudouPagador = this.form.escopo !== "comanda" ||
        Number(this.form.comanda_id) !== Number(comandaId);
      if (mudouPagador) {
        this.limparAjustesGerais();
      }
      this.form.escopo = "comanda";
      this.form.comanda_id = comandaId;
      this.form.pagador = `comanda:${comandaId}`;
      this.conferenciaUrl = null;
      this.carregarItensDaComanda(comandaId);
      if (mudouPagador) this.restaurarDocumentoCobranca(true);
    },
    limparAjustesGerais() {
      this.acrescimoTipo = 1;
      this.acrescimoValor = 0;
      this.descontoTipo = 1;
      this.descontoValor = 0;
    },
    restaurarDocumentoCobranca(limparSemRascunho = false) {
      const rascunho = this.rascunhoCobrancaAtual || this.fechamentoNfcePendente;
      if (!rascunho?.id && !limparSemRascunho) return;
      const documento = rascunho?.documento_cobranca ||
        rascunho?.payload_preview?.documento_cobranca || "";
      this.documentoCobranca = documento;
      this.clienteDocumentoLocalizado = Boolean(
        rascunho?.buyer?.id_pessoa ||
          rascunho?.payload_preview?.buyer?.id_pessoa
      );
      this.clienteDocumentoNome = rascunho?.buyer?.nome ||
        rascunho?.payload_preview?.buyer?.nome || "";
      this.clientePessoaId = rascunho?.buyer?.id_pessoa ||
        rascunho?.payload_preview?.buyer?.id_pessoa || null;
      this.clienteDocumentoNaoCadastrado = false;
      this.clienteDocumentoInativo = false;
      this.documentoConsultado = rascunho?.id
        ? this.normalizeCpfCnpj(documento)
        : null;
    },
    onDocumentoCobrancaInput(value) {
      this.documentoCobranca = this.formatCnpjAlfanumerico(value);
      this.clienteDocumentoLocalizado = false;
      this.clienteDocumentoNome = "";
      this.clienteDocumentoNaoCadastrado = false;
      this.clienteDocumentoInativo = false;
      this.clientePessoaId = null;
      this.documentoConsultado = null;
      this.cobrancaIdempotencyToken = null;
    },
    async buscarPessoaDocumentoCobranca() {
      const documento = this.normalizeCpfCnpj(this.documentoCobranca);
      if (
        !documento ||
        !this.documentoCobrancaValido ||
        this.documentoConsultado === documento
      ) {
        return;
      }

      try {
        const { data } = await this.$http.post(
          "/restaurante/checkout/pessoa-documento",
          {
            documento,
          }
        );
        if (this.normalizeCpfCnpj(this.documentoCobranca) === documento) {
          this.documentoConsultado = documento;
          this.clienteDocumentoLocalizado = Boolean(data?.id_pessoa);
          this.clienteDocumentoNome = data?.nome || "";
          this.clientePessoaId = data?.id_pessoa || null;
          this.clienteDocumentoInativo = Boolean(data?.cadastro_bloqueado);
          this.clienteDocumentoNaoCadastrado = !data?.id_pessoa && !this.clienteDocumentoInativo;
        }
      } catch (error) {
        this.handleError(error, "Não foi possível consultar o CPF/CNPJ.");
      }
    },
    validarDocumentoCobranca() {
      if (this.documentoCobrancaValido) {
        return true;
      }
      this.$toast.warning("Informe um CPF ou CNPJ válido.");
      return false;
    },
    selecionarClienteCriado(cliente) {
      if (this.clienteCadastroPagamentoPendente) {
        if (!cliente?.id_pessoa) return;
        this.clienteFinanceiroId = cliente.id_pessoa;
        this.clienteFinanceiroNome = cliente.nome || cliente.nome_fantasia || "";
        this.clienteFinanceiroDocumento = this.formatCnpjAlfanumerico(cliente.cnpj_cpf);
        this.erroParcelamento = "";
        this.clienteCadastroPagamentoPendente = false;
        this.buscaClientePagamento = "";
        this.resultadosClientePagamento = [];
        return;
      }
      if (!cliente?.id_pessoa ||
        (this.documentoCobranca &&
          this.normalizeCpfCnpj(cliente.cnpj_cpf) !== this.normalizeCpfCnpj(this.documentoCobranca))) {
        this.$toast.warning("O documento do cliente cadastrado difere do informado no Checkout.");
        return;
      }
      this.documentoCobranca = this.formatCnpjAlfanumerico(cliente.cnpj_cpf);
      this.clientePessoaId = cliente.id_pessoa;
      this.clienteDocumentoNome = cliente.nome || cliente.nome_fantasia || "";
      this.clienteDocumentoLocalizado = true;
      this.clienteDocumentoNaoCadastrado = false;
      this.clienteDocumentoInativo = false;
      this.documentoConsultado = this.normalizeCpfCnpj(cliente.cnpj_cpf);
      if (this.clienteCadastroPagamentoPendente) this.clienteSelecionadoNoPagamento = true;
      this.clienteCadastroPagamentoPendente = false;
      this.buscaClientePagamento = "";
      this.resultadosClientePagamento = [];
      this.erroBuscaClientePagamento = "";
    },
    parseValorPagamento(valor) {
      if (typeof valor === "number") return valor;
      const texto = String(valor ?? "").trim().replace(/^R\$\s*/, "").replace(/\s/g, "");
      if (!texto || !/^-?[\d.,]+$/.test(texto)) return NaN;
      if (texto.includes(",")) {
        if (!/^-?(?:\d{1,3}(?:\.\d{3})+|\d+)(?:,\d{1,2})?$/.test(texto)) return NaN;
        return Number(texto.replace(/\./g, "").replace(",", "."));
      }
      if (!/^-?\d+(?:\.\d{1,2})?$/.test(texto)) return NaN;
      return Number(texto);
    },
    formatarValorPagamento() {
      const valor = this.parseValorPagamento(this.pagamentoForm.valor);
      if (Number.isFinite(valor)) this.pagamentoForm.valor = this.formatMoney(valor);
    },
    pagamentoPrazo(pagamento) {
      const forma = this.formasPagamento.find((item) => Number(item.id) === Number(pagamento.forma_pagamento_id));
      return this.destinoFinanceiroForma(forma) === "receber";
    },
    destinoFinanceiroForma(forma) {
      if (!forma) return "invalido";
      if ([true, 1, "1", "t", "true"].includes(forma.movimenta_caixa)) return "caixa";
      if ([false, 0, "0", "f", "false"].includes(forma.movimenta_caixa)) return "receber";
      return "invalido";
    },
    formatarDataParcela(data) {
      const partes = String(data || "").slice(0, 10).split("-");
      return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : String(data || "");
    },
    async buscarClientePagamento() {
      const busca = String(this.buscaClientePagamento || "").trim();
      const documento = this.normalizeCpfCnpj(busca);
      const pareceDocumento = /\d/.test(busca);
      const porDocumento = pareceDocumento && this.validaCpfCnpj(busca);
      if ((pareceDocumento && !porDocumento) || (!pareceDocumento && busca.length < 3)) return;
      const termo = porDocumento ? documento : busca.toLocaleLowerCase("pt-BR");
      if (this.ultimoTermoBuscaClientePagamento === termo) return;
      this.ultimoTermoBuscaClientePagamento = termo;
      if (porDocumento && documento !== this.normalizeCpfCnpj(this.documentoCobranca)) {
        this.onDocumentoCobrancaInput(busca);
        this.clienteSelecionadoNoPagamento = true;
      }
      const sequencia = ++this.sequenciaBuscaClientePagamento;
      this.buscandoClientePagamento = true;
      this.resultadosClientePagamento = [];
      this.erroBuscaClientePagamento = "";
      try {
        const { data } = porDocumento
          ? await this.$http.post("/restaurante/checkout/pessoa-documento", { documento })
          : await this.$http.post("/pessoa/nome", { nome: busca });
        if (sequencia !== this.sequenciaBuscaClientePagamento) return;
        if (data?.cadastro_bloqueado) {
          this.clienteDocumentoInativo = true;
          this.erroBuscaClientePagamento = "Cliente inativo. Revise o cadastro antes de continuar.";
          return;
        }
        const pessoas = porDocumento ? (data?.id_pessoa ? [{ ...data, cnpj_cpf: documento }] : [])
          : (Array.isArray(data) ? data : []);
        this.resultadosClientePagamento = pessoas.filter((pessoa) => pessoa.id_pessoa &&
          pessoa.status !== "I" && !pessoa.cadastro_bloqueado);
        if (this.resultadosClientePagamento.length === 1) {
          this.selecionarClientePagamento(this.resultadosClientePagamento[0]);
          this.resultadosClientePagamento = [];
        } else if (!this.resultadosClientePagamento.length) {
          this.clienteDocumentoNaoCadastrado = porDocumento;
          if (!porDocumento) this.erroBuscaClientePagamento = "Cliente não encontrado nesta empresa.";
        }
      } catch (error) {
        if (sequencia === this.sequenciaBuscaClientePagamento) {
          this.ultimoTermoBuscaClientePagamento = "";
          this.erroBuscaClientePagamento = error?.response?.data?.msg ||
            "Não foi possível consultar o cliente.";
        }
      } finally {
        if (sequencia === this.sequenciaBuscaClientePagamento) this.buscandoClientePagamento = false;
      }
    },
    invalidarBuscaClientePagamento() {
      if (this.timerBuscaClientePagamento) clearTimeout(this.timerBuscaClientePagamento);
      this.timerBuscaClientePagamento = null;
      this.sequenciaBuscaClientePagamento++;
      this.buscandoClientePagamento = false;
      this.resultadosClientePagamento = [];
      this.erroBuscaClientePagamento = "";
      this.clienteDocumentoNaoCadastrado = false;
      this.clienteDocumentoInativo = false;
      this.ultimoTermoBuscaClientePagamento = "";
    },
    onBuscaClientePagamentoInput(value) {
      this.invalidarBuscaClientePagamento();
      const texto = String(value || "").trim();
      const pareceDocumento = /\d/.test(texto);
      this.buscaClientePagamento = pareceDocumento
        ? this.formatCnpjAlfanumerico(texto) : String(value || "");
      if (pareceDocumento) {
        if (!this.validaCpfCnpj(this.buscaClientePagamento)) return;
      } else if (texto.length < 3) {
        return;
      }
      this.timerBuscaClientePagamento = setTimeout(() => {
        this.timerBuscaClientePagamento = null;
        this.buscarClientePagamento();
      }, 350);
    },
    buscarClientePagamentoAoSair() {
      if (this.timerBuscaClientePagamento) {
        clearTimeout(this.timerBuscaClientePagamento);
        this.timerBuscaClientePagamento = null;
        this.buscarClientePagamento();
      }
    },
    selecionarClientePagamento(cliente) {
      const documento = this.normalizeCpfCnpj(cliente?.cnpj_cpf);
      if (!cliente?.id_pessoa || !documento || !this.validaCpfCnpj(documento)) {
        this.$toast.warning("Cliente sem CPF/CNPJ válido para o faturamento.");
        return false;
      }
      this.clienteFinanceiroId = cliente.id_pessoa;
      this.clienteFinanceiroNome = cliente.nome || cliente.nome_fantasia || "";
      this.clienteFinanceiroDocumento = this.formatCnpjAlfanumerico(cliente.cnpj_cpf);
      this.clienteSelecionadoNoPagamento = true;
      this.erroParcelamento = "";
      this.erroBuscaClientePagamento = "";
      this.buscaClientePagamento = "";
      this.resultadosClientePagamento = [];
      return true;
    },
    removerClienteParcelamento() {
      this.clienteFinanceiroId = null;
      this.clienteFinanceiroNome = "";
      this.clienteFinanceiroDocumento = "";
      this.erroParcelamento = "";
      this.invalidarBuscaClientePagamento();
      this.buscaClientePagamento = "";
    },
    abrirCadastroClientePagamento() {
      if (this.clienteDocumentoInativo) {
        this.$toast.warning("Cliente inativo. Revise o cadastro antes de continuar.");
        return;
      }
      this.clienteCadastroPagamentoPendente = true;
      this.$bvModal.show("novo-cliente-checkout");
    },
    carregarItensDoPagador() {
      if (this.form.escopo === "mesa") {
        this.carregarSaldoMesa();
        return;
      }

      this.carregarItensDaComanda(this.form.comanda_id);
    },
    carregarSaldoMesa() {
      this.cobrancaItens = this.itensPendentes.map((item) =>
        this.buildLinhaCobranca(item)
      );

      this.resetEstadoCobranca();
    },
    carregarItensDaComanda(comandaId) {
      if (!comandaId) {
        this.cobrancaItens = [];
        this.resetEstadoCobranca();
        return;
      }

      this.cobrancaItens = this.itensPendentes
        .filter((item) => Number(item.comanda_id || 0) === Number(comandaId))
        .map((item) => this.buildLinhaCobranca(item));

      this.resetEstadoCobranca();
    },
    buildLinhaCobranca(item) {
      return {
        atendimento_item_id: item.id,
        quantidade: Number(item.quantidade_pendente || 0),
        ajuste_operacao: null,
        ajuste_tipo: 1,
        ajuste_valor: 0,
      };
    },
    resetEstadoCobranca() {
      if (!this.dividindoCobranca) {
        this.rascunho = null;
      }
      this.preVendaGerada = null;
      this.nfeGerada = null;
      this.cobrancaIdempotencyToken = null;
    },
    addItemCobranca(item) {
      if (!this.canAddItem(item)) {
        return;
      }

      const existing = this.cobrancaItens.find(
        (linha) => linha.atendimento_item_id === item.id
      );

      if (existing) {
        this.$toast.warning("Item já está na cobrança.");
        return;
      }

      this.cobrancaItens.push(this.buildLinhaCobranca(item));
      this.resetEstadoCobranca();
    },
    adicionarSaldoTotal() {
      this.selectMesaInteira();
    },
    removeItemCobranca(itemId) {
      this.cobrancaItens = this.cobrancaItens.filter(
        (item) => item.atendimento_item_id !== itemId
      );
      this.resetEstadoCobranca();
    },
    resetCobranca() {
      this.resetEstadoCobranca();
      this.carregarItensDoPagador();
    },
    canAddItem(item) {
      return (
        this.modoDivisao === "itens" &&
        this.podeEditarDivisao &&
        item.status !== "cancelado" &&
        item.cobravel !== false &&
        Number(item.quantidade_pendente || 0) > 0 &&
        Number(item.valor_pendente || 0) > 0 &&
        !this.cobrancaItens.some(
          (linha) => linha.atendimento_item_id === item.id
        )
      );
    },
    itemActionLabel(item) {
      if (item.cobravel === false) {
        return "Sem valor";
      }

      const status = this.statusCobrancaItemLabel(item);

      if (status === "Cobrado") {
        return "Pago";
      }

      if (
        this.cobrancaItens.some(
          (linha) => linha.atendimento_item_id === item.id
        )
      ) {
        return "Adicionado";
      }

      if (status === "Reservado") {
        return "Reservado";
      }

      return "Adicionar";
    },
    requestPayload() {
      // O backend recalcula todos os totais (subtotal, taxa, couvert, acrescimo,
      // total) a partir dos itens e da configuracao. O front envia apenas os
      // insumos; nunca confia/envia valor_total/price/extra_value do cliente.
      return {
        atendimento_id: this.checkout?.atendimento?.id || this.atendimentoId,
        tipo: "total",
        atendente_id: this.atendenteId,
        escopo: this.form.escopo,
        comanda_id:
          this.form.escopo === "comanda" ? this.form.comanda_id : null,
        itens: this.cobrancaItens.map((item) => ({
          atendimento_item_id: item.atendimento_item_id,
          quantidade: item.quantidade,
          ajuste_operacao: item.ajuste_operacao,
          ajuste_tipo: item.ajuste_tipo,
          ajuste_valor: item.ajuste_valor,
          ajuste_acrescimo_tipo: item.ajuste_acrescimo_tipo,
          ajuste_acrescimo_valor: item.ajuste_acrescimo_valor,
          ajuste_desconto_tipo: item.ajuste_desconto_tipo,
          ajuste_desconto_valor: item.ajuste_desconto_valor,
        })),
        taxa_servico_percentual: Number(this.taxaServicoPercentual || 0),
        couvert_valor: Number(this.couvertValor || 0),
        acrescimo_tipo: this.acrescimoTipo,
        acrescimo_valor: Number(this.acrescimoValor || 0),
        desconto_tipo: this.descontoTipo,
        desconto_valor: Number(this.descontoValor || 0),
        documento_cobranca: this.normalizeCpfCnpj(this.documentoCobranca),
        id_pessoa: this.clientePessoaId,
        ...(this.modoDivisao === "valor" && {
          distribuicao_financeira: this.distribuicaoFinanceira.map((cobranca) =>
            this.roundCurrency(Number(cobranca.valor))
          ),
        }),
      };
    },
    deveAplicarAjustesAoFechamento(fechamento) {
      return (
        this.cobrancaItensDetalhados.length > 0 ||
        (this.rascunhoCobrancaAtual?.id &&
          Number(fechamento?.id || 0) ===
            Number(this.rascunhoCobrancaAtual.id || 0))
      );
    },
    normalizarFechamentoComAjustes(fechamento) {
      if (
        this.dividindoCobranca ||
        !fechamento ||
        Array.isArray(fechamento.distribuicao_financeira) ||
        !this.deveAplicarAjustesAoFechamento(fechamento)
      ) {
        return fechamento;
      }

      const totais = {
        ...(fechamento.totais_cobranca || {}),
        ...this.totaisCobrancaCalculados,
      };
      const valorTotal = this.roundCurrency(
        totais.valor_total || this.totalCobrancaExibicao
      );

      return {
        ...fechamento,
        valor_total: valorTotal,
        extra_value: this.roundCurrency(
          this.valorTaxaServico + this.valorCouvertTotal + this.valorAcrescimoCobranca
        ),
        totais_cobranca: {
          ...totais,
          valor_total: valorTotal,
          total: valorTotal,
          total_cobranca: valorTotal,
        },
      };
    },
    valorFechamentoExibicao(fechamento) {
      if (Array.isArray(fechamento?.distribuicao_financeira)) {
        return Number(fechamento.valor_total || 0);
      }

      if (
        this.rascunhoCobrancaAtual?.id &&
        Number(fechamento?.id || 0) ===
          Number(this.rascunhoCobrancaAtual.id || 0)
      ) {
        return this.totalCobrancaExibicao;
      }

      return (
        this.pickNumber(fechamento?.totais_cobranca, [
          "valor_total",
          "total",
          "total_cobranca",
        ]) ?? Number(fechamento?.valor_total || 0)
      );
    },
    async criarRascunho(valorTotalEsperado = null) {
      if (!this.canCriarRascunho) {
        this.$toast.warning(
          "Selecione itens e confira os valores da cobrança."
        );
        return null;
      }
      if (!this.validarDocumentoCobranca()) {
        return null;
      }
      try {
        this.criandoRascunho = true;
        this.setLoading(true);
        const { data } = await this.$http.post(
          "/restaurante/checkout/criar-rascunho",
          {
            ...this.requestPayload(),
            ...(this.dividindoCobranca && this.modoDivisao === "itens" && {
              modo_divisao: "itens",
            }),
            ...(valorTotalEsperado != null && {
              valor_total_esperado: valorTotalEsperado,
            }),
            ...(this.dividindoCobranca && this.rascunho?.id && {
              fechamento_id: this.rascunho.id,
            }),
            idempotency_key: this.idempotencyKey(),
          }
        );
        const atualizando = Boolean(this.rascunho?.id);
        this.rascunho = this.normalizarFechamentoComAjustes(data);
        this.edicaoPersistidaOriginal = JSON.stringify(this.requestPayload());
        if (!this.faturamentoPendente) {
          this.$toast.success(atualizando ? "Alterações salvas." : "Cobrança salva.");
        }
        if (!this.dividindoCobranca && !this.faturamentoPendente) {
          await this.getCheckout();
        }
        return data;
      } catch (error) {
        this.applyBackendData(error);
        this.handleError(error, "Não foi possível criar o rascunho.");
        return null;
      } finally {
        this.criandoRascunho = false;
        this.setLoading(false);
      }
    },
    propriedadeEntradaAjuste(campo) {
      if (campo === "taxaServico") return "taxaServicoPercentual";
      return `${campo}Valor`;
    },
    tipoEntradaAjuste(campo) {
      if (campo === "taxaServico") return 2;
      if (campo === "couvert") return 1;
      return this[`${campo}Tipo`];
    },
    formatarEntradaAjuste(campo) {
      return Number(this[this.propriedadeEntradaAjuste(campo)] || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: campo === "taxaServico" ? 0 : 2,
        maximumFractionDigits: this.tipoEntradaAjuste(campo) === 2 ? 6 : 2,
      });
    },
    parseEntradaAjuste(valor, tipo) {
      let texto = String(valor ?? "").trim().replace(/\s/g, "");
      if (!texto) return 0;
      if (texto.startsWith(",") || texto.startsWith(".")) texto = `0${texto}`;

      const agrupado = /^\d{1,3}(?:\.\d{3})+(?:,\d*)?$/.test(texto);
      const decimalBr = /^\d+(?:,\d*)?$/.test(texto);
      const decimalPonto = /^\d+(?:\.\d*)?$/.test(texto);
      if (!agrupado && !decimalBr && !decimalPonto) return null;

      const separador = texto.includes(",") ? "," : (agrupado ? null : ".");
      const casas = separador ? (texto.split(separador)[1] || "").length : 0;
      if (casas > (tipo === 2 ? 6 : 2)) return null;

      const normalizado = agrupado
        ? texto.replace(/\./g, "").replace(",", ".")
        : texto.replace(",", ".");
      const numero = Number(normalizado);
      return Number.isFinite(numero) && numero >= 0 &&
        numero <= 9999999999999.99 ? numero : null;
    },
    entradaAjusteInvalida(campo) {
      const texto = this.ajusteTexto[campo];
      return texto !== null &&
        this.parseEntradaAjuste(texto, this.tipoEntradaAjuste(campo)) === null;
    },
    focarValorAjuste(campo, event) {
      this.ajusteTexto[campo] = event.target.value;
      event.target.select();
    },
    digitarValorAjuste(campo, texto) {
      this.ajusteTexto[campo] = String(texto);
      const valor = this.parseEntradaAjuste(texto, this.tipoEntradaAjuste(campo));
      if (valor === null) return;
      this[this.propriedadeEntradaAjuste(campo)] = valor;
      this.atualizarAjustesCobranca();
    },
    finalizarEntradaAjuste(campo) {
      if (!this.entradaAjusteInvalida(campo)) {
        this.ajusteTexto[campo] = null;
      }
    },
    alterarTipoAjusteGeral(campo, tipo) {
      if (this.entradaAjusteInvalida(campo)) {
        this.$toast.warning("Corrija o valor antes de mudar a unidade.");
        return;
      }
      const tipoAtual = this[`${campo}Tipo`];
      if (tipoAtual === tipo || !this.podeEditarAjustesCobranca) return;
      const valorAtual = Number(this[`${campo}Valor`] || 0);
      const base = this.baseAjustesGerais;
      const efetivo = tipoAtual === 2
        ? this.roundCurrency(base * valorAtual / 100)
        : this.roundCurrency(valorAtual);
      if (tipo === 2 && base > 0 && efetivo > base) {
        this.$toast.warning("O valor excede 100% do subtotal dos produtos.");
        return;
      }
      this[`${campo}Tipo`] = tipo;
      this[`${campo}Valor`] = tipo === 1
        ? efetivo
        : (base > 0 ? Number((efetivo * 100 / base).toFixed(6)) : 0);
      this.atualizarAjustesCobranca();
    },
    async atualizarAjustesCobranca() {
      this.resetEstadoCobranca();
      if (this.modoDivisao === "valor") {
        this.totalFinanceiroBackend = null;
        return this.carregarTotalFinanceiro();
      }
      return true;
    },
    async carregarTotalFinanceiro() {
      if (this.modoDivisao !== "valor" || !this.cobrancaItens.length) {
        return false;
      }

      const sequencia = ++this.sequenciaTotalFinanceiro;
      this.calculandoTotalFinanceiro = true;
      this.totalFinanceiroBackend = null;
      const payload = this.requestPayload();
      delete payload.distribuicao_financeira;
      delete payload.itens;
      if (this.rascunho?.id) payload.fechamento_id = this.rascunho.id;

      try {
        const { data } = await this.$http.post(
          "/restaurante/checkout/preview",
          payload
        );
        if (sequencia !== this.sequenciaTotalFinanceiro) {
          return false;
        }

        const total = Number(data?.totais_cobranca?.valor_total);
        if (!Number.isFinite(total) || total <= 0) {
          throw new Error("Total da cobrança inválido no preview.");
        }
        this.totalFinanceiroBackend = total;
        return true;
      } catch (error) {
        if (sequencia === this.sequenciaTotalFinanceiro) {
          this.handleError(
            error,
            "Não foi possível atualizar o total da conta."
          );
        }
        return false;
      } finally {
        if (sequencia === this.sequenciaTotalFinanceiro) {
          this.calculandoTotalFinanceiro = false;
        }
      }
    },
    centavosFinanceiros(valor) {
      if (valor === "" || valor === null || valor === undefined) {
        return null;
      }

      const numero = Number(valor);
      const centavos = Math.round(numero * 100);
      return Number.isFinite(numero) &&
        Math.abs(numero * 100 - centavos) < 0.000001
        ? centavos
        : null;
    },
    abrirDivisaoCobranca() {
      if (this.dividindoCobranca) {
        if (!this.podeTrocarModoDivisao && !this.rascunhoFinanceiroIncompleto) return;
      } else {
        if (!this.podeDividirCobranca) return;
        this.iniciarDivisaoCobranca();
      }

      this.divisaoModalSelecionandoModo = !this.modoDivisao;
      this.$bvModal.show("modal-dividir-cobranca");
    },
    fecharDivisaoCobranca() {
      this.$bvModal.hide("modal-dividir-cobranca");
    },
    voltarDivisaoModal() {
      if (this.divisaoModalSelecionandoModo) {
        this.fecharDivisaoCobranca();
      } else {
        this.voltarSelecaoDivisao();
      }
    },
    async salvarCobrancaNoModal() {
      if (
        this.divisaoModalSelecionandoModo ||
        !this.canCriarRascunho
      ) {
        return;
      }
      if ((await this.criarRascunho())?.id) {
        this.encerrarDivisaoCobranca();
        await this.getCheckout();
        this.fecharDivisaoCobranca();
      }
    },
    faturarDivisaoNoModal(tipo) {
      if (
        this.divisaoModalSelecionandoModo ||
        this.faturamentoModalPendente ||
        !(tipo === "nfce" ? this.canFinalizarNfce : this.canFinalizarPreVenda)
      ) {
        return;
      }
      this.faturamentoModalPendente = tipo;
      this.fecharDivisaoCobranca();
    },
    voltarSelecaoDivisao() {
      this.divisaoModalSelecionandoModo = true;
    },
    async escolherModoDivisao(modo) {
      if (!["valor", "itens"].includes(modo) ||
        (!this.podeTrocarModoDivisao && this.modoDivisao !== modo)) {
        return;
      }
      if (this.modoDivisao === modo || (await this.selecionarModoDivisao(modo))) {
        this.divisaoModalSelecionandoModo = false;
      }
    },
    async onDivisaoModalHidden() {
      const tipo = this.faturamentoModalPendente;
      this.faturamentoModalPendente = null;
      this.divisaoModalSelecionandoModo = true;
      if (tipo) {
        await this.finalizarCobranca(tipo);
      } else if (this.dividindoCobranca) {
        this.encerrarDivisaoCobranca();
        this.rascunho = null;
        this.taxaServicoPercentual = 0;
        this.couvertValor = 0;
        this.limparAjustesGerais();
        await this.getCheckout();
      }
    },
    iniciarDivisaoCobranca() {
      if (!this.podeDividirCobranca) {
        return;
      }

      this.dividindoCobranca = true;
      this.rascunho = null;
      this.edicaoPersistidaOriginal = null;
      this.modoDivisao = null;
      this.distribuicaoFinanceira = [];
      this.cobrancaItens = [];
      this.cobrancaParcialConcluida = false;
      this.sequenciaTotalFinanceiro += 1;
      this.totalFinanceiroBackend = null;
      this.calculandoTotalFinanceiro = false;
      this.resetEstadoCobranca();
    },
    async selecionarModoDivisao(modo) {
      if (
        !this.podeTrocarModoDivisao ||
        !["valor", "itens"].includes(modo) ||
        this.modoDivisao === modo
      ) {
        return false;
      }

      if (!(await this.getCheckout()) || !this.podeTrocarModoDivisao) {
        return false;
      }

      this.modoDivisao = modo;
      this.distribuicaoFinanceira = [];
      this.cobrancaItens = [];
      this.sequenciaTotalFinanceiro += 1;
      this.totalFinanceiroBackend = null;
      this.calculandoTotalFinanceiro = false;
      this.resetEstadoCobranca();

      if (modo === "valor") {
        this.carregarItensDoPagador();
        this.adicionarCobrancaFinanceira();
        this.adicionarCobrancaFinanceira();
        await this.carregarTotalFinanceiro();
      }
      return true;
    },
    adicionarCobrancaFinanceira() {
      if (
        this.modoDivisao !== "valor" ||
        !this.podeEditarDistribuicaoFinanceira ||
        this.distribuicaoFinanceira.length >= 20
      ) {
        return;
      }

      this.distribuicaoFinanceira.push({
        id: this.proximaCobrancaFinanceiraId++,
        valor: 0,
      });
      this.resetEstadoCobranca();
    },
    excluirCobrancaFinanceira(id) {
      if (
        !this.podeEditarDistribuicaoFinanceira ||
        this.distribuicaoFinanceira.length <= 2
      ) {
        return;
      }

      this.distribuicaoFinanceira = this.distribuicaoFinanceira.filter(
        (cobranca) => cobranca.id !== id
      );
      this.resetEstadoCobranca();
    },
    limparDistribuicaoFinanceira() {
      if (!this.podeEditarDistribuicaoFinanceira) {
        return;
      }

      this.distribuicaoFinanceira.forEach((cobranca) => {
        cobranca.valor = 0;
      });
      this.resetEstadoCobranca();
    },
    dividirFinanceiroIgualmente() {
      const quantidade = this.distribuicaoFinanceira.length;
      const total = this.totalDistribuirCentavos;
      if (
        !this.podeEditarDistribuicaoFinanceira ||
        this.modoDivisao !== "valor" ||
        this.totalFinanceiroBackend === null ||
        this.calculandoTotalFinanceiro ||
        quantidade < 2 ||
        quantidade > 20 ||
        quantidade > total
      ) {
        this.$toast.warning(quantidade > total
          ? "O total é insuficiente para atribuir ao menos R$ 0,01 a cada cobrança."
          : "Adicione de 2 a 20 cobranças com valor positivo.");
        return;
      }

      const base = Math.floor(total / quantidade);
      this.distribuicaoFinanceira.forEach((cobranca, indice) => {
        cobranca.valor =
          (indice === quantidade - 1
            ? total - base * (quantidade - 1)
            : base) / 100;
      });
      this.resetEstadoCobranca();
    },
    async cancelarDivisaoCobranca() {
      if (this.cancelandoDivisao || this.cancelandoRascunho) {
        return false;
      }

      if (this.rascunho?.id) {
        if (!this.canCancelarRascunho(this.rascunho)) {
          this.$toast.warning(
            "Esta cobrança já possui documento ou pagamento e não pode ser cancelada aqui."
          );
          return false;
        }
        if (!(await this.cancelarRascunho(this.rascunho))) {
          return false;
        }
        this.encerrarDivisaoCobranca();
        this.carregarItensDoPagador();
        this.fecharDivisaoCobranca();
        return true;
      }

      if (!this.podeTrocarModoDivisao) {
        return false;
      }

      try {
        this.cancelandoDivisao = true;
        if (!(await this.getCheckout())) {
          return false;
        }

        this.encerrarDivisaoCobranca();
        this.carregarItensDoPagador();
        this.fecharDivisaoCobranca();
        this.$toast.info("Divisão cancelada. Saldo atual carregado.");
        return true;
      } finally {
        this.cancelandoDivisao = false;
      }
    },
    encerrarDivisaoCobranca({ preservarRascunho = false } = {}) {
      const rascunhoSalvo = preservarRascunho ? this.rascunho : null;
      this.dividindoCobranca = false;
      this.edicaoPersistidaOriginal = null;
      this.modoDivisao = null;
      this.distribuicaoFinanceira = [];
      this.cobrancaItens = [];
      this.sequenciaTotalFinanceiro += 1;
      this.totalFinanceiroBackend = null;
      this.calculandoTotalFinanceiro = false;
      this.resetEstadoCobranca();
      this.rascunho = rascunhoSalvo;
    },
    fazerNovaCobranca() {
      this.cobrancaParcialConcluida = false;
    },
    async onPagamentoModalHidden() {
      const pendente = this.faturamentoPendente;
      const fechamentoId = pendente?.fechamento?.id || this.pagamentoFechamento?.id;
      this.faturamentoPendente = null;
      this.resetPagamentoModal();
      if (!pendente) return;
      if (!fechamentoId && !this.dividindoCobranca) return;

      const atualizado = await this.getCheckout({ silencioso: true, notificarErro: false });
      if (!atualizado) {
        this.$toast.warning("Atualize o Checkout antes de tentar faturar novamente.");
        return;
      }
      if (fechamentoId && !this.dividindoCobranca) {
        const reversivel = await this.reverterFechamentoDiretoSemDocumento(fechamentoId);
        if (!reversivel && this.fechamentoNfcePendente) {
          this.$toast.warning("Existe NFC-e pendente nesta cobrança. Retome pelo F5.");
        }
      }
      if (!pendente.documentoGerado && this.dividindoCobranca) {
        this.encerrarDivisaoCobranca();
        this.rascunho = null;
        await this.getCheckout();
      }
    },
    async recuperarFechamentoDiretoAoAbrir() {
      if (this.faturamentoPendente || this.dividindoCobranca || this.confirmandoPagamento) return;
      const recuperaveis = (this.checkout?.fechamentos_diretos_recuperaveis || [])
        .filter((fechamento) => fechamento.escopo === this.form.escopo &&
          Number(fechamento.comanda_id || 0) === Number(this.form.comanda_id || 0));
      if (recuperaveis.length !== 1) return;
      this.aplicarAtendenteDoFechamento(recuperaveis[0]);
      this.aplicarAjustesGeraisDoFechamento(recuperaveis[0]);
      await this.reverterFechamentoDiretoSemDocumento(recuperaveis[0].id);
    },
    async reverterFechamentoDiretoSemDocumento(fechamentoId) {
      if (this.dividindoCobranca || !fechamentoId) return false;
      const recuperavel = (this.checkout?.fechamentos_diretos_recuperaveis || [])
        .some((fechamento) => Number(fechamento.id) === Number(fechamentoId));
      if (!recuperavel) return false;

      try {
        await this.$http.post(
          `/restaurante/checkout/${fechamentoId}/cancelar-rascunho`,
          {}, { skipErrorToast: true }
        );
      } catch (error) {
        await this.getCheckout({ silencioso: true, notificarErro: false });
        return false;
      }

      this.rascunho = null;
      this.cobrancaItens = [];
      const atualizado = await this.getCheckout({ silencioso: true, notificarErro: false });
      if (!atualizado) return false;
      this.pagamentoFechamento = null;
      this.faturamentoPendente = null;
      this.preVendaGerada = null;
      this.nfeGerada = null;
      return true;
    },
    async gerarPreVenda() {
      return this.finalizarCobranca("pre_venda");
    },
    async finalizarCobranca(tipo = "pre_venda") {
      if (!["pre_venda", "nfce"].includes(tipo)) {
        return;
      }
      if (this.abrindoFaturamento || this.faturamentoPendente) return;

      if (tipo === "nfce" && !this.featureFlags.nfceCheckout) {
        this.$toast.warning("Emissão NFC-e indisponível neste checkout.");
        return;
      }

      if (!this.validarDocumentoCobranca()) {
        return;
      }

      const podeFinalizar =
        tipo === "nfce" ? this.canFinalizarNfce : this.canFinalizarPreVenda;

      if (!podeFinalizar) {
        if (!this.cobrancaItens.length && !this.rascunhoCobrancaAtual?.id &&
          !this.fechamentoNfcePendente) {
          this.$toast.warning(this.form.escopo === "comanda"
            ? "Esta comanda não possui itens disponíveis para faturamento."
            : "Esta mesa não possui itens disponíveis para faturamento.");
        } else {
          this.$toast.warning("Confira itens e valores antes de finalizar.");
        }
        return;
      }

      this.abrindoFaturamento = true;
      try {
        const caixaAberto = await this.ensureCaixaAbertoParaPdv(tipo);

        if (!caixaAberto) {
          return;
        }

        const salvo = tipo === "nfce" && this.fechamentoNfcePendente
          ? this.fechamentoNfcePendente
          : tipo === "pre_venda" && this.fechamentoPreVendaPendente
            ? this.fechamentoPreVendaPendente
            : (this.dividindoCobranca ? this.rascunho : this.rascunhoAtivo);
        const usarSalvo = Boolean(salvo?.id) &&
          (!this.dividindoCobranca || !this.edicaoPersistidaAlterada);
        if (usarSalvo) this.aplicarAtendenteDoFechamento(salvo);
        const fechamento = usarSalvo
          ? salvo
          : {
              valor_total: this.totalCobrancaExibicao,
              itens: this.cobrancaItensDetalhados,
              totais_cobranca: this.totaisCobrancaCalculados,
              ...(this.modoDivisao === "valor" && {
                distribuicao_financeira: this.distribuicaoFinanceira.map(
                  (linha) => this.roundCurrency(Number(linha.valor))
                ),
              }),
            };
        this.faturamentoPendente = {
          tipo,
          valor_total: this.roundCurrency(Number(fechamento.valor_total)),
          documentoGerado: Boolean(tipo === "pre_venda" && salvo?.pre_venda_id),
          criadoNestaTentativa: false,
          persistidoAntes: Boolean(salvo?.id),
          fechamento: usarSalvo ? salvo : null,
        };
        this.selecionarPagamento(fechamento, tipo);
        if (this.pagamentoFechamento) {
          this.pagamentoFechamento.valor_total = this.faturamentoPendente.valor_total;
        }
      } finally {
        this.abrindoFaturamento = false;
      }
    },
    async ensureRascunhoCobranca(valorTotalEsperado = null) {
      if (this.dividindoCobranca) {
        if (!this.canPreview) {
          this.$toast.warning("Selecione itens válidos para esta cobrança.");
          return null;
        }
        if (this.rascunho?.id &&
          (!this.podeEditarDivisao || !this.edicaoPersistidaAlterada)) {
          return this.rascunho;
        }
        return this.criarRascunho(valorTotalEsperado);
      }

      if (this.rascunhoAtivo?.id) {
        return this.rascunhoAtivo;
      }

      if (!this.canPreview) {
        this.$toast.warning("Selecione itens para montar a cobrança.");
        return null;
      }

      const rascunho = await this.criarRascunho(valorTotalEsperado);
      return rascunho || this.rascunhoAtivo;
    },
    async gerarPreVendaDoRascunho(rascunho, { abrirPagamento = true } = {}) {
      if (!rascunho?.id) {
        this.$toast.warning("Crie um rascunho de cobrança primeiro.");
        return null;
      }

      try {
        this.gerandoPreVenda = true;
        this.setLoading(true);
        const { data } = await this.$http.post(
          `/restaurante/checkout/${rascunho.id}/gerar-pre-venda`,
          this.buildPayloadAjustesCobranca(rascunho)
        );

        this.preVendaGerada = data;
        this.rascunho = this.normalizarFechamentoComAjustes(data.fechamento);
        if (abrirPagamento) {
          this.selecionarPagamento(this.rascunho, "pre_venda");
        }
        this.cobrancaItens = [];
        this.$toast.success("Pré-venda gerada com sucesso.");
        await this.getCheckout();
        return data;
      } catch (error) {
        if (this.isErroCaixaFechado(error)) {
          this.gerarPreVendaPendenteAbertura = true;
          this.tipoCobrancaPendenteAbertura = "pre_venda";
          this.$bvModal.show("modal-abertura-caixa-restaurante");
          return null;
        }

        this.handleError(error, "Não foi possível gerar a pré-venda.");
        return null;
      } finally {
        this.gerandoPreVenda = false;
        this.setLoading(false);
      }
    },
    isErroCaixaFechado(error) {
      const message = String(
        error?.response?.data?.msg ||
          error?.response?.data?.message ||
          error?.message ||
          ""
      ).toLowerCase();

      return message.includes("caixa") && message.includes("aberto");
    },
    async imprimirConferencia() {
      if (!this.podeImprimirConferencia) {
        return;
      }
      try {
        this.imprimindoConferencia = true;
        this.conferenciaUrl = null;
        if (this.conferenciaBlobUrl) URL.revokeObjectURL(this.conferenciaBlobUrl);
        this.conferenciaBlobUrl = null;
        const payload = this.requestPayload();
        delete payload.distribuicao_financeira;
        if (this.rascunhoCobrancaAtual?.id) {
          payload.fechamento_id = this.rascunhoCobrancaAtual.id;
        }
        const { data } = await this.$http.post(
          "/restaurante/checkout/conferencia",
          payload,
          { timeout: 45000 }
        );
        const url = data?.["preview-url"];
        if (typeof url !== "string" || !url.trim()) {
          throw new Error("Documento de conferência não retornado.");
        }
        try {
          const protocolo = new URL(url, window.location.href).protocol;
          if (protocolo !== "http:" && protocolo !== "https:") {
            throw new Error();
          }
        } catch {
          throw new Error("Endereço do PDF de conferência inválido.");
        }
        const pdfUrl = data?.["pdf-url"];
        if (typeof pdfUrl !== "string" ||
          !/^\/restaurante\/checkout\/conferencia\/[a-f0-9]{13}\.pdf$/.test(pdfUrl)) {
          throw new Error("Referência do PDF de conferência inválida.");
        }
        this.conferenciaUrl = pdfUrl;

        const envio = await this.enviarPdfPdvCompartilhado(url, "conferencia", this.atendimentoId);
        if (envio.enviado) {
          this.$toast.info("Conferência enviada para a impressora. Confira o papel antes de reenviar.");
          return;
        }
        if (envio.motivo === "indisponivel") {
          this.$toast.warning(`Impressora indisponível (${envio.status}). Confira a Conferência em PDF.`);
        } else if (envio.motivo === "nao_encontrada") {
          this.$toast.warning("Impressora configurada não encontrada. Confira a Conferência em PDF.");
        } else if (envio.motivo === "incerto") {
          this.$toast.warning("QZ não confirmou o envio. Verifique a fila antes de imprimir novamente.");
        } else if (envio.motivo === "falha") {
          this.$toast.warning("Não foi possível confirmar o envio à impressora. Verifique a fila antes de imprimir novamente.");
        }
        await this.abrirConferenciaPdf();
      } catch (error) {
        this.handleError(error, "Não foi possível gerar a conferência.");
      } finally {
        this.imprimindoConferencia = false;
      }
    },
    async enviarPdfPdvCompartilhado(url, tipo, id) {
      if (!url) {
        return { enviado: false, motivo: "sem_impressora" };
      }
      const chave = `${tipo}:${tipo === "conferencia" ? url : id || url}`;
      if (this.impressoesPdvTentadas?.has(chave)) {
        return { enviado: false, motivo: "ja_tentado" };
      }
      const impressora = this.impressoraPdv?.trim();
      if (!impressora) return { enviado: false, motivo: "sem_impressora" };
      let estado;
      try {
        estado = await consultarEstadoImpressoraQz(impressora);
      } catch {
        estado = { estado: "desconhecido" };
      }
      if (estado.estado === "indisponivel") {
        return { enviado: false, motivo: "indisponivel", status: estado.status };
      }

      if (!this.impressoesPdvTentadas) this.impressoesPdvTentadas = new Set();
      if (this.impressoesPdvTentadas.has(chave)) {
        return { enviado: false, motivo: "ja_tentado" };
      }
      this.impressoesPdvTentadas.add(chave);
      let timerQz;
      try {
        await Promise.race([
          imprimirDocumentosPdv({
            tipo,
            id,
            urls: [url],
            impressora,
            tentativas: new Set(),
            imprimirManual: () => {},
          }),
          new Promise((_, reject) => {
            timerQz = setTimeout(() => reject(new Error("QZ_DOCUMENTO_TIMEOUT")), 15000);
          }),
        ]);
        return { enviado: true };
      } catch (error) {
        return {
          enviado: false,
          motivo: error?.message === "QZ_DOCUMENTO_TIMEOUT" ? "incerto" :
            error?.code === "QZ_PRINTER_NOT_FOUND" ? "nao_encontrada" : "falha",
        };
      } finally {
        clearTimeout(timerQz);
      }
    },
    async abrirConferenciaPdf() {
      if (!this.conferenciaUrl || this.carregandoConferenciaPdf) return;
      this.carregandoConferenciaPdf = true;
      try {
        const response = await this.$http.get(this.conferenciaUrl, { responseType: "arraybuffer" });
        const tipo = String(response.headers?.["content-type"] || "").toLowerCase();
        const bytes = new Uint8Array(response.data);
        if (response.status !== 200 || !tipo.startsWith("application/pdf") ||
          bytes.length < 5 || String.fromCharCode(...bytes.slice(0, 5)) !== "%PDF-") {
          throw new Error("O arquivo de Conferência retornado não é um PDF válido.");
        }
        const blobUrl = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
        if (this.conferenciaBlobUrl) URL.revokeObjectURL(this.conferenciaBlobUrl);
        this.conferenciaBlobUrl = blobUrl;
        this.$bvModal.show("modal-conferencia-pdf");
      } catch (error) {
        this.$toast.error(error?.response?.status === 401 || error?.response?.status === 403
          ? "Sem acesso ao PDF da Conferência. Verifique sua sessão."
          : "Não foi possível carregar o PDF da Conferência. Tente abrir novamente.");
      } finally {
        this.carregandoConferenciaPdf = false;
      }
    },
    selecionarPagamento(fechamento, tipo = "pre_venda") {
      this.invalidarBuscaClientePagamento();
      this.clienteAntesPagamento = {
        documento: this.documentoCobranca,
        id: this.clientePessoaId,
        nome: this.clienteDocumentoNome,
        localizado: this.clienteDocumentoLocalizado,
        consultado: this.documentoConsultado,
        financeiroId: this.clienteFinanceiroId,
        financeiroNome: this.clienteFinanceiroNome,
        financeiroDocumento: this.clienteFinanceiroDocumento,
      };
      this.clienteSelecionadoNoPagamento = false;
      this.clienteCadastroPagamentoPendente = false;
      this.clienteFinanceiroId = this.clientePessoaId || fechamento?.buyer?.id_pessoa || null;
      this.clienteFinanceiroNome = this.clienteDocumentoNome || fechamento?.buyer?.nome || "";
      this.clienteFinanceiroDocumento = this.documentoCobranca || fechamento?.buyer?.cnpj_cpf || "";
      this.tipoPagamentoCobranca = tipo;
      this.pagamentoFechamento = this.faturamentoPendente && fechamento?.id
        ? { ...fechamento, documento_tipo: tipo, valor_total: Number(fechamento.valor_total) }
        : this.normalizarFechamentoPagamento(fechamento, tipo);
      const buyer = this.pagamentoFechamento?.buyer;
      const documentoBuyer = this.normalizeCpfCnpj(buyer?.cnpj_cpf);
      const documentoAtual = this.normalizeCpfCnpj(this.documentoCobranca);
      if (buyer?.id_pessoa && documentoBuyer && this.validaCpfCnpj(documentoBuyer) &&
          (!documentoAtual || documentoAtual === documentoBuyer) && !this.clientePessoaId) {
        this.documentoCobranca = this.formatCnpjAlfanumerico(documentoBuyer);
        this.clientePessoaId = buyer.id_pessoa;
        this.clienteDocumentoNome = buyer.nome || "";
        this.clienteDocumentoLocalizado = true;
      }
      if (
        this.nfeGerada?.fechamento_id !== this.pagamentoFechamento?.id &&
        !this.pagamentoFechamento?.nfe_id
      ) {
        this.nfeGerada = null;
      }
      const planoNfce = tipo === "nfce"
        ? (this.pagamentoFechamento?.pagamento_nfce ||
          this.pagamentoFechamento?.payload_preview?.pagamento_nfce)
        : null;
      if (planoNfce?.pagamentos?.length) {
        this.clienteFinanceiroId = planoNfce.id_pessoa_financeiro || null;
        this.clienteFinanceiroNome = planoNfce.cliente_financeiro?.nome || "";
        this.clienteFinanceiroDocumento = planoNfce.cliente_financeiro?.cnpj_cpf
          ? this.formatCnpjAlfanumerico(planoNfce.cliente_financeiro.cnpj_cpf) : "";
      }
      this.pagamentos = planoNfce?.pagamentos?.length
        ? planoNfce.pagamentos.map((pagamento) => ({
            ...pagamento,
            descricao: this.formasPagamento.find((forma) =>
              Number(forma.id) === Number(pagamento.forma_pagamento_id))?.descricao || "Pagamento",
          }))
        : this.pagamentoDistribuido
        ? this.distribuicaoFinanceiraFechamento.map((valor, index) => ({
            forma_pagamento_id: null,
            descricao: `Cobrança ${index + 1}`,
            valor: Number(valor),
          }))
        : [];
      this.previsoesParcelas = {};
      this.erroPreviaParcelas = "";
      this.buscaClientePagamento = "";
      this.resultadosClientePagamento = [];
      this.erroBuscaClientePagamento = "";
      this.ultimoTermoBuscaClientePagamento = "";
      this.ultimoPagamentoAdicionado = null;
      this.pagamentoForm.valor = this.pagamentoDistribuido
        ? 0
        : Number(this.pagamentoFechamento?.valor_total || 0);
      if (!this.pagamentoDistribuido) {
        this.pagamentoForm.forma_pagamento_id = null;
        this.ensureFormaPagamentoPadrao();
      }
      this.$bvModal.show("modal-pagamento-restaurante");
      if (this.clientePessoaId && !this.clienteDocumentoNome &&
          this.validaCpfCnpj(this.documentoCobranca)) {
        this.buscaClientePagamento = this.documentoCobranca;
        this.buscarClientePagamento();
      }
      if (!this.pagamentoDistribuido) {
        this.$nextTick(() => {
          const inputEl =
            this.$refs.valorPagamentoRestaurante?.$el ||
            this.$refs.valorPagamentoRestaurante;

          if (inputEl) {
            inputEl.focus();
            inputEl.select();
          }
        });
      }
    },
    canRetomarFechamento(fechamento) {
      return (
        !this.dividindoCobranca &&
        ["rascunho", "preview", "aguardando_conversao", "erro"].includes(
          fechamento.status
        ) &&
        !fechamento.pre_venda_id &&
        !fechamento.venda_id &&
        !fechamento.nfe_id &&
        !fechamento.pagamentos?.length
      );
    },
    async retomarFechamento(fechamento) {
      if (!this.canRetomarFechamento(fechamento) || !(await this.getCheckout())) {
        return false;
      }

      const salvo = (this.checkout?.fechamentos || []).find(
        (item) => Number(item.id) === Number(fechamento.id)
      );
      if (!salvo || !this.canRetomarFechamento(salvo)) {
        this.$toast.warning("Cobrança indisponível para retomada. Atualize o Checkout.");
        return false;
      }

      const porValor = this.fechamentoPorValor(salvo);
      if (!porValor && !(salvo.itens || []).length) {
        this.$toast.warning("Os itens desta cobrança não estão disponíveis para retomada.");
        return false;
      }

      this.form.escopo = salvo.escopo === "comanda" ? "comanda" : "mesa";
      this.form.comanda_id = this.form.escopo === "comanda"
        ? Number(salvo.comanda_id)
        : null;
      this.syncPagadorFromForm();
      this.rascunho = salvo;
      this.aplicarAtendenteDoFechamento(salvo);
      this.dividindoCobranca = true;
      this.modoDivisao = porValor ? "valor" : "itens";
      this.divisaoModalSelecionandoModo = false;
      this.distribuicaoFinanceira = [];
      this.cobrancaItens = [];
      this.aplicarAjustesGeraisDoFechamento(salvo);

      if (porValor) {
        this.cobrancaItens = salvo.rascunho_financeiro_incompleto
          ? []
          : (salvo.itens_edicao?.length ? salvo.itens_edicao : salvo.itens || []).map((item) => ({
              atendimento_item_id: Number(item.atendimento_item_id),
              quantidade: Number(item.quantidade),
            }));
        this.distribuicaoFinanceira = (salvo.distribuicao_financeira || []).map(
          (valor) => ({ id: this.proximaCobrancaFinanceiraId++, valor })
        );
        if (salvo.rascunho_financeiro_incompleto) {
          this.carregarItensDoPagador();
          if (!(await this.carregarTotalFinanceiro())) {
            return false;
          }
          if (
            Math.round(Number(salvo.valor_referencia || salvo.valor_total) * 100) !==
            this.totalDistribuirCentavos
          ) {
            this.$toast.info(
              "O total da conta mudou. Os valores salvos foram mantidos; confira o restante."
            );
          }
        } else {
          this.totalFinanceiroBackend = Number(
            salvo.valor_referencia || salvo.valor_total
          );
        }
      } else {
        this.totalFinanceiroBackend = null;
        this.cobrancaItens = salvo.itens.map((item) => {
          const original = (salvo.itens_edicao || []).find(
            (linha) => Number(linha.atendimento_item_id) === Number(item.atendimento_item_id)
          );
          return {
            atendimento_item_id: Number(item.atendimento_item_id),
            quantidade: Number(item.quantidade),
            ajuste_operacao: original?.ajuste?.operacao || null,
            ajuste_tipo: Number(original?.ajuste?.tipo || 1),
            ajuste_valor: Number(original?.ajuste?.valor || 0),
            ajuste_acrescimo_tipo: Number(original?.ajuste?.acrescimo?.tipo ||
              (original?.ajuste?.operacao === "acrescimo" ? original.ajuste.tipo : 1) || 1),
            ajuste_acrescimo_valor: Number(original?.ajuste?.acrescimo?.valor ??
              (original?.ajuste?.operacao === "acrescimo" ? original.ajuste.valor : 0)),
            ajuste_desconto_tipo: Number(original?.ajuste?.desconto?.tipo ||
              (original?.ajuste?.operacao === "desconto" ? original.ajuste.tipo : 1) || 1),
            ajuste_desconto_valor: Number(original?.ajuste?.desconto?.valor ??
              (original?.ajuste?.operacao === "desconto" ? original.ajuste.valor : 0)),
            valor_total_salvo: Number(item.valor_total),
          };
        });
      }

      this.restaurarDocumentoCobranca();
      this.edicaoPersistidaOriginal = JSON.stringify(this.requestPayload());
      this.$bvModal.show("modal-dividir-cobranca");
      return true;
    },
    canCancelarRascunho(fechamento) {
      return (
        ["rascunho", "preview", "aguardando_conversao"].includes(
          fechamento.status
        ) &&
        !fechamento.pre_venda_id &&
        !fechamento.venda_id &&
        (!fechamento.nfe_id || [3, 6].includes(Number(fechamento.nfe_status_fiscal))) &&
        !fechamento.pagamentos?.length &&
        !this.cancelandoRascunho
      );
    },
    async cancelarRascunho(fechamento) {
      if (!this.canCancelarRascunho(fechamento)) {
        return false;
      }

      try {
        this.cancelandoRascunho = true;
        this.setLoading(true);
        await this.$http.post(
          `/restaurante/checkout/${fechamento.id}/cancelar-rascunho`
        );
        this.$toast.success("Rascunho cancelado. Ajuste a cobrança novamente.");
        this.rascunho = null;
        this.preVendaGerada = null;
        this.nfeGerada = null;
        this.cobrancaItens = [];
        const atualizado = await this.getCheckout();
        if (atualizado && this.modoDivisao === "valor") {
          this.carregarItensDoPagador();
        }
        return atualizado;
      } catch (error) {
        this.handleError(error, "Não foi possível cancelar o rascunho.");
        return false;
      } finally {
        this.cancelandoRascunho = false;
        this.setLoading(false);
      }
    },
    adicionarPagamento() {
      const forma = this.formasPagamento.find(
        (forma) => forma.id === this.pagamentoForm.forma_pagamento_id
      );

      if (!forma) {
        this.$toast.warning("Selecione a forma de pagamento.");
        return;
      }

      if ([5, 90].includes(Number(forma.id_meio_pagamento)) ||
          this.destinoFinanceiroForma(forma) === "invalido") {
        this.$toast.warning("Forma de pagamento indisponível no Checkout Restaurante.");
        return;
      }

      const valor = this.parseValorPagamento(this.pagamentoForm.valor);
      if (!Number.isFinite(valor) || valor <= 0 || valor > 9999999999999.99 ||
          Math.abs(Math.round(valor * 100) - valor * 100) > 0.000001) {
        this.$toast.warning("Informe um valor positivo com até duas casas decimais.");
        return;
      }
      if (Number.isFinite(this.restantePagamento) && valor > this.restantePagamento &&
          Number(forma.id_meio_pagamento) !== 1) {
        this.$toast.warning("Somente dinheiro pode ultrapassar o valor restante para gerar troco.");
        return;
      }
      const agora = Date.now();
      if (this.ultimoPagamentoAdicionado &&
          agora - this.ultimoPagamentoAdicionado < 350) return;
      this.ultimoPagamentoAdicionado = agora;
      if (this.destinoFinanceiroForma(forma) === "receber") {
        this.abrirParcelamento({
          forma_pagamento_id: forma.id, descricao: forma.descricao, valor,
        });
        return;
      }
      this.pagamentos.push({
        forma_pagamento_id: forma.id,
        descricao: forma.descricao,
        valor,
      });
      this.pagamentoForm.valor = this.restantePagamento;
    },
    async atualizarPreviaParcelas() {
      const sequencia = this.sequenciaPreviaParcelas =
        (this.sequenciaPreviaParcelas || 0) + 1;
      this.previsoesParcelas = {};
      this.erroPreviaParcelas = "";
      const linhas = this.pagamentosPrazo || [];
      if (!linhas.length) {
        this.carregandoPreviaParcelas = false;
        return;
      }
      this.carregandoPreviaParcelas = true;
      try {
        const respostas = await Promise.all(linhas.map((linha) =>
          this.$http.post("/restaurante/checkout/preview-parcelas", {
            forma_pagamento_id: linha.forma.id,
            valor: Number(linha.pagamento.valor),
          })
        ));
        if (sequencia !== this.sequenciaPreviaParcelas) return;
        this.previsoesParcelas = Object.fromEntries(respostas.map((resposta, indice) => [
          linhas[indice].index, resposta.data.parcelas,
        ]));
      } catch (error) {
        if (sequencia !== this.sequenciaPreviaParcelas) return;
        const linha = linhas.find((item) => !this.previsoesParcelas[item.index]) || linhas[0];
        this.erroPreviaParcelas = `Não foi possível calcular os vencimentos de ${linha.forma.descricao}. ` +
          (error?.response?.data?.msg || "Verifique a condição de pagamento.");
      } finally {
        if (sequencia === this.sequenciaPreviaParcelas) this.carregandoPreviaParcelas = false;
      }
    },
    adicionarPagamentoPorAtalho(atalho) {
      const forma = this.findFormaPagamentoAtalho(atalho);

      if (!forma) {
        this.$toast.warning(`${atalho.descricao} não encontrado.`);
        return;
      }

      this.pagamentoForm.forma_pagamento_id = forma.id;
      if (this.tipoPagamentoCobranca !== "pre_venda") {
        this.pagamentoForm.valor =
          this.restantePagamento || this.pagamentoFechamentoValor;
      }
      this.adicionarPagamento();
    },
    onFormaPagamentoDistribuidoChange(index) {
      const pagamento = this.pagamentos[index];
      if (pagamento) this.$set(pagamento, "parcelas", null);
      if (pagamento && this.pagamentoPrazo(pagamento)) {
        this.abrirParcelamento(pagamento, index);
      }
    },
    lembrarPagamentoDistribuido(index) {
      const pagamento = this.pagamentos[index];
      this.$set(this.pagamentoDistribuidoAnterior, index, {
        ...pagamento,
        parcelas: pagamento?.parcelas?.map((parcela) => ({ ...parcela })) || null,
      });
    },
    async abrirParcelamento(pagamento, index = null) {
      if (this.parcelamentoForm) return;
      const forma = this.formasPagamento.find((item) =>
        Number(item.id) === Number(pagamento.forma_pagamento_id));
      if (!forma || this.destinoFinanceiroForma(forma) !== "receber") return;
      this.erroParcelamento = "";
      this.parcelamentoForm = {
        index,
        forma_pagamento_id: forma.id,
        valor: Number(pagamento.valor),
        quantidade: pagamento.parcelas?.length || Number(forma.vezes),
        limite: Number(forma.vezes),
        parcelas: (pagamento.parcelas || []).map((parcela) => ({
          ...parcela, value: this.formatarNumeroParcela(parcela.value),
        })),
        original: (pagamento.parcelas || []).map((parcela) => ({
          ...parcela, value: this.formatarNumeroParcela(parcela.value),
        })),
        padrao: null,
        clienteAnterior: {
          id: this.clienteFinanceiroId,
          nome: this.clienteFinanceiroNome,
          documento: this.clienteFinanceiroDocumento,
        },
        confirmado: false,
      };
      this.$bvModal.show("modal-parcelamento-restaurante");
      if (!pagamento.parcelas?.length) await this.carregarParcelamentoPadrao();
      else {
        const form = this.parcelamentoForm;
        this.carregandoParcelamento = true;
        try {
          const { data } = await this.$http.post("/restaurante/checkout/preview-parcelas", {
            forma_pagamento_id: forma.id, valor: Number(pagamento.valor),
          });
          if (this.parcelamentoForm === form) {
            form.limite = Math.min(Number(forma.vezes), Number(data.limite_parcelas || forma.vezes));
            form.padrao = (data.parcelas || []).map((parcela) => ({
              ...parcela, value: this.formatarNumeroParcela(parcela.value),
            }));
          }
        } catch (error) {
          if (this.parcelamentoForm === form) this.erroParcelamento =
            error?.response?.data?.msg || "Não foi possível validar a condição de pagamento.";
        } finally {
          if (this.parcelamentoForm === form) this.carregandoParcelamento = false;
        }
      }
    },
    formatarNumeroParcela(valor) {
      const numero = this.parseValorPagamento(valor);
      return Number.isFinite(numero) ? numero.toLocaleString("pt-BR", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }) : String(valor ?? "");
    },
    formatarValorParcela(parcela) {
      parcela.value = this.formatarNumeroParcela(parcela.value);
    },
    solicitarAlteracaoQuantidade(valor) {
      if (!this.parcelamentoForm) return;
      this.parcelamentoForm.quantidade = valor;
      if (this.timerQuantidadeParcelas) clearTimeout(this.timerQuantidadeParcelas);
      this.sequenciaParcelamento++;
      if (valor === "" || valor === null) {
        this.carregandoParcelamento = false;
        this.erroParcelamento = "";
        return;
      }
      this.timerQuantidadeParcelas = setTimeout(() => {
        this.timerQuantidadeParcelas = null;
        this.alterarQuantidadeParcelas();
      }, 250);
    },
    validarQuantidadeAoSair() {
      if (!this.parcelamentoForm) return;
      if (this.timerQuantidadeParcelas) clearTimeout(this.timerQuantidadeParcelas);
      this.timerQuantidadeParcelas = null;
      if (this.parcelamentoForm.quantidade === "") {
        this.erroParcelamento = "Informe a quantidade de parcelas.";
        return;
      }
      this.alterarQuantidadeParcelas();
    },
    excluirParcela(indice) {
      const form = this.parcelamentoForm;
      if (!form || this.carregandoParcelamento || indice < 0 ||
          indice >= form.parcelas.length || form.parcelas.length <= 1) return;
      if (this.timerQuantidadeParcelas) clearTimeout(this.timerQuantidadeParcelas);
      this.timerQuantidadeParcelas = null;
      this.sequenciaParcelamento++;
      form.quantidade = form.parcelas.length - 1;
      return this.alterarQuantidadeParcelas();
    },
    async alterarQuantidadeParcelas() {
      const form = this.parcelamentoForm;
      if (!form) return;
      if (form.quantidade === "" || form.quantidade === null) return;
      const quantidade = Number(form.quantidade);
      const limite = Math.min(Number(form.limite), Number(this.parcelamentoForma?.vezes));
      if (!/^\d+$/.test(String(form.quantidade)) || !Number.isInteger(quantidade) ||
          quantidade < 1 || quantidade > limite) {
        this.erroParcelamento = "";
        this.carregandoParcelamento = false;
        return;
      }
      if (quantidade === form.parcelas.length) {
        this.carregandoParcelamento = false;
        this.erroParcelamento = "";
        return;
      }
      if (JSON.stringify(form.parcelas) !== JSON.stringify(form.original)) {
        const sequencia = this.sequenciaParcelamento;
        const ok = await this.$bvModal.msgBoxConfirm(
          "Recalcular parcelas e descartar valores ou vencimentos editados?",
          { title: "Alterar quantidade" });
        if (!ok || this.parcelamentoForm !== form || sequencia !== this.sequenciaParcelamento ||
            Number(form.quantidade) !== quantidade) {
          if (this.parcelamentoForm === form && sequencia === this.sequenciaParcelamento &&
              Number(form.quantidade) === quantidade)
            form.quantidade = form.parcelas.length;
          if (this.parcelamentoForm === form) this.carregandoParcelamento = false;
          return;
        }
      }
      await this.carregarParcelamentoPadrao(quantidade);
    },
    async carregarParcelamentoPadrao(quantidade = null) {
      const form = this.parcelamentoForm;
      if (!form) return;
      const sequencia = ++this.sequenciaParcelamento;
      const anterior = form.parcelas.length;
      this.carregandoParcelamento = true;
      this.erroParcelamento = "";
      try {
        const { data } = await this.$http.post("/restaurante/checkout/preview-parcelas", {
          forma_pagamento_id: form.forma_pagamento_id, valor: form.valor,
          ...(quantidade !== null && { quantidade }),
        });
        if (this.parcelamentoForm !== form || sequencia !== this.sequenciaParcelamento) return;
        if (!Array.isArray(data.parcelas) || data.parcelas.length > Number(this.parcelamentoForma?.vezes))
          throw new Error("A prévia excede o limite da condição.");
        form.parcelas = (data.parcelas || []).map((parcela) => ({
          ...parcela, value: this.formatarNumeroParcela(parcela.value),
        }));
        form.original = form.parcelas.map((parcela) => ({ ...parcela }));
        if (!form.padrao && quantidade === null)
          form.padrao = form.parcelas.map((parcela) => ({ ...parcela }));
        form.quantidade = form.parcelas.length;
        form.limite = Math.min(Number(this.parcelamentoForma?.vezes),
          Number(data.limite_parcelas || form.limite));
      } catch (error) {
        if (this.parcelamentoForm === form && sequencia === this.sequenciaParcelamento) {
          form.quantidade = anterior;
          this.erroParcelamento = error?.response?.data?.msg ||
            "Não foi possível calcular as parcelas desta condição.";
        }
      } finally {
        if (this.parcelamentoForm === form && sequencia === this.sequenciaParcelamento)
          this.carregandoParcelamento = false;
      }
    },
    async restaurarParcelamentoPadrao() {
      const form = this.parcelamentoForm;
      if (!form) return;
      if (form.padrao) {
        this.sequenciaParcelamento++;
        form.parcelas = form.padrao.map((parcela) => ({ ...parcela }));
        form.original = form.padrao.map((parcela) => ({ ...parcela }));
        form.quantidade = form.parcelas.length;
        this.erroParcelamento = "";
      } else {
        await this.carregarParcelamentoPadrao(Number(this.parcelamentoForma.vezes));
      }
    },
    erroDataParcela(parcelas, indice) {
      const data = String(parcelas[indice]?.date || "");
      if (!/^\d{4}-\d{2}-\d{2}$/.test(data)) return "Informe uma data válida.";
      const [ano, mes, dia] = data.split("-").map(Number);
      const calendario = new Date(Date.UTC(ano, mes - 1, dia));
      if (calendario.toISOString().slice(0, 10) !== data) return "Informe uma data válida.";
      const agora = new Date();
      const hoje = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, "0")}-${String(agora.getDate()).padStart(2, "0")}`;
      if (data < hoje) return "O vencimento não pode ser anterior a hoje.";
      if (indice > 0 && /^\d{4}-\d{2}-\d{2}$/.test(String(parcelas[indice - 1]?.date || "")) &&
          data < parcelas[indice - 1].date)
        return "O vencimento deve ser igual ou posterior ao anterior.";
      return "";
    },
    erroValorParcela(value) {
      const valor = this.parseValorPagamento(value);
      return !Number.isFinite(valor) || valor <= 0 ||
        Math.abs(Math.round(valor * 100) - valor * 100) > 0.000001;
    },
    parcelasPagamentoValidas(pagamento) {
      const forma = this.formasPagamento.find((item) =>
        Number(item.id) === Number(pagamento.forma_pagamento_id));
      const parcelas = pagamento.parcelas;
      const limite = Math.min(Number(forma?.vezes), Number(pagamento.limite_parcelas ||
        this.parcelamentoForm?.limite || forma?.vezes));
      if (!forma || !Array.isArray(parcelas) ||
          parcelas.length < 1 || parcelas.length > limite) return false;
      let soma = 0;
      for (const [indice, parcela] of parcelas.entries()) {
        const valor = this.parseValorPagamento(parcela.value);
        if (this.erroValorParcela(parcela.value) || this.erroDataParcela(parcelas, indice)) return false;
        soma += Math.round(valor * 100);
      }
      return soma === Math.round(Number(pagamento.valor) * 100);
    },
    confirmarParcelamento() {
      if (!this.parcelamentoForm || this.confirmandoParcelamento ||
          this.carregandoParcelamento || this.timerQuantidadeParcelas ||
          !/^\d+$/.test(String(this.parcelamentoForm.quantidade)) ||
          this.erroQuantidadeParcelamento ||
          Number(this.parcelamentoForm.quantidade) !== this.parcelamentoForm.parcelas.length) return;
      const form = this.parcelamentoForm;
      if (!this.clienteFinanceiroId) {
        this.erroParcelamento = "Consulte ou cadastre um cliente para o Contas a Receber.";
        return;
      }
      if (!this.parcelasPagamentoValidas(form)) {
        return;
      }
      this.confirmandoParcelamento = true;
      const parcelas = form.parcelas.map((parcela) => ({
        date: parcela.date, value: this.parseValorPagamento(parcela.value),
      }));
      if (form.index === null) {
        this.pagamentos.push({
          forma_pagamento_id: form.forma_pagamento_id,
          descricao: this.parcelamentoForma.descricao,
          valor: form.valor, parcelas, limite_parcelas: form.limite,
        });
      } else {
        this.$set(this.pagamentos, form.index, {
          ...this.pagamentos[form.index],
          forma_pagamento_id: form.forma_pagamento_id,
          descricao: this.parcelamentoForma.descricao,
          parcelas, limite_parcelas: form.limite,
        });
      }
      form.confirmado = true;
      this.pagamentoForm.valor = this.restantePagamento;
      this.$bvModal.hide("modal-parcelamento-restaurante");
    },
    onParcelamentoHidden() {
      if (this.timerQuantidadeParcelas) clearTimeout(this.timerQuantidadeParcelas);
      this.timerQuantidadeParcelas = null;
      this.sequenciaParcelamento++;
      const form = this.parcelamentoForm;
      if (form && !form.confirmado && form.clienteAnterior) {
        this.clienteFinanceiroId = form.clienteAnterior.id;
        this.clienteFinanceiroNome = form.clienteAnterior.nome;
        this.clienteFinanceiroDocumento = form.clienteAnterior.documento;
      }
      if (form && !form.confirmado && form.index !== null && this.pagamentoDistribuido) {
        const anterior = this.pagamentoDistribuidoAnterior[form.index];
        if (anterior) this.$set(this.pagamentos, form.index, anterior);
        else this.$set(this.pagamentos[form.index], "forma_pagamento_id", null);
      }
      if (form && !form.confirmado && !this.pagamentosPrazo.length &&
          this.clienteSelecionadoNoPagamento && this.clienteAntesPagamento) {
        this.restaurarClienteAnteriorPagamento();
      }
      this.clienteCadastroPagamentoPendente = false;
      this.parcelamentoForm = null;
      this.pagamentoDistribuidoAnterior = {};
      this.confirmandoParcelamento = false;
      this.carregandoParcelamento = false;
      this.erroParcelamento = "";
    },
    findFormaPagamentoAtalho(atalho) {
      return this.formasPagamento.find((forma) => {
        const descricao = String(forma.descricao || "").toLowerCase();

        return atalho.termos.some((termo) => descricao.includes(termo));
      });
    },
    removerPagamento(index) {
      const tinhaReceber = this.pagamentosPrazo.length > 0;
      this.pagamentos.splice(index, 1);
      this.ultimoPagamentoAdicionado = null;
      this.previsoesParcelas = {};
      if (tinhaReceber && !this.pagamentosPrazo.length &&
          this.clienteSelecionadoNoPagamento && this.clienteAntesPagamento) {
        this.restaurarClienteAnteriorPagamento();
      }
      this.pagamentoForm.valor = this.restantePagamento;
    },
    restaurarClienteAnteriorPagamento() {
      this.clienteFinanceiroId = this.clienteAntesPagamento.financeiroId || null;
      this.clienteFinanceiroNome = this.clienteAntesPagamento.financeiroNome || "";
      this.clienteFinanceiroDocumento = this.clienteAntesPagamento.financeiroDocumento || "";
      this.clienteSelecionadoNoPagamento = false;
    },
    resetPagamentoModal() {
      if (this.confirmandoPagamento) {
        return;
      }

      this.pagamentos = [];
      this.sequenciaBuscaClientePagamento++;
      this.buscandoClientePagamento = false;
      if (this.timerBuscaClientePagamento) clearTimeout(this.timerBuscaClientePagamento);
      this.timerBuscaClientePagamento = null;
      this.buscaClientePagamento = "";
      this.resultadosClientePagamento = [];
      this.erroBuscaClientePagamento = "";
      this.ultimoTermoBuscaClientePagamento = "";
      this.ultimoPagamentoAdicionado = null;
      this.clienteAntesPagamento = null;
      this.clienteSelecionadoNoPagamento = false;
      this.clienteCadastroPagamentoPendente = false;
      this.clienteFinanceiroId = null;
      this.clienteFinanceiroNome = "";
      this.clienteFinanceiroDocumento = "";
      this.previsoesParcelas = {};
      this.pagamentoFechamento = null;
      this.pagamentoForm.valor = 0;
      this.tipoPagamentoCobranca = "pre_venda";
    },
    async confirmarPagamento() {
      if (!this.canConfirmarPagamento) {
        return;
      }
      if (this.pagamentosPrazo.length && !this.clienteFinanceiroId) {
        this.$toast.warning("Consulte ou cadastre um cliente antes de confirmar o Contas a Receber.");
        return;
      }

      let nfeEmitida = null;
      let pagamentoConcluido = false;
      let falhaRevertida = false;

      try {
        this.confirmandoPagamento = true;
        this.setLoading(true);

        if (this.faturamentoPendente) {
          const pendente = this.faturamentoPendente;
          if (!pendente.fechamento?.id) {
            const rascunho = await this.ensureRascunhoCobranca(pendente.valor_total);
            if (!rascunho?.id) return;
            if (Math.round(Number(rascunho.valor_total) * 100) !==
              Math.round(pendente.valor_total * 100)) {
              throw new Error("O total da cobrança mudou. Atualize o Checkout.");
            }
            pendente.fechamento = rascunho;
            pendente.criadoNestaTentativa = !pendente.persistidoAntes;
          }
          if (pendente.tipo === "pre_venda" && !pendente.documentoGerado) {
            const gerada = await this.gerarPreVendaDoRascunho(
              pendente.fechamento, { abrirPagamento: false }
            );
            if (!gerada?.fechamento?.id) {
              if (!(await this.getCheckout())) return;
              const convertido = (this.checkout?.fechamentos || []).find(
                (fechamento) => Number(fechamento.id) ===
                  Number(pendente.fechamento.id) && fechamento.pre_venda_id
              );
              if (!convertido) {
                if (await this.reverterFechamentoDiretoSemDocumento(pendente.fechamento.id)) {
                  falhaRevertida = true;
                }
                return;
              }
              pendente.fechamento = convertido;
            } else {
              pendente.fechamento = gerada.fechamento;
            }
            pendente.documentoGerado = true;
          }
          this.pagamentoFechamento = {
            ...this.normalizarFechamentoPagamento(pendente.fechamento, pendente.tipo),
            valor_total: Number(pendente.fechamento.valor_total),
            totais_cobranca: pendente.fechamento.totais_cobranca ||
              pendente.fechamento.payload_preview?.totais_cobranca,
          };
        }

        if (this.tipoPagamentoCobranca === "nfce") {
          nfeEmitida = await this.emitirNfceCheckout();
        }

        const { data } = await this.$http.post(
          `/restaurante/checkout/${this.pagamentoFechamento.id}/confirmar-pagamento`,
          this.buildPayloadConfirmarPagamento(nfeEmitida),
          this.tipoPagamentoCobranca === "nfce" ? { skipErrorToast: true } : undefined
        );
        const liberouMesa = Boolean(data.liberou_mesa);

        this.$toast.success(data.fechamento?.payload_resultado?.pagamento?.financeiro?.valor_a_receber > 0
          ? (liberouMesa ? "Faturamento formalizado e mesa liberada." : "Faturamento formalizado.")
          : (liberouMesa ? "Pagamento confirmado e mesa liberada." : "Pagamento confirmado."));

        if (this.tipoPagamentoCobranca === "pre_venda") {
          try {
            await this.imprimirComprovantePreVenda(data.impressao || data);
          } catch (error) {
            this.$toast.warning("Pagamento finalizado, mas não foi possível imprimir o comprovante.");
          }
        }

        if (data.impressao_erro) {
          this.$toast.warning(
            `Pagamento finalizado, mas não foi possível imprimir: ${data.impressao_erro}`
          );
        }
        if (nfeEmitida?.id) {
          try {
            const { data: impressao } = await this.$http.post("/nfe/printNota", {
              id: nfeEmitida.id,
            }, { skipErrorToast: true });
            await this.imprimirDocumentosNfce(impressao);
          } catch (error) {
            this.$toast.warning("NFC-e finalizada, mas não foi possível imprimir o documento.");
          }
        }

        this.pagamentoFechamento = null;
        this.pagamentos = [];
        this.rascunho = null;
        this.preVendaGerada = null;
        this.nfeGerada = null;
        this.faturamentoPendente = null;
        this.cobrancaItens = [];
        this.documentoCobranca = "";
        this.clienteDocumentoLocalizado = false;
        this.clienteDocumentoNome = "";
        this.clienteDocumentoNaoCadastrado = false;
        this.clienteDocumentoInativo = false;
        this.clientePessoaId = null;
        this.documentoConsultado = null;

        if (this.dividindoCobranca) {
          this.encerrarDivisaoCobranca();
        }

        this.cobrancaParcialConcluida = false;
        pagamentoConcluido = true;
      } catch (error) {
        const tipo = this.tipoPagamentoCobranca;
        const fechamentoId = this.faturamentoPendente?.fechamento?.id ||
          this.pagamentoFechamento?.id;
        if (fechamentoId && await this.getCheckout({ silencioso: true, notificarErro: false })) {
          const confirmado = (this.checkout?.fechamentos || []).find(
            (fechamento) => Number(fechamento.id) === Number(fechamentoId)
          );
          if (confirmado?.status === "pago") {
            this.$toast.success("Pagamento já confirmado. Checkout atualizado.");
            this.faturamentoPendente = null;
            this.pagamentoFechamento = null;
            this.pagamentos = [];
            this.rascunho = null;
            this.preVendaGerada = null;
            this.nfeGerada = null;
            if (this.dividindoCobranca) this.encerrarDivisaoCobranca();
            pagamentoConcluido = true;
            return;
          }
          if (await this.reverterFechamentoDiretoSemDocumento(fechamentoId)) {
            falhaRevertida = true;
            this.handleError(error, tipo === "nfce"
              ? "Não foi possível emitir a NFC-e. Revise a cobrança e tente novamente."
              : "Não foi possível concluir a pré-venda. Revise a cobrança e tente novamente.",
            { ocultarDetalhes: tipo === "nfce" });
            return;
          }
          if (confirmado?.nfe_id && !confirmado?.pre_venda_id) {
            if (this.faturamentoPendente) this.faturamentoPendente.documentoGerado = true;
            this.handleError(error,
              "NFC-e existente com financeiro pendente. Retome pelo F5 sem criar outra nota.",
            { ocultarDetalhes: true });
            return;
          }
        }
        if (nfeEmitida?.id || this.nfeGerada?.id) {
          this.handleError(
            error,
            "NFC-e emitida, mas não foi possível vincular o pagamento ao checkout. Atualize a tela e revise a cobrança.",
            { ocultarDetalhes: true }
          );
          return;
        }

        this.handleError(
          error,
          tipo === "nfce"
            ? "Não foi possível emitir a NFC-e."
            : "Não foi possível confirmar o pagamento.",
          { ocultarDetalhes: tipo === "nfce" }
        );
      } finally {
        this.confirmandoPagamento = false;
        this.gerandoNfce = false;
        this.setLoading(false);
        if (falhaRevertida) this.$bvModal.hide("modal-pagamento-restaurante");
        if (pagamentoConcluido) {
          this.abrindoFaturamento = false;
          this.criandoRascunho = false;
          this.gerandoPreVenda = false;
          this.$bvModal.hide("modal-pagamento-restaurante");
          this.voltarMesas();
        }
      }
    },
    buildPayloadConfirmarPagamento(nfeEmitida = null) {
      return {
        pagamentos: this.pagamentos,
        documento_tipo: this.tipoPagamentoCobranca,
        id_pessoa_financeiro: this.clienteFinanceiroId,
        ...(this.tipoPagamentoCobranca === "pre_venda" && {
          id_pessoa: this.clientePessoaId || this.pagamentoFechamento?.buyer?.id_pessoa || null,
          documento_cobranca: this.normalizeCpfCnpj(this.documentoCobranca),
        }),
        ...(nfeEmitida?.id && { nfe_id: nfeEmitida.id }),
      };
    },
    buildPayloadAjustesCobranca(fechamento = null) {
      // Mantem apenas insumos + id do fechamento. Totais sao autoridade do
      // backend (recalculados a partir dos itens/configuracao).
      return {
        ...this.requestPayload(),
        fechamento_id: fechamento?.id || this.rascunhoCobrancaAtual?.id || null,
        idempotency_key: this.idempotencyKey(),
      };
    },
    normalizarFechamentoPagamento(fechamento, tipo = "pre_venda") {
      const fechamentoComAjustes =
        this.normalizarFechamentoComAjustes(fechamento) || fechamento || {};
      const valorTotal =
        this.valorFechamentoExibicao(fechamentoComAjustes) ||
        Number(fechamentoComAjustes.valor_total || 0);

      return {
        ...fechamentoComAjustes,
        documento_tipo: tipo,
        valor_total: this.roundCurrency(valorTotal),
        itens:
          fechamentoComAjustes?.itens?.length > 0
            ? fechamentoComAjustes.itens
            : this.cobrancaItensDetalhados,
      };
    },
    async emitirNfceCheckout() {
      this.gerandoNfce = true;
      const payload = this.buildVendaNfcePayload();
      if (this.faturamentoPendente) {
        this.faturamentoPendente.criacaoNfceIniciada = true;
      }
      const recuperar = async () => {
        const { data: nota } = await this.$http.post("/nfe/create", {
          dados: payload,
          pagamentos: this.pagamentos,
          id_pessoa_financeiro: this.clienteFinanceiroId,
        }, { skipErrorToast: true });
        const id = this.getNotaId(nota);
        if (!id || (this.nfeGerada?.id && Number(this.nfeGerada.id) !== Number(id))) {
          throw new Error("NFC-e existente não pôde ser identificada com segurança.");
        }
        this.nfeGerada = {
          id,
          fechamento_id: this.pagamentoFechamento?.id,
          nota,
          autorizada: Number(nota.status_fiscal) === 2 &&
            Number(nota.status_transmissao) === 4,
        };
        return nota;
      };
      let nota;
      try {
        nota = await recuperar();
      } catch (error) {
        if (error?.response) {
          if (error.response.status < 500) throw error;
          let notaVinculada = false;
          try {
            const busca = this.codigoComandaRoute
              ? { codigo_comanda: this.codigoComandaRoute }
              : { atendimento_id: this.atendimentoId };
            const { data: checkout } = await this.$http.post(
              "/restaurante/checkout/find", busca, { skipErrorToast: true }
            );
            notaVinculada = Boolean((checkout?.fechamentos || []).find((fechamento) =>
              Number(fechamento.id) === Number(this.pagamentoFechamento?.id))?.nfe_id);
          } catch (consultaErro) {
            // Sem estado conclusivo, a próxima chamada recupera a nota pelo fechamento.
            notaVinculada = true;
          }
          if (!notaVinculada) throw error;
        }
        try {
          nota = await recuperar();
        } catch (segundaFalha) {
          throw new Error("Não foi possível recuperar a NFC-e desta cobrança. Consulte o documento antes de tentar novamente.");
        }
      }

      if (!this.nfeGerada.autorizada) {
        const podeTransmitir = Number(nota.status_fiscal) === 1 &&
          nota.status_transmissao == null;
        if (podeTransmitir) {
          try {
            const { data: transmissao } = await this.$http.post("/nfe/transmitter", {
              id: this.nfeGerada.id,
            }, { skipErrorToast: true });
            if (transmissao?.status === false) {
              throw new Error(transmissao.msg || "A transmissão da NFC-e não foi autorizada.");
            }
          } catch (error) {
            // O envio pode ter chegado à SEFAZ; a consulta abaixo decide o estado.
          }
          nota = await recuperar();
        }
        if (!this.nfeGerada.autorizada) {
          try {
            await this.$http.post("/nfe/consult", { id: this.nfeGerada.id },
              { skipErrorToast: true });
            nota = await recuperar();
          } catch (error) {
            throw new Error("Situação fiscal da NFC-e existente ainda não confirmada. Consulte a nota antes de continuar.");
          }
        }
        if (!this.nfeGerada.autorizada) {
          throw new Error("A NFC-e existente ainda não está autorizada. Consulte ou corrija o documento antes de continuar.");
        }
      }
      if (this.faturamentoPendente) {
        this.faturamentoPendente.documentoGerado = true;
      }

      return this.nfeGerada;
    },
    buildVendaNfcePayload() {
      const productOfTradeCollection = this.buildProdutosNfce();
      const paymentModeCollection = this.buildPagamentosNfce();
      const valorProdutos = this.roundCurrency(
        productOfTradeCollection.reduce(
          (total, item) => total + Number(item.totalPrice || 0),
          0
        )
      );
      const totais = this.pagamentoFechamento?.totais_cobranca ||
        this.totaisCobrancaCalculados;
      const extraValue = this.roundCurrency(
        Number(totais.taxa_servico_valor || 0) +
        Number(totais.couvert_total || 0) +
        Number(totais.valor_acrescimo_geral || 0)
      );
      const discount = this.roundCurrency(Number(totais.valor_desconto_geral || 0));
      const price = this.roundCurrency(valorProdutos + extraValue - discount);
      if (price !== this.roundCurrency(this.pagamentoFechamentoValor)) {
        throw new Error("Total fiscal diverge do fechamento da cobrança.");
      }

      return {
        modelo: 65,
        tpNF: 1,
        serie: this.serieNfce || 99,
        remainder: this.roundCurrency(this.trocoPagamento),
        discount,
        extraValue,
        freightPrice: 0,
        price,
        observation: this.buildObservacaoNfce(),
        buyer:
          this.pagamentoFechamento?.buyer ||
          this.pagamentoFechamento?.payload_preview?.buyer ||
          {},
        seller: {},
        paymentModeCollection,
        productOfTradeCollection,
        tipoIntermediador: {
          id: 0,
          name: "Operação sem intermediador (em site ou plataforma própria)",
        },
        intermediador: null,
        usuarioIntermediador: null,
        status: 1,
        isNFce: true,
        tipoAcrescimo: extraValue > 0 ? { value: 1, text: "Valor R$" } : null,
        tipoAcrescimoValue: extraValue > 0 ? 1 : null,
        restaurante_checkout: true,
        restaurante_fechamento_id: this.pagamentoFechamento?.id || null,
        atendente_id: this.pagamentoFechamento?.atendente_id || this.atendenteId,
      };
    },
    buildProdutosNfce() {
      const linhas = this.linhasFiscaisCobranca();

      if (!linhas.length) {
        throw new Error("Não há itens para emitir a NFC-e.");
      }

      return linhas.map((linha, index) => {
        const quantidade = Number(linha.quantidade || 0);
        const totalPrice = this.roundCurrency(this.valorLinhaFiscal(linha));
        const price =
          quantidade > 0
            ? this.dividindoCobranca
              ? Number(
                  (
                    Number(linha.valor_unitario) || totalPrice / quantidade
                  ).toFixed(4)
                )
              : this.roundCurrency(totalPrice / quantidade)
            : 0;
        if (
          (this.dividindoCobranca || this.pagamentoDistribuido) &&
          this.roundCurrency(price * quantidade) !== totalPrice
        ) {
          throw new Error(
            "O valor do item fiscal diverge do fechamento da cobrança."
          );
        }

        return {
          product: this.buildProdutoFiscalCheckout(linha.item, price),
          price,
          quantity: quantidade,
          totalPrice,
          currentIndex: index + 1,
          active: true,
        };
      });
    },
    linhasFiscaisCobranca() {
      if (this.dividindoCobranca || this.pagamentoDistribuido) {
        const itens = this.pagamentoFechamento?.itens?.length
          ? this.pagamentoFechamento.itens
          : this.rascunhoCobrancaAtual?.itens || [];
        if (!itens.length) {
          throw new Error("Itens do rateio não encontrados no fechamento.");
        }
        return itens
          .map((item) => this.normalizarLinhaFechamentoFiscal(item))
          .filter((linha) => linha.item && Number(linha.quantidade || 0) > 0);
      }
      if (this.cobrancaItensDetalhados.length) {
        return this.cobrancaItensDetalhados;
      }

      const itens =
        this.pagamentoFechamento?.itens ||
        this.rascunhoCobrancaAtual?.itens ||
        [];

      return itens
        .map((item) => this.normalizarLinhaFechamentoFiscal(item))
        .filter((linha) => linha.item && Number(linha.quantidade || 0) > 0);
    },
    normalizarLinhaFechamentoFiscal(linha) {
      const item =
        linha?.atendimento_item ||
        linha?.item ||
        linha?.restaurante_atendimento_item ||
        linha;

      return {
        ...linha,
        item: { ...item, produto: item?.produto || linha?.produto },
        quantidade: Number(
          linha?.quantidade ||
            linha?.quantidade_cobrada ||
            item?.quantidade_pendente ||
            item?.quantidade ||
            0
        ),
        valor_total: Number(
          linha?.valor_total ||
            linha?.total ||
            linha?.valor ||
            item?.valor_pendente ||
            item?.valor_total ||
            0
        ),
      };
    },
    buildProdutoFiscalCheckout(item, price) {
      const produtoBase = item?.produto || {};
      const produtoId =
        produtoBase.id || produtoBase.id_produto || item?.produto_id || null;
      const produtoUuid =
        produtoBase.uuid || item?.produto_uuid || item?.produto?.uuid || null;

      if (!produtoUuid) {
        throw new Error(
          `Produto fiscal sem UUID: ${this.produtoDescricao(
            item
          )}. Atualize a tela e tente novamente.`
        );
      }

      return {
        ...produtoBase,
        ...(produtoId && { id: produtoId, id_produto: produtoId }),
        uuid: produtoUuid,
        descricao:
          produtoBase.descricao ||
          item?.produto_descricao ||
          this.produtoDescricao(item),
        preco: price,
        originalPreco: produtoBase.originalPreco || produtoBase.preco || price,
      };
    },
    valorLinhaFiscal(linha) {
      if (linha.valor_total !== undefined && Number(linha.valor_total) > 0) {
        return Number(linha.valor_total);
      }

      return this.valorLinhaCobranca(linha);
    },
    buildPagamentosNfce() {
      const pagamentos = this.pagamentos
        .map((pagamento) => {
          const valor = Number(pagamento.valor || 0);
          return this.normalizarPagamentoNfce(pagamento, this.roundCurrency(valor));
        })
        .filter((pagamento) => Number(pagamento.value || 0) > 0);

      if (!pagamentos.length) {
        throw new Error(
          "Informe ao menos uma forma de pagamento para a NFC-e."
        );
      }

      return pagamentos;
    },
    normalizarPagamentoNfce(pagamento, valor) {
      const forma =
        this.formasPagamento.find(
          (item) => item.id === pagamento.forma_pagamento_id
        ) || {};
      const descricao = forma.descricao || pagamento.descricao;

      return {
        ...forma,
        id: forma.id || pagamento.forma_pagamento_id,
        descricao,
        id_meio_pagamento: forma.id_meio_pagamento || null,
        vezes: forma.vezes || 1,
        requer_entrada: forma.requer_entrada,
        deleted_at: null,
        created_at: forma.createdAt || forma.created_at || null,
        updated_at: forma.updatedAt || forma.updated_at || null,
        value: valor,
        paymentMode: {
          description: descricao,
          id: forma.id_meio_pagamento || null,
          name: descricao,
        },
        range: pagamento.parcelas?.length
          ? pagamento.parcelas.map((parcela) => ({
              value: Number(parcela.value), date: parcela.date,
            }))
          : [{ value: valor, date: this.todayDate() }],
      };
    },
    buildObservacaoNfce() {
      return [
        this.informacoesComplementaresNfce,
        `Checkout restaurante - atendimento #${this.atendimentoId}`,
        this.valorTaxaServico > 0
          ? `Taxa de serviço: ${this.formatMoney(this.valorTaxaServico)}`
          : null,
        this.valorCouvertTotal > 0
          ? `Couvert: ${this.formatMoney(this.valorCouvertTotal)}`
          : null,
      ]
        .filter(Boolean)
        .join("\n");
    },
    async imprimirDocumentosNfce(impressao) {
      if (!impressao) {
        return;
      }

      const envio = await this.enviarPdfPdvCompartilhado(
        impressao["danfe-url"], "nfce_restaurante", this.nfeGerada?.id || this.atendimentoId
      );
      if (envio.motivo === "indisponivel") {
        this.$toast.warning(`Impressora indisponível (${envio.status}). Abra o DANFE no navegador.`);
      } else if (envio.motivo === "nao_encontrada") {
        this.$toast.warning("Impressora configurada não encontrada. Abra o DANFE no navegador.");
      } else if (!envio.enviado && envio.motivo !== "sem_impressora") {
        this.$toast.warning("Envio do DANFE não confirmado. Verifique a fila antes de imprimir manualmente.");
      }
      for (const key of ["danfe-url", "voucher-url", "duplicata"]) {
        if (key === "danfe-url" && envio.enviado) continue;
        if (impressao[key]) {
          await this.imprimirDocumentoPdv(impressao[key]);
        }
      }
    },
    getNotaId(nota) {
      return nota?.id || nota?.nfe?.id || nota?.nota?.id || nota?.data?.id;
    },
    ensureFormaPagamentoPadrao() {
      const atual = this.formasPagamento.find(
        (forma) => Number(forma.id) === Number(this.pagamentoForm.forma_pagamento_id)
      );

      if (atual && this.destinoFinanceiroForma(atual) !== "invalido") return;
      const padrao = this.formasPagamento.find((forma) =>
        [true, 1, "1", "t", "true"].includes(forma.default) &&
        this.destinoFinanceiroForma(forma) === "caixa");
      this.pagamentoForm.forma_pagamento_id = padrao?.id || null;
    },
    async imprimirDocumentoPdv(url) {
      if (!url) return false;
      const documento = new URL(url, window.location.href);
      if (!["http:", "https:"].includes(documento.protocol)) {
        throw new Error("Endereço do PDF inválido.");
      }
      if (window.open(documento.href, "_blank")) return true;
      const escolha = await this.$swal.fire({
        icon: "warning",
        title: "O navegador bloqueou o PDF.",
        text: "O documento está pronto para impressão manual.",
        confirmButtonText: "Abrir PDF",
        showCancelButton: true,
        cancelButtonText: "Fechar",
      });
      if (escolha.isConfirmed && !window.open(documento.href, "_blank")) {
        this.$toast.warning("Permita pop-ups deste site e abra o PDF novamente.");
      }
      return false;
    },
    async imprimirComprovantePreVenda(impressao) {
      if (!impressao) {
        return;
      }

      const envio = await this.enviarPdfPdvCompartilhado(
        impressao["venda-url"], "prevenda_restaurante", this.pagamentoFechamento?.id || this.atendimentoId
      );
      if (envio.motivo === "indisponivel") {
        this.$toast.warning(`Impressora indisponível (${envio.status}). Abra a Pré-venda no navegador.`);
      } else if (envio.motivo === "nao_encontrada") {
        this.$toast.warning("Impressora configurada não encontrada. Abra a Pré-venda no navegador.");
      } else if (!envio.enviado && envio.motivo !== "sem_impressora") {
        this.$toast.warning("Envio da Pré-venda não confirmado. Verifique a fila antes de imprimir manualmente.");
      }

      for (const key of [
        "venda-url",
        "comprovante-url",
        "url_comprovante",
        "url",
        "voucher-url",
        "duplicata",
      ]) {
        if (key === "venda-url" && envio.enviado) {
          continue;
        }
        if (impressao[key]) {
          await this.imprimirDocumentoPdv(impressao[key]);
        }
      }
    },
    openAjusteItemModal(linha) {
      const item = linha.item;
      this.ajusteItemModal = {
        atendimento_item_id: linha.atendimento_item_id,
        produto: this.produtoDescricao(item),
        imagem: item.produto?.imagem || "",
        unidade: item.produto?.unidade_medida?.nome || "",
        acrescimo: {
          tipo: Number(linha.ajuste_acrescimo_tipo ||
            (linha.ajuste_operacao === "acrescimo" ? linha.ajuste_tipo : 1) || 1),
          valor: this.formatarValorAjusteItem(linha.ajuste_acrescimo_valor ??
            (linha.ajuste_operacao === "acrescimo" ? linha.ajuste_valor : 0)),
        },
        desconto: {
          tipo: Number(linha.ajuste_desconto_tipo ||
            (linha.ajuste_operacao === "desconto" ? linha.ajuste_tipo : 1) || 1),
          valor: this.formatarValorAjusteItem(linha.ajuste_desconto_valor ??
            (linha.ajuste_operacao === "desconto" ? linha.ajuste_valor : 0)),
        },
        precoAtual: this.valorUnitarioBaseCobranca(item),
      };
      this.$bvModal.show("modal-ajuste-item-restaurante");
    },
    focusAjusteItemInput() {
      this.$nextTick(() => {
        const ref = this.$refs.ajusteItemInput_acrescimo;
        const input = Array.isArray(ref) ? ref[0] : ref;
        const inputEl = input?.$el || input;

        if (inputEl) {
          inputEl.focus();
          inputEl.select();
        }
      });
    },
    parseValorAjusteItem(value) {
      if (typeof value === "number") {
        return value;
      }

      const valueString = String(value || "0")
        .replace("R$", "")
        .replace("%", "")
        .trim();

      if (!valueString) {
        return 0;
      }

      const normalized = valueString.includes(",")
        ? valueString.replace(/\./g, "").replace(",", ".")
        : valueString;

      return Number(normalized);
    },
    formatarValorAjusteItem(value) {
      const numero = Number(value || 0);
      return Number.isFinite(numero)
        ? new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(numero)
        : String(value || "");
    },
    formatarEntradaAjusteItem(operacao) {
      const ajuste = this.ajusteItemModal[operacao];
      const valor = this.parseValorAjusteItem(ajuste.valor);
      if (Number.isFinite(valor) && valor >= 0) {
        ajuste.valor = this.formatarValorAjusteItem(valor);
      }
    },
    calcularValorAjusteItem(operacao) {
      const precoBase = Number(this.ajusteItemModal.precoAtual || 0);
      const ajuste = this.ajusteItemModal[operacao];
      const valorAjuste = this.parseValorAjusteItem(ajuste.valor);

      if (!Number.isFinite(precoBase) || precoBase <= 0) {
        return 0;
      }

      if (!Number.isFinite(valorAjuste) || valorAjuste <= 0) {
        return 0;
      }

      return ajuste.tipo === 2
        ? Math.round(precoBase * (valorAjuste / 100) * 1000000) / 1000000
        : valorAjuste;
    },
    aplicarAjusteItem() {
      const linha = this.cobrancaItens.find(
        (item) =>
          item.atendimento_item_id === this.ajusteItemModal.atendimento_item_id
      );

      if (!linha) {
        return;
      }

      const acrescimo = this.parseValorAjusteItem(this.ajusteItemModal.acrescimo.valor);
      const desconto = this.parseValorAjusteItem(this.ajusteItemModal.desconto.valor);
      if (![acrescimo, desconto].every((valor) => Number.isFinite(valor) && valor >= 0)) {
        this.$toast.warning("Informe valores de ajuste válidos.");
        return;
      }
      if (this.ajusteItemModal.desconto.tipo === 2 && desconto > 100) {
        this.$toast.warning("O desconto percentual não pode passar de 100%.");
        return;
      }
      if (this.calcularValorAjusteItem("desconto") > Number(this.ajusteItemModal.precoAtual || 0)) {
        this.$toast.warning(
          "O desconto em reais não pode ser maior que o preço unitário."
        );
        return;
      }
      if (this.precoUnitarioComAjusteItem <= 0) {
        this.$toast.warning("O valor unitário ajustado deve ser maior que zero.");
        return;
      }
      linha.ajuste_operacao = null;
      linha.ajuste_tipo = 1;
      linha.ajuste_valor = 0;
      linha.ajuste_acrescimo_tipo = this.ajusteItemModal.acrescimo.tipo;
      linha.ajuste_acrescimo_valor = acrescimo;
      linha.ajuste_desconto_tipo = this.ajusteItemModal.desconto.tipo;
      linha.ajuste_desconto_valor = desconto;
      delete linha.valor_total_salvo;
      this.resetEstadoCobranca();
      this.$bvModal.hide("modal-ajuste-item-restaurante");
      this.$toast.success("Ajuste aplicado no item.");
    },
    valorUnitarioBaseCobranca(item) {
      const quantidadePendente = Number(item.quantidade_pendente || 0);

      if (quantidadePendente > 0) {
        return Number(item.valor_pendente || 0) / quantidadePendente;
      }

      return Number(item.valor_unitario || 0);
    },
    valorLinhaCobranca(linha) {
      if (this.rascunho?.id && linha.valor_total_salvo != null &&
        Number.isFinite(Number(linha.valor_total_salvo))) {
        return Number(linha.valor_total_salvo);
      }
      const item = linha.item;
      const quantidade = Number(linha.quantidade || 0);
      const valorUnitario = this.valorUnitarioBaseCobranca(item);
      const valorFinal = valorUnitario +
        this.valorAjusteLinha(linha, "acrescimo", valorUnitario) -
        this.valorAjusteLinha(linha, "desconto", valorUnitario);
      return this.roundCurrency(Math.max(valorFinal, 0) * quantidade);
    },
    valorAjusteLinha(linha, operacao, valorUnitario = this.valorUnitarioBaseCobranca(linha.item)) {
      const valorAjuste = Number(linha[`ajuste_${operacao}_valor`] ??
        (linha.ajuste_operacao === operacao ? linha.ajuste_valor : 0));
      const tipo = Number(linha[`ajuste_${operacao}_tipo`] ||
        (linha.ajuste_operacao === operacao ? linha.ajuste_tipo : 1) || 1);
      if (valorAjuste <= 0) {
        return 0;
      }
      return tipo === 2
        ? Math.round(valorUnitario * (valorAjuste / 100) * 1000000) / 1000000
        : valorAjuste;
    },
    ajusteItemLabel(linha, operacao) {
      const tipo = Number(linha[`ajuste_${operacao}_tipo`] ||
        (linha.ajuste_operacao === operacao ? linha.ajuste_tipo : 1) || 1);
      const valor = Number(linha[`ajuste_${operacao}_valor`] ??
        (linha.ajuste_operacao === operacao ? linha.ajuste_valor : 0));
      return `${operacao === "acrescimo" ? "Acréscimo" : "Desconto"} ${tipo === 2
        ? `${this.formatQuantity(valor)}%` : this.formatMoney(valor)}`;
    },
    idempotencyKey() {
      // O token nasce uma vez por tentativa e permanece estável nas
      // retentativas. Ao alterar a cobrança ou concluir um pagamento ele é
      // renovado, permitindo novos fechamentos parciais no mesmo atendimento.
      if (!this.cobrancaIdempotencyToken) {
        this.cobrancaIdempotencyToken = this.criarTokenIdempotencia("cobranca");
      }

      return [
        "restaurante-cobranca",
        this.atendimentoId,
        this.form.escopo,
        this.form.comanda_id || "mesa",
        this.cobrancaIdempotencyToken,
      ]
        .join("-")
        .slice(0, 120);
    },
    criarTokenIdempotencia(prefix) {
      return [
        prefix,
        Date.now().toString(36),
        Math.random().toString(36).slice(2, 10),
      ].join("-");
    },
    applyBackendData(error) {
      const data = error?.response?.data?.data;

      if (data && data.atendimento) {
        this.checkout = data;
      }
    },
    handleError(error, fallback, { ocultarDetalhes = false } = {}) {
      if (ocultarDetalhes && Number(error?.response?.status) >= 500) {
        this.$toast.error(fallback);
        return;
      }
      const message =
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.message ||
        fallback;
      this.$toast.error(message);
    },
    voltarMesas() {
      this.$router.push({ name: "restauranteCaixa" });
    },
    produtoDescricao(item) {
      return item.produto
        ? item.produto.descricao
        : `Produto #${item.produto_id}`;
    },
    comandaLabel(comanda) {
      if (!comanda) {
        return "Sem comanda";
      }

      if (comanda.tipo === "avulso" || (!comanda.nome && comanda.codigo)) {
        return `Avulsa ${comanda.codigo || comanda.id}`;
      }

      return comanda.nome || `Comanda ${comanda.numero || comanda.id}`;
    },
    comandaCardLabel(comanda) {
      return this.comandaLabel(comanda).replace(/^Avulsa(?=\s|$)/, "Comanda");
    },
    itemComandaOrigem(item) {
      return item.comanda ? `Origem: ${this.comandaLabel(item.comanda)}` : "-";
    },
    numeroCobrancaHistorico(index) {
      return 1 + this.cobrancasDivisao
        .slice(index + 1)
        .reduce(
          (total, fechamento) =>
            total + (this.fechamentoPorValor(fechamento)
              ? (fechamento.distribuicao_financeira || []).length
              : 1),
          0
        );
    },
    fechamentoPorValor(fechamento) {
      return (
        fechamento.modo_divisao === "valor" ||
        (Array.isArray(fechamento.distribuicao_financeira) &&
          fechamento.distribuicao_financeira.length > 0)
      );
    },
    totalDistribuidoFechamento(fechamento) {
      return (fechamento.distribuicao_financeira || []).reduce(
        (total, valor) => total + Number(valor || 0), 0
      );
    },
    restanteFechamentoHistorico(fechamento) {
      return Math.max(
        0,
        Math.round(
          (Number(fechamento.valor_referencia || fechamento.valor_total || 0) -
            this.totalDistribuidoFechamento(fechamento)) * 100
        ) / 100
      );
    },
    statusFechamentoLabel(fechamento) {
      if (fechamento.status === "convertido" && fechamento.pre_venda_id) {
        return "Pré-venda";
      }
      return {
        rascunho: "Rascunho",
        preview: "Em aberto",
        aguardando_conversao: "Preparando documento",
        convertido: "Documento gerado",
        aguardando_pagamento: "Aguardando pagamento",
        pago: "Pago",
        cancelado: "Cancelado",
        erro: "Erro",
      }[fechamento.status] || fechamento.status;
    },
    statusFechamentoClasse(fechamento) {
      return {
        rascunho: "is-draft",
        preview: "is-open",
        aguardando_conversao: "is-document",
        convertido: "is-document",
        aguardando_pagamento: "is-document",
        pago: "is-paid",
        cancelado: "is-cancelled",
        erro: "is-error",
      }[fechamento.status] || "is-draft";
    },
    fechamentoLabel(fechamento) {
      if (fechamento.escopo === "comanda") {
        return fechamento.comanda
          ? this.comandaLabel(fechamento.comanda)
          : "Comanda";
      }

      return "Mesa inteira";
    },
    statusCobrancaItemLabel(item) {
      if (item.cancelado) {
        return "Cancelado";
      }

      if (item.cobravel === false) {
        return "Sem valor";
      }

      const total = Number(item.quantidade_total || item.quantidade || 0);
      const cobrado = Number(item.quantidade_ja_cobrada || 0);
      const reservado = Number(item.quantidade_reservada || 0);
      const pendente = Number(item.quantidade_pendente || 0);

      if (total > 0 && cobrado >= total) {
        return "Cobrado";
      }

      if (reservado > 0 && pendente <= 0) {
        return "Reservado";
      }

      if (cobrado > 0 || reservado > 0) {
        return "Parcial";
      }

      return "Pendente";
    },
    statusCobrancaItemClass(item) {
      const label = this.statusCobrancaItemLabel(item);
      const classes = {
        Cancelado: "label label-lg label-inline label-light-danger",
        Cobrado: "label label-lg label-inline label-light-success",
        Reservado: "label label-lg label-inline label-light-warning",
        Parcial: "label label-lg label-inline label-light-primary",
        Pendente: "label label-lg label-inline label-light-dark",
        "Sem valor": "label label-lg label-inline label-light-info",
      };

      return classes[label] || "label label-lg label-inline label-light";
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatarValorFinanceiro(value) {
      return value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
        ? "—" : this.formatMoney(value);
    },
    formatQuantity(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
    },
    pickNumber(source, keys) {
      const found = keys
        .map((key) => source?.[key])
        .find((value) => value !== undefined && value !== null && value !== "");

      if (found === undefined) {
        return null;
      }

      const number = Number(found);
      return Number.isFinite(number) ? number : null;
    },
    roundCurrency(value) {
      const number = Number(value || 0);
      return Number.isFinite(number) ? Number(number.toFixed(2)) : 0;
    },
    todayDate() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
  },
};
</script>

<style>
@media (min-width: 992px) and (min-height: 650px) {
  html:has(.restaurante-checkout:not(.is-fullscreen)) {
    overflow-y: hidden;
  }

  body:has(.restaurante-checkout:not(.is-fullscreen)) > .flex-root {
    height: 100dvh;
    min-height: 0;
    overflow: hidden;
  }

  body:has(.restaurante-checkout:not(.is-fullscreen)) > .flex-root > .flex-root,
  body:has(.restaurante-checkout:not(.is-fullscreen)) .page,
  body:has(.restaurante-checkout:not(.is-fullscreen)) #kt_wrapper,
  body:has(.restaurante-checkout:not(.is-fullscreen)) #kt_content,
  body:has(.restaurante-checkout:not(.is-fullscreen)) #kt_content > .flex-column-fluid,
  body:has(.restaurante-checkout:not(.is-fullscreen)) #kt_content > .flex-column-fluid > .container,
  body:has(.restaurante-checkout:not(.is-fullscreen)) #kt_content > .flex-column-fluid > .container > .d-lg-flex,
  body:has(.restaurante-checkout:not(.is-fullscreen)) .content-wrapper {
    min-height: 0;
  }

  body:has(.restaurante-checkout:not(.is-fullscreen)) > .flex-root > .flex-root,
  body:has(.restaurante-checkout:not(.is-fullscreen)) .page,
  body:has(.restaurante-checkout:not(.is-fullscreen)) #kt_wrapper,
  body:has(.restaurante-checkout:not(.is-fullscreen)) #kt_content,
  body:has(.restaurante-checkout:not(.is-fullscreen)) #kt_content > .flex-column-fluid {
    flex: 1 1 auto;
  }

  body:has(.restaurante-checkout:not(.is-fullscreen)) #kt_content {
    background: #eef1f6;
    padding-bottom: 0;
    overflow-x: hidden;
    overflow-y: auto;
  }

  body:has(.restaurante-checkout:not(.is-fullscreen)) #kt_content > .flex-column-fluid > .container {
    display: flex;
    flex-direction: column;
  }

  body:has(.restaurante-checkout:not(.is-fullscreen)) #kt_content > .flex-column-fluid > .container > .d-lg-flex,
  body:has(.restaurante-checkout:not(.is-fullscreen)) .content-wrapper > .restaurante-checkout {
    flex: 1 1 auto;
  }

  body:has(.restaurante-checkout:not(.is-fullscreen)) .content-wrapper {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  body:has(.restaurante-checkout:not(.is-fullscreen)) #kt_footer {
    display: none !important;
  }
}
</style>

<style scoped>
.restaurante-checkout {
  background: #eef1f6;
  min-width: 0;
  padding: 16px;
}

.checkout-page {
  margin-inline: auto;
  width: min(94vw, 1900px, 100%);
}

.restaurante-checkout.is-fullscreen .checkout-page {
  width: min(97vw, calc(100vw - 48px), 100%);
}

.checkout-page-content {
  min-height: 420px;
  min-width: 0;
}

.restaurante-checkout.is-fullscreen {
  background: #eef1f6;
  height: 100vh;
  height: 100dvh;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 16px;
}

.restaurante-checkout.is-fullscreen .checkout-page-content {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 32px);
  min-height: calc(100dvh - 32px);
}

.restaurante-checkout.is-fullscreen .checkout-layout {
  margin-block: auto;
}

.checkout-header-context { color: #69718b; font-weight: 600; }
.checkout-main-card { padding: 10px 20px; }
.checkout-main-card .checkout-title h3 { margin-bottom: 2px; }
.checkout-main-card .checkout-title { margin-bottom: 0; }
.checkout-billing-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; margin-bottom: 14px; }
.checkout-billing-fields > * { min-width: 0; margin-bottom: 0; }
.checkout-billing-fields .form-control { min-height: 40px; }
.checkout-billing-panel::v-deep label {
  color: #3f4254;
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  margin-bottom: 6px;
  text-transform: none;
}
@media (min-width: 992px) and (max-width: 1439.98px) {
  .checkout-billing-fields::v-deep label { min-height: 2.5rem; }
}
.checkout-products-table-wrap { overflow-x: auto; margin-top: 16px; }
.checkout-products-table { border-collapse: collapse; width: 100%; }
.checkout-products-table th, .checkout-products-table td { border-bottom: 1px solid #ebedf3; padding: 9px 10px; }
.checkout-products-table th { background: #f7f9fc; color: #69718b; font-size: .75rem; text-transform: uppercase; }
.checkout-products-table thead th { position: sticky; top: 0; z-index: 1; }
.checkout-products-table th:first-child, .checkout-products-table td:first-child { text-align: left; width: 52%; }
.checkout-products-table th:not(:first-child), .checkout-products-table td:not(:first-child) { text-align: right; white-space: nowrap; }
.checkout-products-table td:first-child { overflow-wrap: anywhere; }
.checkout-products-table td strong { color: #181c32; }
.checkout-products-empty { color: #7e8299; text-align: center !important; }

.checkout-layout {
  align-items: flex-start;
  margin-left: 0;
  margin-right: 0;
  row-gap: 20px;
}

@media (min-width: 992px) {
  .checkout-layout {
    display: grid;
    align-items: stretch;
    column-gap: 20px;
    grid-template-columns: minmax(0, 13fr) minmax(0, 7fr);
  }

  .checkout-layout > .checkout-left-column,
  .checkout-layout > .checkout-right-column {
    max-width: none;
    min-width: 0;
    padding-left: 0;
    padding-right: 0;
    width: auto;
  }

  .checkout-left-column {
    contain: size;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .checkout-left-column > .checkout-selection-card {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    min-height: 0;
  }

  .checkout-selection-card > .checkout-products-table-wrap {
    flex: 1 1 0;
    min-height: 7rem;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .checkout-left-column > .checkout-history-card {
    display: flex;
    flex: 0 1 auto;
    flex-direction: column;
    max-height: 40%;
    min-height: 0;
  }

  .checkout-history-card > .cobrancas-list {
    min-height: 6rem;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

}

.checkout-title {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 18px;
}

.checkout-fullscreen-button {
  align-items: center;
  background: #f3f6f9;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  color: #3f4254;
  display: inline-flex;
  flex: 0 0 38px;
  height: 38px;
  justify-content: center;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  width: 38px;
}

.checkout-fullscreen-button:hover,
.checkout-fullscreen-button:focus-visible {
  background: #e8fff3;
  border-color: #1bc5bd;
  color: #0bb783;
}

.checkout-title h3 {
  color: #181c32;
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0;
}

.checkout-actions {
  align-items: center;
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.checkout-actions .btn {
  border-radius: 6px;
  min-height: 38px;
}

.empty-state,
.blocked-state {
  background: #f3f6f9;
  border-radius: 6px;
  color: #7e8299;
  font-weight: 600;
  padding: 24px;
  text-align: center;
}

.blocked-state {
  background: #fff4de;
  color: #895c00;
}

.checkout-summary {
  display: grid;
  margin-bottom: 18px;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.checkout-summary span,
.totals-box span {
  color: #7e8299;
  display: block;
  font-weight: 600;
  margin-bottom: 4px;
}

.checkout-summary strong {
  color: #181c32;
  display: block;
  font-size: 1.05rem;
  overflow-wrap: anywhere;
}

.checkout-summary > div {
  background: #f7f9fc;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  min-height: 74px;
  min-width: 0;
  padding: 12px 14px;
}

.checkout-card,
.checkout-section,
.checkout-panel {
  background: #fff;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  min-width: 0;
  padding: 20px;
}

.checkout-summary-panel {
  min-height: 0;
}

.checkout-summary-panel .checkout-action-grid {
  margin-top: 14px !important;
  padding-top: 0;
}

.checkout-panel-title h4,
.checkout-summary-panel .section-title h4 {
  align-items: center;
  display: flex;
  gap: 9px;
}

.checkout-panel-title h4 .material-design-icon,
.checkout-summary-panel .section-title h4 .material-design-icon {
  color: #69718b;
  font-size: 1.2rem;
}

.checkout-attendant-field {
  margin-bottom: 0;
  min-width: 0;
}

.checkout-attendant-field label span {
  color: #f64e60;
}

.checkout-attendant-select::v-deep .vs__dropdown-toggle {
  border: 1px solid #d9dce5;
  border-radius: 6px;
  min-height: 40px;
}

.checkout-attendant-select::v-deep .vs__selected {
  color: #181c32;
  max-width: calc(100% - 36px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.checkout-attendant-select::v-deep .vs__search {
  min-width: 0;
}

.checkout-attendant-select.vs--open::v-deep .vs__dropdown-toggle,
.checkout-attendant-select:focus-within::v-deep .vs__dropdown-toggle {
  border-color: #3699ff;
  box-shadow: 0 0 0 2px rgba(54, 153, 255, 0.12);
}

.checkout-adjustment-value {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}
.checkout-adjustments {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.checkout-adjustment-input {
  border: 1px solid #d9dce5;
  border-radius: 6px;
  display: flex;
  min-height: 40px;
  min-width: 0;
  overflow: hidden;
}

.checkout-adjustment-input:focus-within {
  border-color: #3699ff;
  box-shadow: 0 0 0 2px rgba(54, 153, 255, 0.12);
}

.checkout-adjustment-input.invalid {
  border-color: #f64e60;
}

.checkout-adjustment-input .form-control,
.checkout-adjustment-input .form-control:focus {
  border: 0;
  border-radius: 0;
  box-shadow: none;
  font-size: 0.875rem;
  height: 38px;
  min-width: 0;
  padding: 0 12px;
  text-align: right;
}

.checkout-adjustment-unit {
  display: flex;
  flex: 0 0 auto;
}

.checkout-adjustment-unit button,
.checkout-adjustment-fixed {
  align-items: center;
  background: #f3f6f9;
  border: 0;
  border-right: 1px solid #d9dce5;
  color: #69718b;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 600;
  justify-content: center;
  min-width: 36px;
  padding: 0 6px;
}

.checkout-adjustment-unit button + button {
  border-left: 0;
}

.checkout-adjustment-unit button:first-child {
  border-radius: 5px 0 0 5px;
}

.checkout-adjustment-unit button.active {
  background: #e9f2ff;
  color: #1b4d91;
  font-weight: 800;
}

.checkout-adjustment-unit button:not(:disabled) {
  cursor: pointer;
}

.checkout-adjustment-unit button:not(:disabled):hover {
  background: #e7eef8;
}

.checkout-adjustment-unit button.active:not(:disabled):hover {
  background: #dfebff;
}

.checkout-adjustment-unit button:focus-visible {
  outline: 2px solid #3699ff;
  outline-offset: -2px;
  z-index: 1;
}

.checkout-action-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.checkout-action-grid > button {
  margin: 0 !important;
  min-height: 38px;
  min-width: 0;
  width: 100%;
}
.checkout-action-grid > button {
  align-items: center;
  display: inline-flex;
  gap: 7px;
  justify-content: center;
  min-height: 42px;
  white-space: normal;
}

.checkout-action-grid .material-design-icon {
  flex: 0 0 auto;
  font-size: 1.1rem;
}

.checkout-action-secondary {
  background: #eaf3ff;
  border-color: #eaf3ff;
  color: #174b91;
}

.checkout-action-presale {
  background: #f46f60;
  border-color: #f46f60;
  color: #fff;
}

.checkout-action-nfce {
  background: #009b83;
  border-color: #009b83;
  color: #fff;
}

.checkout-action-presale:not(:disabled):hover {
  background: #de5e50;
  border-color: #de5e50;
}

.checkout-action-nfce:not(:disabled):hover {
  background: #008570;
  border-color: #008570;
  color: #fff;
}


.checkout-tables-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.itens-pendentes-scroll {
  max-height: 330px;
  overflow-y: auto;
}

.cobranca-itens-scroll {
  max-height: 330px;
  overflow-y: auto;
}

.itens-pendentes-scroll thead th,
.cobranca-itens-scroll thead th {
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 1;
}

.table-compact th,
.table-compact td {
  padding-left: 10px;
  padding-right: 10px;
}

.cobranca-quantidade-cell {
  min-width: 92px;
  width: 92px;
}

.cobranca-acoes-cell {
  min-width: 92px;
  width: 92px;
}

.item-action-buttons {
  align-items: center;
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
  white-space: nowrap;
}

.item-action-buttons .btn {
  flex: 0 0 auto;
  height: 34px;
  width: 34px;
}

.item-action-buttons .mr-2 {
  margin-right: 0 !important;
}

.section-title {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-title.compact {
  margin-bottom: 10px;
}

.section-title h4 {
  color: #181c32;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
}

.section-title span {
  color: #7e8299;
  font-weight: 600;
}

.checkout-comandas-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.checkout-comanda {
  background: #f7f9fc;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  color: #69718b;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 38px;
  min-width: 0;
  padding: 7px 12px;
  text-align: left;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.checkout-comanda:hover:not(:disabled),
.checkout-comanda:focus-visible {
  background: #eef6ff;
  border-color: #8cc4ff;
}

.checkout-comanda:focus-visible {
  outline: 2px solid #3699ff;
  outline-offset: 2px;
}

.checkout-comanda.active,
.checkout-comanda.active:hover:not(:disabled) {
  background: #fff2ef;
  border-color: #ee7361;
}

.checkout-comanda-heading,
.checkout-comanda-financial {
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  justify-content: space-between;
  min-width: 0;
  width: 100%;
}

.checkout-comanda-heading strong {
  color: #181c32;
  font-size: 1rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.checkout-comanda-selected {
  background: #d4f8e9;
  border-radius: 4px;
  color: #008f7c;
  flex: 0 0 auto;
  font-size: 0.67rem;
  font-weight: 700;
  padding: 2px 5px;
}

.checkout-comanda-context,
.checkout-comanda-extra {
  color: #7e8299;
  font-size: 0.72rem;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.checkout-comanda-financial {
  margin-top: auto;
}

.checkout-comanda-financial strong {
  color: #25304b;
  font-size: 1.125rem;
  font-weight: 800;
  white-space: nowrap;
}
.muted {
  opacity: 0.62;
}

.cobrancas-empty {
  align-items: center;
  background: #f7f9fc;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  color: #7e8299;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 18px 12px;
  text-align: center;
}

.cobrancas-empty strong {
  color: #3f4254;
}

.cobrancas-list {
  display: grid;
  gap: 8px;
}

.cobrancas-list:focus-visible {
  outline: 2px solid #67a9ef;
  outline-offset: 2px;
}

.cobranca-record {
  display: grid;
  gap: 12px;
  min-width: 0;
}

.cobranca-record + .cobranca-record {
  border-top: 1px solid #ebedf3;
  padding-top: 14px;
}

.cobranca-division-heading,
.cobranca-line {
  align-items: start;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) max-content;
  min-width: 0;
}

.cobranca-division-heading {
  align-items: center;
  padding: 2px;
}

.cobranca-division-label {
  color: #3f4254;
  font-size: 0.9rem;
  font-weight: 700;
}

.cobranca-line {
  background: #fff;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  min-height: 88px;
  padding: 14px 16px;
}

.cobranca-financial-cards {
  display: grid;
  gap: 12px;
}

.cobranca-line-content {
  min-width: 0;
}

.cobranca-line-heading {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cobranca-line-heading > strong:first-child {
  color: #181c32;
  font-size: 1rem;
  font-weight: 700;
}

.cobranca-line-side {
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.cobranca-line-value {
  color: #181c32;
  font-size: 1.2rem;
  font-weight: 800;
  line-height: 1.4;
  white-space: nowrap;
}

.cobranca-line-meta {
  align-items: center;
  color: #7e8299;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.85rem;
  font-weight: 600;
  gap: 6px 12px;
  margin-top: 6px;
}

.cobranca-status-badge {
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.2;
  padding: 5px 9px;
}

.cobranca-status-badge.is-draft {
  background: #edf2f7;
  color: #52637a;
}

.cobranca-status-badge.is-open {
  background: #eaf3ff;
  color: #2865a4;
}

.cobranca-status-badge.is-document {
  background: #f1effc;
  color: #6255a0;
}

.cobranca-status-badge.is-paid {
  background: #e8f7ef;
  color: #24774f;
}

.cobranca-status-badge.is-cancelled {
  background: #fff0ec;
  color: #a65d4f;
}

.cobranca-status-badge.is-error {
  background: #fdebed;
  color: #ad3647;
}

.cobranca-financial-summary {
  color: #7e8299;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.82rem;
  font-weight: 600;
  gap: 4px 16px;
  padding: 2px;
}

.cobranca-line-actions {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  justify-content: flex-end;
}

.cobranca-line-actions .btn {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  min-height: 32px;
  white-space: nowrap;
}

@media (max-width: 767.98px) {
  .cobranca-division-heading,
  .cobranca-line {
    grid-template-columns: minmax(0, 1fr);
  }

  .cobranca-line {
    min-height: 0;
  }

  .cobranca-line-actions {
    flex-wrap: wrap;
  }
}

.empty-mini {
  background: #f3f6f9;
  border-radius: 6px;
  color: #7e8299;
  font-weight: 600;
  padding: 14px;
  text-align: center;
}

.ajuste-chip {
  border-radius: 4px;
  display: inline-block;
  margin-top: 4px;
  padding: 2px 6px;
}

.ajuste-chip-desconto {
  background: #e8fff3;
  color: #0bb783 !important;
}

.ajuste-chip-acrescimo {
  background: #fff4de;
  color: #ffa800 !important;
}

.totals-box {
  border-top: 1px solid #ebedf3;
  margin-top: 18px;
  padding-top: 14px;
}

.checkout-summary-panel .totals-box {
  border-top: 0;
  margin-top: 0;
  padding-top: 0;
}

.checkout-summary-panel .totals-box span {
  color: #69718b;
  margin-bottom: 0;
}

.totals-section {
  border-bottom: 1px dashed #e4e9f1;
  padding: 0 0 8px;
}

.totals-section + .totals-section {
  padding-top: 9px;
}

.totals-section > div,
.total-main {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-width: 0;
}

.totals-section > div {
  margin-bottom: 5px;
}

.totals-section > div:last-child {
  margin-bottom: 0;
}

.totals-section strong {
  color: #25304b;
  flex: 0 0 auto;
  font-weight: 600;
  text-align: right;
}

.checkout-financial-note {
  color: #69718b;
  font-size: 0.76rem;
  line-height: 1.35;
  margin: 8px 0 0;
}

.totals-section .total-pending strong,
.totals-composition > div:first-child strong {
  font-weight: 800;
}

.total-main {
  background: #e7f8f4;
  border-radius: 6px;
  color: #008f7c;
  margin-top: 12px;
  padding: 11px 12px;
}

.checkout-summary-panel .total-main span,
.total-main strong {
  color: #008f7c;
  font-weight: 800;
}

.total-main strong {
  font-size: 1.5rem;
  line-height: 1;
  white-space: nowrap;
}

.restaurante-payment-add-button {
  min-height: 40px;
  width: 100%;
}

.restaurante-payment-field-label {
  color: #181c32;
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.restaurante-payment-shortcuts {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.restaurante-payment-shortcuts__item {
  background: #f5f7fa;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  color: #5e6278;
  font-weight: 600;
  min-height: 38px;
  padding: 6px 10px;
  text-align: center;
}

.restaurante-payment-shortcuts__item:hover,
.restaurante-payment-shortcuts__item:focus-visible {
  background: #fff6f4;
  border-color: #ee7361;
}

.restaurante-payment-shortcuts__item strong {
  color: #c75445;
  margin-right: 6px;
}

.restaurante-payment-list {
  border-top: 1px solid #ebedf3;
  max-height: min(30vh, 240px);
  overflow-y: auto;
  padding-top: 14px;
}

.restaurante-payment-section-title {
  color: #181c32;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 7px;
}

.restaurante-payment-item {
  align-items: center;
  background: #f8f9fb;
  border: 1px solid #edf0f4;
  border-radius: 6px;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) auto 34px;
  margin-bottom: 6px;
  padding: 8px 10px;
}

.restaurante-payment-item__description {
  min-width: 0;
}

.restaurante-payment-item__description > strong {
  color: #181c32;
  display: block;
  overflow-wrap: anywhere;
}

.restaurante-payment-item__value {
  color: #181c32;
  font-size: 1rem;
  white-space: nowrap;
}

.restaurante-payment-item__edit {
  color: #315e8e;
  font-weight: 600;
  padding: 2px 4px;
  white-space: nowrap;
}

.restaurante-payment-item__remove {
  background: transparent;
  color: #c75445;
  height: 30px;
  width: 30px;
}

.restaurante-payment-item.has-installments {
  grid-template-columns: minmax(0, 1fr) auto auto 34px;
}

@media (max-width: 575.98px) {
  .restaurante-payment-item.has-installments {
    gap: 5px;
    grid-template-columns: minmax(0, 1fr) auto 34px;
  }
  .restaurante-payment-item.has-installments > .btn-link {
    grid-column: 1 / -1;
    grid-row: 2;
    justify-self: start;
    padding: 0;
  }
}

.restaurante-receber {
  background: #fffaf8;
  border-top: 1px solid #f1e2dc;
  padding: 10px 12px;
}

.restaurante-receber-cliente {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.restaurante-receber-cliente.is-found strong {
  color: #008c80;
}

.restaurante-receber-busca {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.restaurante-receber-busca .form-control {
  flex: 1 1 220px;
  min-width: 0;
}

.restaurante-receber-resultados {
  background: #fff;
  border: 1px solid #e4e6ef;
  max-height: 160px;
  overflow-y: auto;
}

.restaurante-receber-resultados button {
  background: #fff;
  border: 0;
  border-bottom: 1px solid #ebedf3;
  color: #181c32;
  display: block;
  padding: 8px 12px;
  text-align: left;
  width: 100%;
}

.restaurante-receber-resultados button:hover,
.restaurante-receber-resultados button:focus {
  background: #eef5ff;
}

.restaurante-receber-grupo {
  border-top: 1px solid #e4e6ef;
  margin-top: 8px;
  padding-top: 8px;
}

.restaurante-receber-tabela-wrap {
  overflow-x: auto;
}

.restaurante-receber-tabela {
  background: #fff;
  border-collapse: collapse;
  margin-top: 5px;
  min-width: 0;
  width: 100%;
}

.restaurante-receber-tabela th,
.restaurante-receber-tabela td {
  border-bottom: 1px solid #edf0f4;
  padding: 4px 8px;
}

.restaurante-receber-tabela th {
  background: #fff;
  color: #7e8299;
  font-weight: 600;
}

.restaurante-receber-tabela th:last-child,
.restaurante-receber-tabela td:last-child {
  text-align: right;
}

.restaurante-parcelamento-info {
  display: grid;
  align-items: end;
  background: #f8f9fb;
  border-radius: 6px;
  gap: 16px;
  grid-template-columns: 1.25fr 1.2fr 0.8fr 0.9fr;
  margin-bottom: 16px;
  padding: 12px 14px;
}

.restaurante-parcelamento-info > div {
  min-width: 0;
}

.restaurante-parcelamento-info small {
  color: #7e8299;
  display: block;
}

.restaurante-parcelamento-info strong {
  color: #181c32;
  display: block;
  overflow-wrap: anywhere;
}

.restaurante-parcelamento-valor strong {
  display: block;
  font-size: 1.45rem;
  font-weight: 600;
  line-height: 1.25;
}

.restaurante-parcelamento-quantidade {
  width: 90px;
  height: 36px;
  padding: 3px 7px;
}

.restaurante-parcelamento-quantidade:focus {
  border-color: #ee7361;
  box-shadow: 0 0 0 2px rgba(238, 115, 97, 0.12);
}

.restaurante-parcelamento-tabela-wrap {
  max-height: min(44vh, 360px);
  overflow-y: auto;
  overflow-x: hidden;
}

.restaurante-parcelamento-tabela-wrap .restaurante-receber-tabela {
  margin-top: 0;
  table-layout: fixed;
}

.restaurante-parcelamento-tabela-wrap th:first-child {
  width: 76px;
}

.restaurante-parcelamento-tabela-wrap th:nth-child(2) {
  width: 42%;
}

.restaurante-parcelamento-tabela-wrap th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f8f9fb;
}

.restaurante-parcelamento-tabela-wrap th:last-child,
.restaurante-parcelamento-tabela-wrap td:last-child {
  width: 48px;
  text-align: center;
}

.restaurante-parcelamento-tabela-wrap td:nth-child(3) input {
  text-align: right;
}

.restaurante-parcelamento-tabela-wrap .form-control {
  height: 36px;
}

.restaurante-parcelamento-erro {
  color: #c44d44;
  display: block;
  font-size: 0.78rem;
  line-height: 1.25;
  margin-top: 3px;
}

.restaurante-parcelamento-excluir {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 4px;
  color: #ee7361;
  cursor: pointer;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  width: 32px;
}

.restaurante-parcelamento-excluir:hover:not(:disabled) {
  background: #fff0ed;
}

.restaurante-parcelamento-excluir:disabled {
  cursor: not-allowed;
  opacity: 0.35;
}

.restaurante-parcelamento-cliente {
  background: #f3faf7;
  color: #008c80;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.restaurante-parcelamento-cliente small {
  color: #7e8299;
  margin-left: 8px;
}

.restaurante-parcelamento-cliente button {
  border: 0;
  background: transparent;
  color: #008c80;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

.restaurante-parcelamento-modal .modal-dialog {
  max-width: calc(100vw - 16px);
  width: 780px;
}

.restaurante-parcelamento-modal .modal-content {
  max-height: calc(100dvh - 24px);
}

.restaurante-parcelamento-modal .restaurante-receber-tabela .form-control {
  min-width: 0;
}

@media (max-width: 575.98px) {
  .restaurante-parcelamento-info {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .restaurante-parcelamento-tabela-wrap th:first-child {
    width: 48px;
  }
  .restaurante-parcelamento-tabela-wrap th:nth-child(2) {
    width: 44%;
  }
  .restaurante-parcelamento-modal .restaurante-receber-tabela th,
  .restaurante-parcelamento-modal .restaurante-receber-tabela td {
    padding: 3px;
  }
  .restaurante-parcelamento-modal .restaurante-receber-tabela .form-control {
    font-size: 0.8rem;
    padding: 4px;
  }
}

.restaurante-payment-item span {
  color: #7e8299;
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
}

.checkout-distributed-payment {
  grid-template-columns: minmax(0, 1fr) minmax(0, 240px);
}

@media (max-width: 575.98px) {
  .checkout-distributed-payment {
    grid-template-columns: minmax(0, 1fr);
  }
}

.restaurante-payment-summary {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
  margin-top: 14px;
  padding: 0;
}

.restaurante-payment-summary.has-change {
  grid-template-columns: minmax(0, 1.25fr) repeat(2, minmax(0, 1fr));
}

.restaurante-payment-summary > div {
  border: 1px solid transparent;
  border-radius: 8px;
  min-width: 0;
  padding: 14px 16px;
}

.restaurante-payment-summary span {
  display: block;
  font-size: 0.88rem;
  font-weight: 600;
}

.restaurante-payment-summary strong {
  display: block;
  line-height: 1.15;
  margin-top: 6px;
  overflow-wrap: anywhere;
}

.restaurante-payment-summary > .restaurante-payment-total {
  background: #edf8f3;
  border-color: #d9eee3;
}

.restaurante-payment-total strong {
  color: #087c51;
  font-size: 1.8rem;
  font-weight: 700;
}

.restaurante-payment-total span {
  color: #316d58;
}

.restaurante-payment-summary > .restaurante-payment-remaining {
  background: #f3f6fa;
  border-color: #e7ecf3;
}

.restaurante-payment-remaining span {
  color: #5e6a80;
}

.restaurante-payment-remaining strong {
  color: #344054;
  font-size: 1.4rem;
}

.restaurante-payment-summary > .restaurante-payment-change {
  background: #fff3f1;
  border-color: #f7e1dd;
}

.restaurante-payment-change span,
.restaurante-payment-change strong {
  color: #c34e41;
}

.restaurante-payment-change strong {
  font-size: 1.4rem;
}

.cancelled-list {
  display: grid;
  gap: 10px;
}

.cancelled-item {
  align-items: center;
  background: #f3f6f9;
  border-radius: 4px;
  color: #7e8299;
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr auto auto;
  padding: 0;
}

@media (max-width: 991.98px) {
  .checkout-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .checkout-adjustments {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .itens-pendentes-scroll {
    max-height: none;
  }

  .checkout-tables-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .restaurante-payment-shortcuts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575.98px) {
  .checkout-billing-fields { grid-template-columns: minmax(0, 1fr); }
  .checkout-products-table { min-width: 520px; }
  .restaurante-checkout.is-fullscreen {
    padding: 8px;
  }

  .restaurante-checkout.is-fullscreen .checkout-page-content {
    min-height: calc(100vh - 16px);
    min-height: calc(100dvh - 16px);
  }

  .checkout-adjustments {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .checkout-panel {
    padding: 14px;
  }

  .checkout-actions {
    display: grid;
    grid-template-columns: 38px minmax(0, 1fr);
    width: 100%;
  }

  .checkout-comandas-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .checkout-summary {
    grid-template-columns: 1fr;
  }
  .restaurante-payment-shortcuts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .restaurante-payment-summary {
    grid-template-columns: minmax(0, 1fr);
  }
  .restaurante-payment-summary.has-change {
    grid-template-columns: minmax(0, 1fr);
  }
}


.checkout-division-options {
  display: grid;
  gap: 12px;
}
.checkout-division-option {
  align-items: center;
  background: #fff;
  border: 1px solid #e0e5ed;
  border-radius: 9px;
  color: #17213a;
  cursor: pointer;
  display: flex;
  gap: 18px;
  min-height: 104px;
  padding: 18px 20px;
  text-align: left;
  transition: background-color 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;
  width: 100%;
}
.checkout-division-option--items { border-color: #ffb2a8; }
.checkout-division-option--value { border-color: #a9d0ff; }
.checkout-division-option:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}
.checkout-division-option-icon {
  align-items: center;
  background: #fff1ef;
  border-radius: 8px;
  color: #ee7361;
  display: flex;
  flex: 0 0 58px;
  height: 58px;
  justify-content: center;
}
.checkout-division-option--value .checkout-division-option-icon {
  background: #eff7ff;
  color: #258cf4;
}
.checkout-division-option-chevron { color: #ee7361; flex: 0 0 auto; }
.checkout-division-option--value .checkout-division-option-chevron { color: #258cf4; }
.checkout-division-option-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  overflow-wrap: anywhere;
}
.checkout-division-option-copy strong {
  color: #17213a;
  font-size: 1.05rem;
  font-weight: 700;
}

.checkout-division-option--items:hover:not(:disabled) {
  background: #fff8f6;
  border-color: #ee7361;
  box-shadow: 0 3px 12px rgba(238, 115, 97, 0.08);
}
.checkout-division-option--value:hover:not(:disabled) {
  background: #f7fbff;
  border-color: #258cf4;
  box-shadow: 0 3px 12px rgba(37, 140, 244, 0.08);
}
.checkout-division-option:focus-visible {
  outline: 2px solid #258cf4;
  outline-offset: 2px;
}
.checkout-division-option-copy span {
  color: #7c86a0;
}
.checkout-division-modal-heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
  line-height: 1.25;
}
.checkout-division-modal-heading small {
  color: #7c86a0;
  font-size: 0.88rem;
  font-weight: 400;
}
.checkout-division-footer-back { background: #e9edf3; border-color: #e9edf3; color: #17213a; font-weight: 700; }
.checkout-division-footer-save { background: #eef1f5; border-color: #eef1f5; color: #3c4964; }
.checkout-division-footer-presale { background: #ee7361; border-color: #ee7361; color: #fff; }
.checkout-division-footer-nfce { background: #aee4d8; border-color: #aee4d8; color: #096d61; }
.checkout-division-footer {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  width: 100%;
}
.checkout-division-footer-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  margin-left: auto;
}
.checkout-division-footer .btn {
  border-radius: 7px;
  min-height: 39px;
  padding: 8px 15px;
  white-space: nowrap;
}
.checkout-division-footer .btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
@media (max-width: 767.98px) {
  .checkout-division-footer,
  .checkout-division-footer-actions {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }
  .checkout-division-footer-actions {
    margin-left: 0;
  }
}
.checkout-division-items > .checkout-section {
  display: flex;
  flex-direction: column;
  min-height: 320px;
  overflow: hidden;
  padding: 0;
}
.checkout-division-panel-header {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-height: 49px;
  padding: 11px 14px;
}
.checkout-division-panel--pending .checkout-division-panel-header {
  background: #eff7ff;
  border-bottom: 1px solid #d8eafd;
}
.checkout-division-panel--selected .checkout-division-panel-header {
  background: #fff1ef;
  border-bottom: 1px solid #ffddd8;
}
.checkout-division-panel-header h4 {
  align-items: center;
  color: #17213a;
  display: flex;
  font-size: 0.97rem;
  font-weight: 700;
  gap: 9px;
  margin: 0;
}
.checkout-division-panel--pending .checkout-division-panel-icon { color: #258cf4; }
.checkout-division-panel--selected .checkout-division-panel-icon { color: #ee7361; }
.checkout-division-panel-header > span {
  color: #52617e;
  font-size: 0.78rem;
  white-space: nowrap;
}
.checkout-division-table-wrap {
  flex: 1 1 auto;
  max-height: min(44vh, 420px);
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  padding: 0;
}
.checkout-division-items .table { margin-bottom: 0; min-width: 385px; }
.checkout-division-items .table th:first-child,
.checkout-division-items .table td:first-child { padding-left: 16px; }
.checkout-division-items .table th:last-child,
.checkout-division-items .table td:last-child { padding-right: 16px; }
.checkout-division-items .table th {
  background: #f4f6f9;
  color: #69748d;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 10px 7px;
  text-transform: uppercase;
  white-space: nowrap;
}
.checkout-division-items .table td {
  border-top-color: #e9edf3;
  padding: 10px 7px;
}
.checkout-division-items .table .btn {
  border-radius: 6px;
  padding: 6px 9px;
}
.checkout-division-add:not(:disabled) {
  background: #fff1ef;
  border-color: #fff1ef;
  color: #d85747;
}
.checkout-division-product {
  align-items: center;
  display: flex;
  gap: 9px;
  min-width: 0;
}
.checkout-division-product-image {
  border-radius: 6px;
  flex: 0 0 36px;
  height: 36px;
  object-fit: cover;
  width: 36px;
}
.checkout-division-product-placeholder {
  align-items: center;
  background: #f1f4f8;
  color: #8a96ae;
  display: flex;
  justify-content: center;
}
.checkout-division-product-copy { display: flex; flex-direction: column; min-width: 0; }
.checkout-division-product-copy strong { color: #17213a; font-size: 0.82rem; overflow-wrap: anywhere; }
.checkout-division-product-copy small { color: #7c86a0; font-size: 0.72rem; overflow-wrap: anywhere; }
.checkout-division-panel .blocked-state { margin: 14px; }
.checkout-division-empty {
  align-items: center;
  background: #f8fafc;
  border: 1px dashed #dbe3ed;
  border-radius: 8px;
  color: #7c86a0;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 7px;
  justify-content: center;
  margin: 12px;
  min-height: 175px;
  padding: 20px;
  text-align: center;
}
.checkout-division-empty strong {
  color: #17213a;
  font-weight: 700;
}

.checkout-dividir-controles {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.checkout-equal-action {
  background: #eff7ff;
  border: 1px solid #eff7ff;
  color: #1872d0;
}
.checkout-equal-action:hover:not(:disabled) {
  background: #e3f0ff;
  border-color: #cbe3ff;
  color: #1261af;
}
.checkout-clear-values {
  background: #f1f3f6;
  border: 1px solid #f1f3f6;
  color: #52617e;
  font-weight: 600;
}
.checkout-dividir-valor {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}
.checkout-financial-actions {
  flex: 0 0 auto;
  justify-content: flex-end;
  margin: 0;
}
.checkout-financial-actions .btn {
  align-items: center;
  border-radius: 6px;
  display: inline-flex;
  gap: 5px;
  justify-content: center;
  min-height: 35px;
  padding: 6px 10px;
}
.checkout-financial-actions .btn-primary:not(:disabled) {
  background: #ee7361;
  border-color: #ee7361;
}
.checkout-financial-list {
  border: 1px solid #e0e5ed;
  border-radius: 8px;
  flex: 0 1 auto;
  max-height: 320px;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  padding: 0;
  scrollbar-color: #cbd5e1 transparent;
  scrollbar-width: thin;
}
.checkout-financial-list-head,
.checkout-financial-line {
  display: grid;
  gap: 12px;
  grid-template-columns: 34px minmax(0, 1fr) minmax(130px, 190px) 38px;
  min-width: 425px;
}
.checkout-financial-list-head {
  background: #f4f6f9;
  color: #69748d;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 10px 15px;
  position: sticky;
  text-transform: uppercase;
  top: 0;
  z-index: 1;
}

.checkout-financial-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.checkout-financial-summary > div {
  align-items: center;
  background: #eff7ff;
  border: 1px solid #cee4ff;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  min-height: 92px;
  min-width: 0;
  padding: 14px 16px;
}
.checkout-financial-summary .checkout-financial-summary-icon {
  align-items: center;
  background: #dfefff;
  border-radius: 50%;
  color: #258cf4;
  display: flex;
  flex: 0 0 52px;
  height: 52px;
  justify-content: center;
}
.checkout-financial-summary-copy { min-width: 0; }
.checkout-financial-summary-copy > span {
  color: #52617e;
  font-size: 0.82rem;
  margin-bottom: 5px;
}
.checkout-financial-summary-copy > strong {
  color: #17213a;
  font-size: 1.32rem;
  font-weight: 700;
}
.checkout-financial-summary > .checkout-financial-summary__distributed {
  background: #eaf8f3;
  border-color: #ccecdf;
}
.checkout-financial-summary__distributed .checkout-financial-summary-icon {
  background: #d8f1e7;
  color: #16885f;
}
.checkout-financial-summary__distributed .checkout-financial-summary-copy > strong {
  color: #126f52;
}
.checkout-financial-summary__remaining.is-pending {
  background: #fff3f0;
  border-color: #ffd8d1;
}
.checkout-financial-summary__remaining.is-pending .checkout-financial-summary-icon {
  background: #ffe5df;
  color: #ea4335;
}
.checkout-financial-summary__remaining.is-pending .checkout-financial-summary-copy > strong {
  color: #d82d25;
}
.checkout-financial-summary__remaining.is-complete {
  background: #eaf8f3;
  border-color: #b9e7d8;
}
.checkout-financial-summary__remaining.is-complete .checkout-financial-summary-icon {
  background: #d8f1e7;
  color: #16885f;
}
.checkout-financial-summary__remaining.is-complete .checkout-financial-summary-copy > strong {
  color: #117c67;
}
.checkout-financial-summary__remaining.is-over {
  background: #fff1f0;
  border-color: #f5c2c0;
}
.checkout-financial-summary__remaining.is-over .checkout-financial-summary-copy > strong {
  color: #b42318;
}

.checkout-financial-summary-copy > span,
.checkout-financial-summary-copy > strong {
  display: block;
  overflow-wrap: anywhere;
}

.checkout-financial-section-header {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin: 18px 0 12px;
}
.checkout-financial-section-header h4 {
  color: #17213a;
  font-size: 1.08rem;
  font-weight: 700;
  margin: 0;
}
.checkout-financial-line {
  align-items: center;
  background: #fff;
  border-top: 1px solid #e9edf3;
  padding: 9px 15px;
}
.checkout-financial-line-name {
  align-items: center;
  display: flex;
  min-width: 0;
}
.checkout-financial-line-index {
  color: #52617e;
  font-size: 0.82rem;
  font-weight: 700;
}
.checkout-financial-line label {
  color: #17213a;
  font-weight: 600;
  margin: 0;
}
.checkout-financial-field {
  align-items: center;
  background: #fff;
  border: 1px solid #d9e1ec;
  border-radius: 6px;
  display: flex;
  min-width: 0;
}
.checkout-financial-field:focus-within {
  border-color: #80bdff;
  box-shadow: 0 0 0 2px rgba(54, 153, 255, 0.12);
}
.checkout-financial-field > span {
  color: #7e8299;
  font-size: 0.82rem;
  font-weight: 600;
  padding-left: 10px;
}
.checkout-financial-field .form-control {
  background: transparent;
  border: 0;
  box-shadow: none;
  min-width: 0;
  padding-left: 6px;
  text-align: right;
}
.checkout-financial-field input::-webkit-inner-spin-button,
.checkout-financial-field input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.checkout-financial-field input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
.checkout-financial-remove {
  height: 32px;
  width: 32px;
}

@media (min-width: 768px) {
  .checkout-tables-grid.checkout-division-items {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 767.98px) {
  .checkout-division-items > .checkout-section {
    min-height: 0;
  }
  .checkout-division-table-wrap { max-height: 34vh; }
  .checkout-financial-summary { grid-template-columns: minmax(0, 1fr); }
  .checkout-financial-list { flex: 0 0 auto; max-height: 35vh; }
  .checkout-financial-actions { justify-content: flex-start; }
}
@media (max-width: 575.98px) {
  .checkout-division-option { gap: 12px; min-height: 90px; padding: 14px; }
  .checkout-division-option-icon { flex-basis: 46px; height: 46px; }
  .checkout-division-panel-header { flex-wrap: wrap; }
  .checkout-financial-actions { width: 100%; }
  .checkout-financial-actions .btn { flex: 1 1 auto; }
}
.restaurante-ajuste-layout { display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); gap: 20px; }
.restaurante-ajuste-fields { min-width: 0; }
.restaurante-ajuste-product { align-items: center; background: #f4f6f9; border-radius: 8px; display: flex; gap: 16px; min-height: 120px; padding: 14px; }
.restaurante-ajuste-product img, .restaurante-ajuste-product-placeholder { border-radius: 7px; flex: 0 0 72px; height: 72px; object-fit: cover; width: 72px; }
.restaurante-ajuste-product-placeholder { align-items: center; background: #e9edf3; color: #7c86a0; display: flex; justify-content: center; }
.restaurante-ajuste-product > div { display: flex; flex-direction: column; min-width: 0; }
.restaurante-ajuste-product strong { color: #17213a; font-size: 1.05rem; overflow-wrap: anywhere; }
.restaurante-ajuste-product small { align-self: flex-start; background: #e9edf3; border-radius: 5px; color: #52617e; margin: 4px 0; padding: 2px 7px; }
.restaurante-ajuste-product span:not(.restaurante-ajuste-product-placeholder) { color: #69748d; }
.restaurante-ajuste-inputs { display: grid; gap: 14px; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-top: 22px; }
.restaurante-ajuste-inputs label { color: #17213a; font-weight: 600; margin-bottom: 7px; }
.restaurante-ajuste-inputs .checkout-adjustment-input { min-width: 0; }
.restaurante-ajuste-summary { align-self: start; border: 1px solid #ffddd8; border-radius: 8px; overflow: hidden; }
.restaurante-ajuste-summary-title { align-items: center; background: #fff2f0; color: #17213a; display: flex; gap: 10px; padding: 14px 16px; }
.restaurante-ajuste-summary-title svg { color: #ee7361; }
.restaurante-ajuste-summary-rows { padding: 10px 16px; }
.restaurante-ajuste-summary-rows > div { align-items: center; display: flex; justify-content: space-between; gap: 12px; padding: 8px 0; }
.restaurante-ajuste-summary-rows strong { color: #17213a; }
.restaurante-ajuste-summary-rows .restaurante-ajuste-positive { color: #16885f; }
.restaurante-ajuste-summary-rows .restaurante-ajuste-negative { color: #ee7361; }
.restaurante-ajuste-summary-total { align-items: center; background: #fff2f0; color: #ee7361; display: flex; justify-content: space-between; gap: 12px; margin: 0 12px 12px; padding: 12px; }
.restaurante-ajuste-summary-total strong { font-size: 1.3rem; white-space: nowrap; }
@media (max-width: 767.98px) { .restaurante-ajuste-layout { grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 479.98px) { .restaurante-ajuste-inputs { grid-template-columns: minmax(0, 1fr); } }
</style>

<style>
.restaurante-ajuste-item-modal .modal-dialog { max-width: calc(100vw - 24px); width: 920px; }
.restaurante-ajuste-item-modal .modal-content { border-radius: 9px; }
.restaurante-ajuste-item-modal .modal-header { border-bottom: 0; padding: 18px 22px 8px; }
.restaurante-ajuste-item-modal .modal-title { color: #17213a; font-size: 1.3rem; font-weight: 700; }
.restaurante-ajuste-item-modal .modal-body { padding: 14px 22px 22px; }
.restaurante-ajuste-item-modal .modal-footer { border-top: 1px solid #e9edf3; padding: 12px 22px; }
.restaurante-ajuste-item-modal .modal-footer .btn-primary { background: #ee7361; border-color: #ee7361; }
.restaurante-ajuste-item-modal .modal-footer .btn-primary:hover { background: #d85f50; border-color: #d85f50; }
.restaurante-checkout-division-modal .modal-header {
  align-items: flex-start;
  border-bottom: 0;
  padding: 20px 24px 12px;
}
.restaurante-checkout-division-modal .modal-body {
  min-height: 0;
  overflow-y: auto;
  padding: 12px 24px 22px;
}
.restaurante-checkout-division-modal .modal-footer {
  background: #fff;
  border-top: 1px solid #e9edf3;
  padding: 12px 24px 18px;
}
.restaurante-checkout-division-modal .modal-dialog {
  max-width: calc(100vw - 24px);
  width: 960px;
}
.restaurante-checkout-division-modal--choice .modal-dialog {
  width: 560px;
}
.restaurante-checkout-division-modal--value .modal-dialog {
  width: 1060px;
}
.restaurante-checkout-division-modal--value .modal-body.checkout-division-value-body {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px 20px 20px;
}
.restaurante-checkout-division-modal--value .modal-footer {
  border-top: 1px solid #e9edf3;
  box-shadow: none;
}

.restaurante-checkout-division-modal .modal-content {
  border: 1px solid #e0e5ed;
  border-radius: 10px;
  max-height: calc(100vh - 24px);
  max-height: calc(100dvh - 24px);
  box-shadow: 0 15px 38px rgba(23, 33, 58, 0.13);
}
.restaurante-checkout-division-modal .modal-title {
  color: #17213a;
  font-size: 1.4rem;
  font-weight: 700;
}
.restaurante-checkout-division-modal .checkout-division-footer-presale:not(:disabled) {
  background: #ee7361;
  border-color: #ee7361;
  color: #fff;
}
.restaurante-checkout-division-modal .checkout-division-footer-nfce:not(:disabled) {
  background: #aee4d8;
  border-color: #aee4d8;
  color: #096d61;
}

.restaurante-checkout-payment-modal .modal-dialog {
  max-width: calc(100vw - 24px);
  width: 860px;
}

.restaurante-checkout-payment-modal .modal-content {
  border-radius: 8px;
  max-height: calc(100vh - 24px);
  max-height: calc(100dvh - 24px);
}

.restaurante-checkout-payment-modal .modal-header,
.restaurante-checkout-payment-modal .modal-footer {
  border-color: #e4e6ef;
  padding: 11px 20px;
}

.restaurante-checkout-payment-modal .modal-title {
  color: #181c32;
  font-size: 1.25rem;
  font-weight: 700;
}

.restaurante-checkout-payment-modal .modal-body {
  padding: 12px 20px;
}

@media (max-width: 575.98px) {
  .restaurante-checkout-payment-modal .modal-header,
  .restaurante-checkout-payment-modal .modal-footer,
  .restaurante-checkout-payment-modal .modal-body {
    padding-left: 14px;
    padding-right: 14px;
  }
}

.restaurante-checkout-compact-modal .modal-dialog {
  max-width: calc(100vw - 24px);
}

.restaurante-checkout-avulsa-modal .modal-dialog {
  max-width: calc(100vw - 24px);
  width: 420px;
}

.restaurante-checkout-avulsa-modal .modal-content {
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(24, 28, 50, 0.08);
  max-height: calc(100dvh - 24px);
}

.restaurante-checkout-avulsa-modal .modal-header,
.restaurante-checkout-avulsa-modal .modal-body,
.restaurante-checkout-avulsa-modal .modal-footer {
  padding-left: 20px;
  padding-right: 20px;
}

.restaurante-checkout-avulsa-modal .modal-header {
  border-bottom: 1px solid #e4e6ef;
  padding-bottom: 14px;
  padding-top: 16px;
}

.restaurante-checkout-avulsa-modal .modal-title {
  color: #181c32;
  font-size: 1.05rem;
  font-weight: 700;
}

.restaurante-checkout-avulsa-modal .modal-body {
  padding-bottom: 10px;
  padding-top: 18px;
}

.restaurante-checkout-avulsa-modal .checkout-avulsa-field {
  margin-bottom: 0;
}

.restaurante-checkout-avulsa-modal .checkout-avulsa-field .form-control {
  border-color: #d9dce5;
  border-radius: 6px;
  min-height: 40px;
}

.restaurante-checkout-avulsa-modal .checkout-avulsa-field .form-control.is-invalid {
  border-color: #f64e60;
}
.restaurante-checkout-avulsa-modal .checkout-avulsa-field .form-control:focus {
  border-color: #3699ff;
  box-shadow: 0 0 0 2px rgba(54, 153, 255, 0.12);
}

.restaurante-checkout-avulsa-modal .modal-footer {
  background: #fafbfd;
  border-top: 1px solid #e4e6ef;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: space-between;
  padding-bottom: 12px;
  padding-top: 12px;
}

.restaurante-checkout-avulsa-modal .modal-footer .btn {
  min-height: 36px;
  padding: 7px 14px;
}

.restaurante-checkout-avulsa-modal .checkout-avulsa-submit {
  background: #f46f60;
  border-color: #f46f60;
  margin-left: auto;
}

.restaurante-checkout-avulsa-modal .checkout-avulsa-submit:not(:disabled):hover {
  background: #e95e50;
  border-color: #e95e50;
}
.restaurante-checkout-payment-modal .restaurante-payment-modal-body {
  min-height: 0;
}

.restaurante-checkout-payment-modal .restaurante-payment-form-row .form-control,
.restaurante-checkout-payment-modal .restaurante-payment-form-row .custom-select {
  min-height: 40px;
}

.restaurante-checkout-payment-modal .modal-footer .btn-primary,
.restaurante-checkout-payment-modal .restaurante-payment-add-button {
  background: #ee7361;
  border-color: #ee7361;
  font-weight: 700;
}

.restaurante-checkout-payment-modal .modal-footer .btn-primary {
  min-height: 40px;
  padding: 8px 18px;
}

.restaurante-checkout-payment-modal .modal-footer .btn-link {
  color: #5e6278;
  font-weight: 600;
}

.restaurante-checkout-payment-modal .modal-header,
.restaurante-parcelamento-modal .modal-header,
.restaurante-checkout-division-modal .modal-header {
  border-bottom: 0;
  padding-bottom: 10px;
}

.restaurante-checkout-payment-modal .modal-footer,
.restaurante-parcelamento-modal .modal-footer,
.restaurante-checkout-division-modal .modal-footer,
.restaurante-checkout-division-modal--value .modal-footer {
  background: #fff;
  border-top: 0;
  box-shadow: none;
  gap: 8px;
  padding-top: 10px;
}

.restaurante-parcelamento-modal .modal-header,
.restaurante-parcelamento-modal .modal-footer {
  padding-left: 20px;
  padding-right: 20px;
}

.restaurante-parcelamento-modal .modal-footer .btn {
  border-radius: 6px;
  font-size: 0.9rem;
  min-height: 36px;
  padding: 7px 14px;
}

.restaurante-parcelamento-modal .modal-footer .btn-light-primary {
  background: #fff1ee;
  border-color: #fff1ee;
  color: #b94e40;
}

.restaurante-parcelamento-modal .modal-footer .btn-primary {
  background: #ee7361;
  border-color: #ee7361;
}

@media (max-width: 575.98px) {
  .restaurante-checkout-division-modal .modal-header,
  .restaurante-checkout-division-modal .modal-body,
  .restaurante-checkout-division-modal .modal-footer {
    padding-left: 14px;
    padding-right: 14px;
  }
  .restaurante-checkout-division-modal .modal-dialog,
  .restaurante-checkout-division-modal--choice .modal-dialog,
  .restaurante-checkout-division-modal--value .modal-dialog,
  .restaurante-checkout-payment-modal .modal-dialog,
  .restaurante-checkout-compact-modal .modal-dialog {
    max-width: calc(100vw - 8px);
    width: calc(100vw - 8px);
  }
  .restaurante-checkout-avulsa-modal .modal-dialog {
    max-width: calc(100vw - 24px);
    width: calc(100vw - 24px);
  }
}
@media (max-width: 767.98px) {
  .restaurante-checkout-division-modal--value .modal-body.checkout-division-value-body {
    overflow-y: auto;
  }
}
@media (min-width: 992px) {
  .checkout-layout .checkout-right-column > .checkout-summary-panel {
    margin-top: 1.25rem !important;
  }
}
@media (max-width: 430px) {
  .checkout-adjustments,
  .checkout-action-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .total-main strong {
    font-size: 1.3rem;
  }
}
</style>
